import React, { useState, useEffect, useRef, useCallback, useMemo, ReactElement } from "react";
import { processHtmlEmail } from "../../helpers/customLinkify";
import {
  shouldShowReplyAll,
  extractEmailFromRecipient,
  getCurrentInboxEmailFromChannelName,
} from "../../helpers/emailUtils";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Paper,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme as useMuiTheme,
  Badge,
  Snackbar,
  Drawer,
  Toolbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  Analytics as AnalyticsIcon,
  Source as SourceIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Inbox as InboxIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  ReplyAll as ReplyAllIcon,
  Edit as ComposeIcon,
  Chat as ChatIcon,
  FiberManualRecord as FiberManualRecordIcon,
  DriveFileMove as MoveThreadIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  API_CONFIG,
  API_ENDPOINTS,
  EMAIL_API_ENDPOINTS,
  EMAIL_LABEL_CHIP_BG,
  EMAIL_LABEL_CHIP_TEXT,
  EMAIL_LABEL_DISPLAY_TO_KEY,
  ML_API_ENDPOINTS,
  MESSAGING_API_ENDPOINTS,
  STANDARD_CATEGORY_LABELS,
  SPAM_FOLDER_NOTICE,
  SPAM_FOLDER_NOTICE_COUNTDOWN_LABEL,
  SPAM_FOLDER_NOTICE_COUNTDOWN_SECONDS,
  STORAGE_KEYS,
  SOCKET_KEYS,
  DISABLE_SUBSCRIPTION,
  SUBSCRIPTION_LIMIT_MESSAGES,
} from "../../helpers/constants";
import { getCompanyId, getLoggedInUserData } from "../../helpers/storageUtils";
import { isAdminUIDisabled } from "../../config/runtimeConfig";
import { resolveWebmailProvider, WEBMAIL_PROVIDER_CONFIG, resolveMessagingProvider, MESSAGING_PROVIDER_CONFIG } from "../../helpers/emailProviderUtils";
import {
  isChannelIconLandingOpen,
  openDashboardFlags,
  closeDashboardFlags,
  openSourcePanelFlags,
  closeSourcePanelFlags,
} from "../../helpers/channelLandingOverlay";
import {
  moveToInbox,
  moveToClassification,
  hideDraft,
  MoveToInboxRequest,
  MoveToInboxResponse,
  MoveToClassificationRequest,
  EmailClassification,
  EmailMetric,
} from "../../services/emailService";
import { sendMessage, SendMessageRequest } from "../../services/messagingService";
import { uploadMessageAttachments } from "../../services/messageAttachmentService";
import { LanguageConstants } from "../../helpers/LanguageConstants";
import ChannelSettings from "../../components/ChannelSettings";
import {
  parseChannelSettingsTabParam,
  type ChannelSettingsTabId,
} from "../../components/ChannelSettings/channelSettingsTabs";
import AddThreadModal from "../../components/AddThreadModal";
import TaskPanel from "../../components/TaskPanel";
import ApprovalPanelButton from "../../components/ApprovalPanelButton";
import AskPrism7Button from "../../components/AskPrism7Button";
import AskPrism7Panel from "../../components/AskPrism7Panel";
import ThreadList, {
  ApiThread,
  messageThreadMatchesClientSearch,
} from "../../components/ThreadList";
import AccessDenied from "../../components/AccessDenied";
import EmailFilterModal, { EmailFilters } from "../../components/EmailFilterModal";
import EmailClassificationPanel from "../../components/EmailClassificationPanel";
import DataSourcesPanel, { EmbeddingStatusResponse } from "../../components/DataSourcesPanel";
import EmailSyncStatusIndicator from "../../components/EmailSyncStatusIndicator";
import ChannelTypeChip from "../../components/ChannelTypeChip";
import ComposeBox from "../../components/ComposeBox";
import ChannelDashboard from "../../components/ChannelDashboard";
import EmailAttachments from "../../components/EmailAttachments";
import CitationsDisplay, { Citation } from "../../components/CitationsDisplay";
import DraftUsageMetrics, { DraftUsageMetricsData } from "../../components/DraftUsageMetrics";
import MessageActionsMenu from "../../components/MessageActionsMenu";
import MessageAttachmentInput, { SelectedFile } from "../../components/MessageAttachmentInput";
import ThreadMessageList from "../../components/ThreadMessageList";
import ChannelRestrictionModal from "../../components/ChannelRestrictionModal";
import MoveThreadModal from "../../components/MoveThreadModal";
import { useTheme } from "../../contexts/ThemeContext";
import { useSubscriptionValidation } from "../../hooks/useSubscriptionValidation";
import { useChannelAdmin } from "../../hooks/useChannelAdmin";
import {
  capitalizeFirstLetter,
  capitalizeChannelName,
  getFirstAlphabeticChar,
  getAvatarColor,
} from "../../helpers/stringUtils";
import {
  formatDetailedTooltip,
  formatRelativeTime,
  formatDateSeparator,
  isSameDay,
} from "../../helpers/dateUtils";
import socketService from "../../services/socketService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { convertMarkdownToHtmlIfNeeded, markdownToHtml, hasMarkdown, flattenParagraphsToBr } from "../../helpers/markdownUtils";
import { CHANNEL_LANDING_VIEW_Z_INDEX } from "../../helpers/zIndexLayers";
import {
  RichTextEditor,
  MenuControlsContainer,
  MenuDivider,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
} from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import {
  mentionSuggestionOptions,
  initializeChannelMembers,
  setChannelMembersForMentions,
} from "../../helpers/mentionSuggestionOptions";
import LinkifyText from "../../helpers/customLinkify";
import MentionText from "../../components/MentionText";
import {
  mapMembersToMentionUsers,
  PRISM7_MENTION_USER,
  type MentionUser,
} from "../../helpers/parseMentionText";

/** True when any email thread-list API filters are active (search box, modal filters, etc.) */
function hasActiveEmailListFilters(f: EmailFilters): boolean {
  return (
    f.from_email.length > 0 ||
    f.to_email.length > 0 ||
    f.subject.trim() !== "" ||
    f.body.trim() !== "" ||
    f.has_attachment !== null ||
    f.date_from !== "" ||
    f.date_to !== "" ||
    f.is_archived !== null
  );
}

/** Default thread-list filters (modal + list API) — used when clearing state on full reload */
const DEFAULT_EMAIL_FILTERS: EmailFilters = {
  from_email: [],
  to_email: [],
  subject: "",
  body: "",
  has_attachment: null,
  date_from: "",
  date_to: "",
  is_archived: null,
};

/** True when the user landed via full page reload (F5 / refresh), not client-side navigation */
function isNavigationReload(): boolean {
  if (typeof performance === "undefined") return false;
  const entry = performance.getEntriesByType?.("navigation")?.[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (entry?.type === "reload") return true;
  const legacy = (performance as unknown as { navigation?: { type: number } }).navigation;
  return legacy?.type === 1;
}

/**
 * Dedupes reload handling when React Strict Mode runs the effect twice on the same full reload (module reload clears the set).
 */
const threadReloadStripDoneChannelIds = new Set<string>();

/** Query key for email folder (matches API classification_type); survives refresh alongside threadId in the path */
const THREAD_CLASSIFICATION_QUERY_KEY = "classification";

/**
 * Builds a react-router location for /threads routes; merges classification into the query without dropping openSettings, openTask, etc.
 */
function buildThreadsNavigate(
  channelId: string,
  threadId: string | undefined,
  baseSearch: URLSearchParams,
  classification: string,
  isEmailChannel: boolean
): { pathname: string; search: string } {
  const next = new URLSearchParams(baseSearch); // clone so the router’s search object is not mutated in place
  if (isEmailChannel && classification) {
    next.set(THREAD_CLASSIFICATION_QUERY_KEY, classification);
  } else {
    next.delete(THREAD_CLASSIFICATION_QUERY_KEY);
  }
  // Deep-link URL race fix — these one-shot params are only ever meant to be read once, on the
  // very first render, by the four deep-link effects below (which already ran and captured their
  // intent into React state by the time any navigate() fires). Previously each effect cleaned up
  // its own param(s) independently via a 200ms setTimeout, racing against fetchThreads()'s
  // auto-select-first-thread navigate() — on a real network, that navigate() reliably arrived
  // after the 200ms timer had already stripped the params, so this function's own
  // preserve-existing-params behavior had nothing left to preserve, silently discarding the
  // original deep-link intent from the URL. Deleting them here, in the one place every
  // navigate() in this component already funnels through, removes the race entirely.
  next.delete('openSettings');
  next.delete('tab');
  next.delete('openTask');
  next.delete('openApproval');
  next.delete('viewDashboard');
  next.delete('viewDS');
  next.delete('viewSkills');
  next.delete('openAddDataSource');
  next.delete('addDataSource');
  next.delete('openAddSkill');
  next.delete('addSkill');
  const qs = next.toString();
  const pathname =
    threadId && threadId.length > 0
      ? `/threads/${channelId}/${threadId}`
      : `/threads/${channelId}`;
  return { pathname, search: qs ? `?${qs}` : "" };
}

// Common collapsible field component with character-based truncation
const CollapsibleField: React.FC<{
  label: string;
  value: string;
  maxChars?: number;
}> = ({ label, value, maxChars = 60 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate = value.length > maxChars;
  const displayText = isExpanded ? value : value.substring(0, maxChars);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
    >
      <Typography variant="caption" color="text.secondary" component="span">
        {label}: {displayText}
        {shouldTruncate && !isExpanded && "..."}
      </Typography>
      {shouldTruncate && (
        <Typography
          variant="caption"
          color="primary"
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            fontSize: "0.7rem",
            whiteSpace: "nowrap",
          }}
          onClick={toggleExpanded}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Typography>
      )}
    </Box>
  );
};

// Collapsible quoted email component for email content view
const CollapsibleQuotedEmail: React.FC<{
  content: string;
  isCollapsed?: boolean;
}> = ({ content, isCollapsed: initialCollapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const muiTheme = useMuiTheme();

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (isCollapsed) {
    return (
      <Box
        onClick={toggleCollapse}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: '2px 0',
          margin: '8px 0',
          width: '24px',
          height: '12px',
          backgroundColor: '#f0f0f0',
          border: '1px solid #d0d0d0',
          fontSize: '14px',
          fontFamily: 'arial, sans-serif',
          fontWeight: 'bold',
          color: '#333',
          userSelect: 'none',
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: '#e8e8e8',
            borderColor: '#c0c0c0',
          },
        }}
      >
        ⋯
      </Box>
    );
  }

  return (
    <Box
      sx={{
        margin: '0',
        position: 'relative',
      }}
    >
      {/* Gmail-style quoted content */}
      <Box
        sx={{
          borderLeft: '3px solid #cccccc',
          paddingLeft: '10px',
          margin: '0',
          color: '#777777',
          fontSize: '13px',
          fontFamily: 'arial, sans-serif',
          lineHeight: '1.4',
          '& strong': {
            fontWeight: 'bold',
            color: '#333333',
          },
          '& div': {
            margin: '0 0 2px 0',
            lineHeight: '1.4',
            fontSize: '13px',
          },
          '& p': {
            margin: '0 0 2px 0',
            lineHeight: '1.4',
            fontSize: '13px',
            '&:last-child': {
              marginBottom: 0,
            },
          },
        }}
      >
        {/* Click-to-collapse dots - positioned at the top right */}
        <Box
          onClick={toggleCollapse}
          sx={{
            position: 'absolute',
            right: '0px',
            top: '0px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '10px',
            backgroundColor: '#f0f0f0',
            border: '1px solid #d0d0d0',
            fontSize: '12px',
            fontFamily: 'arial, sans-serif',
            fontWeight: 'bold',
            color: '#333',
            zIndex: 1,
            borderRadius: '5px',
            '&:hover': {
              backgroundColor: '#e8e8e8',
              borderColor: '#c0c0c0',
            },
          }}
        >
          ⋯
        </Box>
        
        <Box
          dangerouslySetInnerHTML={{ __html: content }}
          className="email-content-scope"
          onCopy={(e) => {
            // Paste as plain text so HTML tags are not copied
            e.preventDefault();
            const plainText = window.getSelection()?.toString() ?? "";
            e.clipboardData.setData("text/plain", plainText);
          }}
        />
      </Box>
    </Box>
  );
};


// API Response types for thread list

interface ThreadsResponse {
  success: boolean;
  data: {
    threads: ApiThread[];
    pagination: {
      limit: number;
      offset: number;
      total: number;
    };
  };
}

// API Response types for messages
interface UserBean {
  id: string;
  email: string;
  /** Often login handle from API; prefer full_name for display when present */
  name: string;
  role: string;
  /** Display name when API separates it from username / login name */
  full_name?: string;
  username?: string;
}

/** Prefer full_name so the thread shows a person’s name, not the login username stored in `name`. */
function displayNameFromUserBean(ub: UserBean | undefined | null): string {
  const raw = ub?.full_name?.trim() || ub?.name?.trim() || "";
  return capitalizeFirstLetter(raw) || "";
}


interface ApiMessage {
  id: string;
  content: string;
  message_type: "user" | "system" | "email";
  thread_id: string;
  channel_id: string;
  user_id: string;
  query_id: string | null;
  userBean: UserBean;
  is_ai_processed: boolean;
  ai_provider: string | null;
  ai_model: string | null;
  ai_processing_time: number | null;
  is_visible: boolean;
  is_pinned: boolean;
  sequence_number: number;
  attachments: Array<{
    attachment_id: string;
    filename: string;
    file_type: string;
    file_size: number;
    storage_path: string;
    is_inline: boolean;
    content_id: string | null;
  }> | null;
  citations?: Citation[];
  usage_metrics?: DraftUsageMetricsData;
  created_at: string;
  updated_at: string;
  /** Top-level from API when emailResponse is absent (e.g. user/system messages); prefer emailResponse.externalAppId first */
  externalAppId?: string;
  emailResponse?: any;
}

/** Strip label_/sublabel_ prefixes so API keys match STANDARD_CATEGORY_LABELS (inbox, spam, …). */
function getBaseClassificationKeyForFolder(classificationType: string): string {
  if (!classificationType) return classificationType;
  if (classificationType.startsWith("label_")) return classificationType.slice(6);
  if (classificationType.startsWith("sublabel_")) return classificationType.slice(9);
  return classificationType;
}

/**
 * After move-to-inbox / move-to-classification succeeds, sync loaded messages’ emailFolder so chips
 * and “Move to Inbox” match the new category (messages are not refetched until selectedThread.id changes).
 */
function applyNewClassificationToMessages(
  prev: ApiMessage[],
  newClassification: string | undefined
): ApiMessage[] {
  if (!newClassification) return prev;
  const baseKey = getBaseClassificationKeyForFolder(newClassification);
  const folderDisplay = STANDARD_CATEGORY_LABELS[baseKey];
  if (!folderDisplay) return prev;
  return prev.map((m) =>
    m.emailResponse
      ? {
          ...m,
          emailResponse: { ...m.emailResponse, emailFolder: folderDisplay },
        }
      : m
  );
}

/** Id of the thread's latest message (highest sequence_number) — sent as message_id on main_chat calls. */
function getLatestMessageId(messages: ApiMessage[]): string | undefined {
  if (messages.length === 0) return undefined;
  return messages.reduce((latest, current) =>
    (current.sequence_number || 0) > (latest.sequence_number || 0) ? current : latest
  ).id;
}

interface ThreadData {
  thread_id: string;
  title: string;
  description?: string;
  message_count: number;
  last_message_at: string;
  email_thread_id?: string | null;
  original_message_id?: string | null;
  email_type?: string | null;
  from_email_id?: string;
}

interface MessagesResponse {
  success: boolean;
  data: {
    thread: ThreadData;
    messages: ApiMessage[];
  };
}

/** GET /messages/threads/:id — authoritative message_count for paging offset (body may be wrapped in { data }) */
interface MessagesApiThreadDetails {
  thread_id: string;
  title: string;
  message_count: number;
  last_message_at: string;
  ai_summary?: string;
  created_at: string;
}

/** Merge thread-details response into list row / selected thread; keep email-only fields from existing */
function mergeMessagesApiDetailsIntoThread(
  existing: ApiThread | undefined,
  d: MessagesApiThreadDetails
): ApiThread {
  return {
    id: d.thread_id,
    title: d.title ?? existing?.title ?? "",
    description: existing?.description ?? "",
    message_count: d.message_count,
    last_message_at: d.last_message_at ?? existing?.last_message_at ?? null,
    email_thread_id: existing?.email_thread_id ?? null,
    original_message_id: existing?.original_message_id ?? null,
    email_type: existing?.email_type ?? null,
    from_email_id: existing?.from_email_id ?? "",
    created_at: d.created_at || existing?.created_at || "",
    updated_at: existing?.updated_at || d.created_at || "",
    ai_summary: d.ai_summary ?? existing?.ai_summary ?? null,
    channel_id: existing?.channel_id,
    is_read: existing?.is_read,
    is_archived: existing?.is_archived,
    external_app_id: existing?.external_app_id ?? null,
  };
}

/** Main API thread details; returns null if unavailable (e.g. email-only thread id on this route) */
async function fetchMessagesThreadDetailsFromApi(
  threadId: string
): Promise<MessagesApiThreadDetails | null> {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
    const response = await fetch(
      `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.GET_THREAD_DETAILS(threadId)}`,
      {
        method: "GET",
        headers: {
          accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!response.ok) return null;
    const json: unknown = await response.json();
    const raw: unknown =
      json !== null &&
      typeof json === "object" &&
      "data" in (json as object) &&
      (json as { data: unknown }).data !== null &&
      typeof (json as { data: unknown }).data === "object"
        ? (json as { data: MessagesApiThreadDetails }).data
        : json;
    if (raw === null || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    if (typeof r.thread_id !== "string" || typeof r.message_count !== "number") return null;
    return raw as MessagesApiThreadDetails;
  } catch {
    return null;
  }
}

/** Normalize attachment from API (message channels may return id/file_name/original_filename instead of attachment_id/filename). */
function normalizeAttachments(attachments: any[] | null | undefined): ApiMessage["attachments"] {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  const normalized = attachments.map((a: any) => ({
    attachment_id: a.attachment_id ?? a.id ?? String(a),
    filename: a.filename ?? a.file_name ?? a.original_filename ?? a.metadata?.original_filename ?? "",
    file_type: a.file_type ?? a.content_type ?? a.mime_type ?? "",
    file_size: a.file_size ?? a.size_bytes ?? 0,
    is_inline: a.is_inline ?? a.inline ?? false,
    storage_path: a.storage_path ?? "",
    content_id: a.content_id ?? null,
  }));
  return normalized as ApiMessage["attachments"];
}

/** Normalize user_bean / userBean so thread UI can show full_name instead of username-only `name`. */
function coalesceUserBean(raw: unknown, fallback: UserBean | undefined): UserBean {
  if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    return {
      id: String(r.id ?? r.user_id ?? fallback?.id ?? ""),
      email: String(r.email ?? r.email_id ?? fallback?.email ?? ""),
      name: String(r.name ?? fallback?.name ?? ""),
      role: String(r.role ?? fallback?.role ?? ""),
      full_name:
        (typeof r.full_name === "string" ? r.full_name : undefined) ??
        (typeof r.fullName === "string" ? r.fullName : undefined) ??
        fallback?.full_name,
      username: typeof r.username === "string" ? r.username : fallback?.username,
    };
  }
  return fallback ?? { id: "", email: "", name: "", role: "" };
}

/** Normalize a message from the messages API so citations and usage_metrics are always available for UI. */
function normalizeMessageFromApi(msg: any): ApiMessage {
  const rawCits = msg.citations ?? msg.citation;
  const citations: Citation[] = Array.isArray(rawCits)
    ? rawCits.map((c: any) =>
        c && typeof c === "object" && "name" in c
          ? { name: String(c.name), type: c.type != null ? String(c.type) : "file" }
          : { name: String(c), type: "file" }
      )
    : [];
  const usageMetrics = msg.usage_metrics;
  const attachments = normalizeAttachments(msg.attachments);
  const userBean = coalesceUserBean(msg.userBean ?? msg.user_bean, msg.userBean);
  return {
    ...msg,
    userBean,
    citations: citations.length > 0 ? citations : msg.citations ?? [],
    ...(usageMetrics != null ? { usage_metrics: usageMetrics } : {}),
    ...(attachments != null ? { attachments } : {}),
  };
}

/**
 * When a user prompt is deleted, the paired Prism AI row is a separate `system` message.
 * Returns ids to drop from local state (matches ThreadMessageList sort for “next” AI bubble).
 */
function getMessageIdsToRemoveForDeletedPrompt(
  messages: ApiMessage[],
  deletedMessageId: string
): Set<string> {
  const ids = new Set<string>([deletedMessageId]); // always remove the deleted row
  const deleted = messages.find((m) => m.id === deletedMessageId);
  if (!deleted || deleted.message_type !== "user") {
    return ids; // non-user deletes: single id only
  }
  // Prefer API link: same query_id on user + system rows
  if (deleted.query_id) {
    let linkedByQuery = false;
    for (const m of messages) {
      if (m.message_type === "system" && m.query_id === deleted.query_id) {
        ids.add(m.id); // paired AI / system reply for this prompt
        linkedByQuery = true;
      }
    }
    if (linkedByQuery) return ids;
  }
  // Tie-break order must match ThreadMessageList (user before system when same timestamp)
  const typeOrder = (m: ApiMessage) =>
    m.message_type === "user" ? 0 : m.message_type === "system" ? 1 : 2;
  const sorted = [...messages].sort((a, b) => {
    const t1 = new Date(a.created_at).getTime();
    const t2 = new Date(b.created_at).getTime();
    if (t1 !== t2) return t1 - t2;
    return typeOrder(a) - typeOrder(b);
  });
  const idx = sorted.findIndex((m) => m.id === deletedMessageId);
  const next = idx >= 0 ? sorted[idx + 1] : undefined;
  if (next?.message_type === "system") {
    ids.add(next.id); // immediate next bubble is the AI response
  }
  return ids;
}

// Original mock data types for right panel
interface Thread {
  id: number;
  title: string;
  type: "chat" | "pdf" | "log";
  timestamp: string;
  content: string;
  sources: string[];
  comments: Comment[];
}

interface Comment {
  id: number;
  userId: string;
  username: string;
  avatarInitials: string;
  timestamp: string;
  text: string;
  tags: string[];
}

interface User {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
}

// Channel API response interface
interface ChannelResponse {
  id: string;
  name: string;
  description: string;
  workspace_id: string;
  workspace_name: string;
  workspace_tag?: string;
  company_id: string;
  is_active: boolean;
  is_public: boolean;
  is_archived: boolean;
  ai_enabled: boolean;
  channel_type?: 'email' | 'message' | 'voice';
  messaging_provider?: 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams';
  app_account_id?: string;
  app_accounts?: Array<{
    id: string;
    app_id?: string;
    provider?: string;
    messaging_provider?: string;
    [key: string]: any;
  }>;
  connections?: Array<{
    id: string;
    app_account_id?: string;
    provider?: string;
    messaging_provider?: string;
    [key: string]: any;
  }>;
  /** API may return provider at top level and/or under channel_settings */
  channel_settings?: {
    notifications?: boolean;
    provider?: string;
  };
  /** Top-level provider from API (e.g. "regular" for message channels) */
  provider?: string;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  /** List of category/tags for the channel (from channel API) */
  channel_tags?: string[];
  /** List of sub-category/subtags for the channel (from channel API) */
  channel_sub_tags?: string[];
  /** Plain-English automation rules for the channel */
  channel_business_rules?: string;
  /** User-entered channel description from the connect/create payload */
  channel_description?: string;
  [key: string]: any; // Allow additional fields from API
}

// Function to check if Ask Prism7 should be enabled based on embedding status
const hasCompletedEmbeddings = (embeddingStatus: any | null): boolean => {
  return embeddingStatus !== null && embeddingStatus.completed > 0;
};

// Default content for Ask Prism7 right panel: pre-filled @Prism7 mention (TipTap mention HTML so it renders styled)
const ASK_PRISM7_DEFAULT_CONTENT =
  '<p><span class="mention" data-id="prism7" data-label="Prism7">@Prism7</span> </p>';

/** Minimum thread list column width (px) */
const THREAD_LIST_PANEL_MIN_PX = 240;
/** Maximum thread list column width (px) when container is wide enough */
const THREAD_LIST_PANEL_MAX_PX = 560;
/** Default width (px) — matches previous fixed layout */
const THREAD_LIST_PANEL_DEFAULT_PX = 300;
/** Reserve at least this many pixels for the message/detail pane when clamping max list width */
const THREAD_LIST_DETAIL_MIN_PX = 200;

const ThreadDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { channelId, threadId } = useParams<{ channelId: string; threadId?: string }>();
  const { isChannelAdmin } = useChannelAdmin(channelId);
  // Refs for socket handler to avoid stale closure and TDZ (handler runs async, must read current values)
  const channelIdRef = useRef<string | undefined>(channelId);
  channelIdRef.current = channelId;
  const fetchThreadByIdRef = useRef<((id: string) => Promise<ApiThread | null | undefined>) | null>(null);
  const fetchMessagesRef = useRef<((threadId: string, wasUnread?: boolean, messageCountOverride?: number, silent?: boolean) => Promise<void>) | null>(null);
  // When set, the selectedThread useEffect skips fetchMessages (silent refresh after edit & send)
  const silentRefreshThreadIdRef = useRef<string | null>(null);
  // When set, socket THREAD_UPDATED skips fetchMessages for this thread (we already refetched in handleComposeSend)
  const weJustSentForThreadIdRef = useRef<string | null>(null);
  // URL race fix — setSearchParams is no longer used directly: all one-shot deep-link params
  // are now cleaned up centrally by
  // buildThreadsNavigate() the next time this component navigates, instead of each deep-link
  // effect racing its own 200ms setTimeout against fetchThreads()'s auto-select-first-thread
  // navigate(). Re-add the setter here if a future feature needs to write to the URL directly.
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const { user, markSessionExpired } = useAuth();
  const { showSuccess, showError } = useToast();
  
  // Subscription validation hook for message posting restrictions
  const { validate: validateSubscription } = useSubscriptionValidation();
  
  // State for message restriction modal
  const [openMessageRestrictionModal, setOpenMessageRestrictionModal] = useState<boolean>(false);
  
  // State for attachment restriction modal
  const [openAttachmentRestrictionModal, setOpenAttachmentRestrictionModal] = useState<boolean>(false);

  // State for API data (thread list)
  const [apiThreads, setApiThreads] = useState<ApiThread[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [threadsLoading, setThreadsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // True when user gets 403 (not a member of this channel)
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  // True when channel returns 404 (channel does not exist)
  const [channelNotFound, setChannelNotFound] = useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  // State for selected thread (using API thread ID)
  const [selectedThread, setSelectedThread] = useState<ApiThread | null>(null);
  const [newComment, setNewComment] = useState("");
  const [editorKey, setEditorKey] = useState<number>(0); // Key to force RichTextEditor re-render
  const [isEditorFocused, setIsEditorFocused] = useState<boolean>(false);

  // State for messages
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [isAiThinking, setIsAiThinking] = useState<boolean>(false);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState<boolean>(false);
  const [isGeneratingDraftCompleted, setIsGeneratingDraftCompleted] =
    useState<boolean>(false);
  const [postingMethod, setPostingMethod] = useState<
    "ask-shay" | "internal-reply" | "channel-reply"
  >("ask-shay");

  // State for edit internal message
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [messageToEdit, setMessageToEdit] = useState<ApiMessage | null>(null);
  const [editedMessageContent, setEditedMessageContent] = useState<string>("");
  const [editedAttachments, setEditedAttachments] = useState<any[]>([]);

  // Mention display: channel members + Prism7 for exact @mention boundaries (no extra word)
  const [mentionUserObjects, setMentionUserObjects] = useState<MentionUser[]>([]);

  // State for delete internal message
  const [isDeleteMessageConfirmOpen, setIsDeleteMessageConfirmOpen] = useState<boolean>(false);
  const [messageToDelete, setMessageToDelete] = useState<ApiMessage | null>(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState<boolean>(false);

  // Messages pagination state
  const [messagesCurrentPage, setMessagesCurrentPage] = useState<number>(1);
  const [messagesHasMore, setMessagesHasMore] = useState<boolean>(true);
  const [messagesLoadingMore, setMessagesLoadingMore] =
    useState<boolean>(false);
  const [messagesCurrentOffset, setMessagesCurrentOffset] = useState<number>(0);
  /** Mirrors messagesCurrentOffset so debounced scroll callbacks never use a stale offset (avoids offset=0 after first page loaded at 23) */
  const messagesCurrentOffsetRef = useRef<number>(0);
  /** Mirrors messagesLoading for loadMoreMessages when invoked from stale setTimeout closures */
  const messagesLoadingRef = useRef<boolean>(false);

  // Refs for messages scroll detection
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const renderOneMessageRef = useRef<((message: ApiMessage) => React.ReactNode) | null>(null);
  const messagesIsLoadingRef = useRef<boolean>(false);
  const messagesScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const justLoadedMoreRef = useRef<boolean>(false);
  /** Store scroll position before prepending so we can restore it after DOM update (point 2 fix) */
  const scrollPreserveForLoadMoreRef = useRef<{ previousScrollHeight: number; previousScrollTop: number } | null>(null);
  /** Timeout id for clearing justLoadedMoreRef after scroll restore so cleanup can clear it */
  const loadMoreScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const hasScrolledToBottomRef = useRef<boolean>(false);
  /** When true, user clicked back to leave channel; skip any async completion that would navigate back into the channel */
  const userNavigatedBackRef = useRef<boolean>(false);

  /** Main row under header: used to clamp thread list max width so the detail pane keeps minimal space */
  const mainContentAreaRef = useRef<HTMLDivElement>(null);
  /** Resets when channelId changes (see effect below); user can resize within the channel */
  const [threadListPanelWidth, setThreadListPanelWidth] = useState(
    THREAD_LIST_PANEL_DEFAULT_PX
  );

  const clampThreadListWidth = useCallback((w: number) => {
    const containerW = mainContentAreaRef.current?.getBoundingClientRect().width;
    let maxPx = THREAD_LIST_PANEL_MAX_PX;
    if (containerW != null && containerW > 0) {
      maxPx = Math.min(
        THREAD_LIST_PANEL_MAX_PX,
        Math.max(
          THREAD_LIST_PANEL_MIN_PX,
          Math.floor(containerW - THREAD_LIST_DETAIL_MIN_PX)
        )
      );
    }
    return Math.max(THREAD_LIST_PANEL_MIN_PX, Math.min(maxPx, Math.round(w)));
  }, []);

  const handleThreadListResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = threadListPanelWidth;
      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        setThreadListPanelWidth(clampThreadListWidth(startW + delta));
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [threadListPanelWidth, clampThreadListWidth]
  );

  useEffect(() => {
    const onResize = () => {
      setThreadListPanelWidth((w) => clampThreadListWidth(w));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampThreadListWidth]);

  // Each channel visit starts at default thread list width; user may resize for that session only
  useEffect(() => {
    if (!channelId) return;
    setThreadListPanelWidth(clampThreadListWidth(THREAD_LIST_PANEL_DEFAULT_PX));
  }, [channelId, clampThreadListWidth]);

  // State for temporary messages during API progress
  const [temporaryUserMessage, setTemporaryUserMessage] = useState<
    string | null
  >(null);

  // State for move to inbox operation
  const [movingToInbox, setMovingToInbox] = useState<Record<string, boolean>>({});
  // State for move-to-classification (inbox/spam/no_reply) – moves whole thread
  const [movingToClassification, setMovingToClassification] = useState<string | null>(null);
  const [deletingDraftById, setDeletingDraftById] = useState<Record<string, boolean>>({});

  // Resolve settings deep-link intent before the first render. The drawer otherwise mounts on
  // its default Config tab and only switches to Skills after the URL effect runs, which causes
  // the add-skill redirect to visibly flash the wrong tab.
  const openSettingsFromUrl = searchParams.get('openSettings') === 'true';
  const requestedSettingsTab = parseChannelSettingsTabParam(searchParams.get('tab'));
  const addSkillFromUrl =
    searchParams.get('viewSkills') === 'true' ||
    searchParams.get('openAddSkill') === 'true' ||
    searchParams.get('addSkill') === 'true' ||
    (openSettingsFromUrl && requestedSettingsTab === 'skills');
  const initialSettingsTabFromUrl = addSkillFromUrl
    ? 'skills'
    : openSettingsFromUrl
      ? requestedSettingsTab ?? 'config'
      : undefined;

  // State for settings drawer
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(
    () => Boolean(channelId && (openSettingsFromUrl || addSkillFromUrl))
  );
  // State for initial tab from URL (stored before URL is cleaned)
  const [initialSettingsTabId, setInitialSettingsTabId] = useState<ChannelSettingsTabId | undefined>(
    initialSettingsTabFromUrl
  );
  const [initialOpenAddSkill, setInitialOpenAddSkill] = useState(addSkillFromUrl);
  const [isSourcePanelOpen, setIsSourcePanelOpen] = useState(false);
  const [initialOpenAddDataSource, setInitialOpenAddDataSource] = useState(false);
  const handleInitialAddSkillOpened = useCallback(() => {
    setInitialOpenAddSkill(false);
  }, []);
  const handleInitialAddDataSourceOpened = useCallback(() => {
    setInitialOpenAddDataSource(false);
  }, []);
  // Landing state for a channel is the icon-only view (see channelIconLandingOpen below) —
  // inbox/compose live in Gmail/Outlook/Google Chat now, so nothing opens until an icon is clicked.
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  // PR #665 review (M2) — set right before opening the Dashboard from the Business Rules Workflow
  // canvas's "Runs: N" label, so ChannelDashboard actually scrolls to the Workflow Runs breakdown
  // instead of just opening at its default position (previously only described in comments, never
  // wired up). Cleared by ChannelDashboard itself once consumed (onScrolledToSection below).
  const [dashboardScrollTarget, setDashboardScrollTarget] = useState<'workflowRuns' | null>(null);

  // Icon click: bring Dashboard to the front, over Data Sources / Settings.
  const handleDashboardToggle = () => {
    const next = openDashboardFlags({ isDashboardOpen, isSourcePanelOpen, isSettingsDrawerOpen });
    setIsDashboardOpen(next.isDashboardOpen);
    setIsSourcePanelOpen(next.isSourcePanelOpen);
    setIsSettingsDrawerOpen(next.isSettingsDrawerOpen);
  };

  // business_rules_workflow_count — same drawer transition as handleDashboardToggle above, plus
  // the scroll-target flag so the Dashboard lands on the Workflow Runs breakdown specifically.
  const handleOpenDashboardToWorkflowRuns = () => {
    setDashboardScrollTarget('workflowRuns');
    handleDashboardToggle();
  };

  // Closing Dashboard returns to the icon-only landing view for this channel.
  const handleDashboardClose = () => {
    const next = closeDashboardFlags({ isDashboardOpen, isSourcePanelOpen, isSettingsDrawerOpen });
    setIsDashboardOpen(next.isDashboardOpen);
  };

  // True when none of the three panels are open — shows just the Dashboard/Data Sources/Settings icons.
  // Disabled when VITE_DISABLE_ADMIN_UI=true in .env.local (build-time constant, DEV only).
  const channelIconLandingOpen = !isAdminUIDisabled() && isChannelIconLandingOpen({
    isDashboardOpen,
    isSourcePanelOpen,
    isSettingsDrawerOpen,
  });

  // The landing Drawer stays visible as a background whenever Data Sources, Dashboard, or
  // Settings opens from the icon-only landing screen — each of those panels slides in above
  // it (ChannelDashboard/ChannelSettings mount later, so they naturally paint on top of this
  // already-mounted Drawer at the same z-1300). Previously this only covered Data Sources;
  // Dashboard/Settings unmounted the landing Drawer instantly on open, which — since neither
  // panel is actually a full-page replacement — exposed the real thread inbox underneath for
  // a moment (or for as long as the panel stayed open) instead of the intended landing
  // backdrop. Keeping it mounted for all three panels fixes that flash.
  const landingDrawerVisible = !isAdminUIDisabled();

  // State for toaster/snackbar notifications
  const [toasterOpen, setToasterOpen] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterSeverity, setToasterSeverity] = useState<"success" | "error" | "warning" | "info">("info");

  const handleToasterClose = () => {
    setToasterOpen(false);
  };

  // Drop bulk selection when switching channel
  useEffect(() => {
    setBulkSelectedThreadIds([]);
  }, [channelId]);

  // State for embedding status
  const [embeddingStatus, setEmbeddingStatus] = useState<EmbeddingStatusResponse | null>(null);
  // State for AddLogDataSourceModal
  const [isAddDataSourceModalOpen, setIsAddDataSourceModalOpen] =
    useState(false);
  // State for AddThreadModal
  const [isAddThreadModalOpen, setIsAddThreadModalOpen] = useState(false);
  // State for subscription message limit modal (same style as channel limit)
  const [messageLimitModalOpen, setMessageLimitModalOpen] = useState(false);
  // When true, Email Sync in Progress is shown; hide "No email conversations" to avoid duplicate messages
  const [emailSyncInProgress, setEmailSyncInProgress] = useState(false);

  // State for Edit/Delete thread
  const [isEditThreadModalOpen, setIsEditThreadModalOpen] = useState(false);
  const [editingThread, setEditingThread] = useState<ApiThread | null>(null);
  const [editThreadTitle, setEditThreadTitle] = useState<string>("");
  const [editThreadDescription, setEditThreadDescription] =
    useState<string>("");
  const [threadMenuAnchor, setThreadMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedThreadForMenu, setSelectedThreadForMenu] =
    useState<ApiThread | null>(null);
  const [isEditingThread, setIsEditingThread] = useState<boolean>(false);
  // State for Move Thread modal (message + regular channels only)
  const [isMoveThreadModalOpen, setIsMoveThreadModalOpen] = useState<boolean>(false);
  const [threadToMove, setThreadToMove] = useState<ApiThread | null>(null);

  // Bulk thread selection (channel admins): checkboxes in list + full-panel actions in content area
  const [bulkSelectedThreadIds, setBulkSelectedThreadIds] = useState<string[]>([]);
  const [isBulkArchiveConfirmOpen, setIsBulkArchiveConfirmOpen] = useState(false);
  const [isBulkUnarchiveConfirmOpen, setIsBulkUnarchiveConfirmOpen] =
    useState(false);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [isBulkUnarchiving, setIsBulkUnarchiving] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // State for hover management (which message is being hovered)
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);

  // State for selected message (for task assignment and approval)
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );

  // State for email classifier selection (restore from URL so refresh keeps the same folder as the open thread)
  const [selectedEmailCategory, setSelectedEmailCategory] = useState<string>(() => {
    if (typeof window === "undefined") return "inbox";
    const c = new URLSearchParams(window.location.search).get(THREAD_CLASSIFICATION_QUERY_KEY);
    return c && c.length > 0 ? c : "inbox";
  });
  /** Latest folder for socket handlers that cannot close over fresh state (effect uses [] deps) */
  const selectedEmailCategoryRef = useRef(selectedEmailCategory);
  selectedEmailCategoryRef.current = selectedEmailCategory;
  const [selectedSidebarLabel, setSelectedSidebarLabel] = useState<string | null>(null);
  // Email metrics from classification panel (used for total count in header)
  const [emailMetricsForHeader, setEmailMetricsForHeader] = useState<EmailMetric[]>([]);

  // Resolve base metric key from classification_type (label_xxx / sublabel_xxx use same metric key for count/chip)
  const getBaseClassificationKey = (classificationType: string): string => {
    if (!classificationType) return classificationType;
    if (classificationType.startsWith("label_")) return classificationType.slice(6);
    if (classificationType.startsWith("sublabel_")) return classificationType.slice(9);
    return classificationType;
  };

  // State for message box visibility
  const [isMessageBoxVisible, setIsMessageBoxVisible] =
    useState<boolean>(false);

  // State to track if message box should show close icon
  const [showCloseIcon, setShowCloseIcon] = useState<boolean>(true);

  // State for message attachments
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);

  // Handle posting method content prefilling (Ask Prism7 panel always gets @Prism7 pre-filled)
  useEffect(() => {
    if (isMessageBoxVisible) {
      if (postingMethod === "ask-shay") {
        setNewComment(ASK_PRISM7_DEFAULT_CONTENT);
        // Force editor re-render with new content
        setEditorKey((prev) => prev + 1);
      } else if (postingMethod === "internal-reply" || postingMethod === "channel-reply") {
        setNewComment("");
        // Force editor re-render with empty content (direct reply / WhatsApp send)
        setEditorKey((prev) => prev + 1);
      }
    }
  }, [isMessageBoxVisible, postingMethod]);


  // State for compose box
  const [isComposeOpen, setIsComposeOpen] = useState<boolean>(false);

  // Opening compose clears bulk selection so the bulk full-panel view does not overlap compose
  useEffect(() => {
    if (isComposeOpen) {
      setBulkSelectedThreadIds([]);
    }
  }, [isComposeOpen]);
  const [isReplyOrForward, setIsReplyOrForward] = useState<boolean>(false);
  const [isForward, setIsForward] = useState<boolean>(false);
  const [composeData, setComposeData] = useState<{
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    content?: string;
    gmail_thread_id?: string | null;
    /** When forwarding, include original email attachments so recipient receives them */
    attachments?: Array<{
      attachment_id: string;
      filename: string;
      file_type: string;
      file_size: number;
    }>;
  } | null>(null);
  const [currentEmailResponse, setCurrentEmailResponse] = useState<any>(null);
  const [isGeneratingDraftForCompose, setIsGeneratingDraftForCompose] =
    useState<boolean>(false);
  const [draftContent, setDraftContent] = useState<string>("");
  const [draftMessageId, setDraftMessageId] = useState<string | null>(null);
  // Draft metadata from main_chat response: citations (Sources) and usage_metrics (confidence + tokens)
  const [draftCitations, setDraftCitations] = useState<Citation[] | null>(null);
  const [draftUsageMetrics, setDraftUsageMetrics] =
    useState<DraftUsageMetricsData | null>(null);

  // State for thread switch confirmation
  const [showThreadSwitchConfirmation, setShowThreadSwitchConfirmation] =
    useState<boolean>(false);
  const [pendingThreadSelection, setPendingThreadSelection] =
    useState<ApiThread | null>(null);

  // Browser back/forward: keep sidebar folder aligned with the classification query param
  useEffect(() => {
    const c = searchParams.get(THREAD_CLASSIFICATION_QUERY_KEY) ?? "inbox";
    setSelectedEmailCategory((prev) => (prev !== c ? c : prev));
  }, [searchParams]);

  // Effect to open panels based on URL query parameters on land or change
  useEffect(() => {
    const viewDashboard = searchParams.get("viewDashboard") === "true";
    const viewDS = searchParams.get("viewDS") === "true";
    const openAddDataSource =
      viewDS ||
      searchParams.get("openAddDataSource") === "true" ||
      searchParams.get("addDataSource") === "true";

    if (viewDashboard || viewDS || openAddDataSource) {
      if (viewDashboard) {
        setIsDashboardOpen(true);
      }
      if (viewDS || openAddDataSource) {
        // Data Sources closes Dashboard AND Settings if either was open, even via a direct
        // deep link — matches the icon toggle's own mutual-exclusion behavior. PR review fix:
        // routed through the same openSourcePanelFlags helper as the icon handler instead of
        // setting flags directly, so this path can't drift out of sync with it again.
        const next = openSourcePanelFlags({ isDashboardOpen, isSourcePanelOpen, isSettingsDrawerOpen });
        setIsDashboardOpen(next.isDashboardOpen);
        setIsSourcePanelOpen(next.isSourcePanelOpen);
        setIsSettingsDrawerOpen(next.isSettingsDrawerOpen);
      }
      if (openAddDataSource) {
        setInitialOpenAddDataSource(true);
      }
      // URL race fix — cleanup of viewDashboard/viewDS now happens centrally in
      // buildThreadsNavigate() the next time this component navigates, instead of racing a
      // 200ms timer against fetchThreads()'s own auto-select-first-thread navigate(). See that
      // function's own comment for why the timer approach silently lost the deep-link intent.
    }
  }, [searchParams]);

  // Compose handlers
  const handleComposeOpen = () => {
    setComposeData(null);
    setIsReplyOrForward(false);
    setIsForward(false);
    setIsComposeOpen(true);
    setDraftMessageId(null); // Clear draft message_id when opening new compose
    setDraftCitations(null);
    setDraftUsageMetrics(null);
  };

  // Filter handlers
  const handleFilterClick = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (filters: EmailFilters) => {
    // Clear active thread so we don't show the previous thread while loading
    clearTemporaryMessages();
    setBulkSelectedThreadIds([]);
    setSelectedThread(null);
    pendingThreadSelectionRef.current = null;
    // Clear URL thread id so fetchThreads selects the first (latest) thread from the new list
    if (channelId) {
      const isEmailCh = (channelData?.channel_type || "email") === "email";
      navigate(
        buildThreadsNavigate(channelId, undefined, searchParams, selectedEmailCategory, isEmailCh),
        { replace: true }
      );
    }
    setEmailFilters(filters);
    // Reset pagination when filters change
    setCurrentPage(1);
    setHasMore(true);
    // Close filter modal and fetch new thread list; fetchThreads will set latest as active
    setIsFilterModalOpen(false);
    fetchThreads(filters);
  };

  // Search handler - updates body filter and fetches threads
  const handleSearch = (searchText: string) => {
    const trimmed = searchText.trim();
    // Non-empty search narrows the list: clear URL thread id like handleApplyFilters so we do not prepend the old selection
    if (trimmed !== "") {
      clearTemporaryMessages();
      setBulkSelectedThreadIds([]);
      setSelectedThread(null);
      pendingThreadSelectionRef.current = null;
      if (channelId) {
        const isEmailCh = (channelData?.channel_type || "email") === "email";
        navigate(
          buildThreadsNavigate(channelId, undefined, searchParams, selectedEmailCategory, isEmailCh),
          { replace: true }
        );
      }
    }
    // Update emailFilters with new search text in body field
    const updatedFilters: EmailFilters = {
      ...emailFilters,
      body: trimmed,
    };
    setEmailFilters(updatedFilters);
    // Reset pagination when search changes
    setCurrentPage(1);
    setHasMore(true);
    // Fetch threads with updated filters
    fetchThreads(updatedFilters);
  };

  // Calculate active filter count
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (emailFilters.from_email.length > 0) count++;
    if (emailFilters.to_email.length > 0) count++;
    if (emailFilters.subject.trim() !== '') count++;
    if (emailFilters.body.trim() !== '') count++;
    if (emailFilters.has_attachment !== null) count++;
    if (emailFilters.date_from !== '' || emailFilters.date_to !== '') count++;
    if (emailFilters.is_archived !== null) count++;
    return count;
  };

  const handleComposeClose = () => {
    setIsComposeOpen(false);
    setComposeData(null);
    setIsReplyOrForward(false);
    setIsForward(false);
    setDraftMessageId(null); // Clear draft message_id when closing compose box
    setDraftCitations(null);
    setDraftUsageMetrics(null);
    setCurrentEmailResponse(null);
    setIsGeneratingDraftForCompose(false);
    setDraftContent(""); // Clear draft content when closing compose box
  };

  // Check if compose box has content that would be lost
  const hasComposeContent = () => {
    if (!isComposeOpen) return false;

    // If compose box is open, check for any content
    const hasContent =
      (composeData &&
        composeData.content &&
        composeData.content.trim() !== "") ||
      (composeData && composeData.to && composeData.to.length > 0) ||
      (composeData && composeData.cc && composeData.cc.length > 0) ||
      (composeData && composeData.bcc && composeData.bcc.length > 0) ||
      (composeData &&
        composeData.subject &&
        composeData.subject.trim() !== "") ||
      (draftContent && draftContent.trim() !== "");


    return hasContent;
  };

  // Check if compose box is open (simpler check for thread switching)
  const isComposeBoxOpen = () => {
    return isComposeOpen;
  };

  // Helper function to clear temporary messages
  const clearTemporaryMessages = () => {
    setTemporaryUserMessage(null);
    setIsAiThinking(false);
    setIsPosting(false);
  };

  // Handle thread selection with compose box check

  const handleThreadSelect = (thread: ApiThread) => {
    if (isComposeBoxOpen()) {
      setPendingThreadSelection(thread);
      setShowThreadSwitchConfirmation(true);
    } else {
      // Clear temporary messages when switching to a different thread
      if (selectedThread && selectedThread.id !== thread.id) {
        clearTemporaryMessages();
      }
      setSelectedThread(thread);
      // Update URL to include threadId
      if (channelId) {
        const isEmailCh = (channelData?.channel_type || "email") === "email";
        navigate(
          buildThreadsNavigate(channelId, thread.id, searchParams, selectedEmailCategory, isEmailCh),
          { replace: true }
        );
      }
    }
  };

  // Helper function to safely set selected thread (with compose box check)
  const safeSetSelectedThread = (thread: ApiThread | null) => {
    if (thread && isComposeBoxOpen()) {
      setPendingThreadSelection(thread);
      setShowThreadSwitchConfirmation(true);
    } else {
      // Clear temporary messages when switching to a different thread
      if (selectedThread && selectedThread.id !== thread?.id) {
        clearTemporaryMessages();
      }
      setSelectedThread(thread);
      // Update URL to include threadId when thread is selected
      if (thread && channelId) {
        const isEmailCh = (channelData?.channel_type || "email") === "email";
        navigate(
          buildThreadsNavigate(channelId, thread.id, searchParams, selectedEmailCategory, isEmailCh),
          { replace: true }
        );
      } else if (!thread && channelId) {
        // Navigate to channel-only URL when thread is cleared
        const isEmailCh = (channelData?.channel_type || "email") === "email";
        navigate(
          buildThreadsNavigate(channelId, undefined, searchParams, selectedEmailCategory, isEmailCh),
          { replace: true }
        );
      }
      // Close compose box when switching threads
      if (isComposeOpen) {
        handleComposeClose();
      }
    }
  };

  // Handle thread switch confirmation
  const handleConfirmThreadSwitch = () => {
    if (pendingThreadSelection) {
      // Clear temporary messages when switching to a different thread
      if (selectedThread?.id !== pendingThreadSelection.id) {
        clearTemporaryMessages();
      }
      setSelectedThread(pendingThreadSelection);
      // Update URL to include threadId
      if (channelId) {
        const isEmailCh = (channelData?.channel_type || "email") === "email";
        navigate(
          buildThreadsNavigate(
            channelId,
            pendingThreadSelection.id,
            searchParams,
            selectedEmailCategory,
            isEmailCh
          ),
          { replace: true }
        );
      }
      handleComposeClose();
    }
    setShowThreadSwitchConfirmation(false);
    setPendingThreadSelection(null);
  };

  const handleCancelThreadSwitch = () => {
    setShowThreadSwitchConfirmation(false);
    setPendingThreadSelection(null);
  };

  const handleGenerateDraft = async () => {
    if (!channelId || !selectedThread?.id) {
      showError("Missing channel or thread information");
      return;
    }

    // Validate subscription before generating draft (pass channelId when inside channel)
    try {
      const validationData = await validateSubscription(channelId ? { channelId } : {});
      
      if (validationData && !validationData.canCreateMessage) {
        // Show restriction modal if message creation is not allowed
        setOpenMessageRestrictionModal(true);
        return;
      }
    } catch (error) {
      // On error, allow user to proceed (fail open approach)
      console.error('Error validating subscription:', error);
    }

    setIsGeneratingDraft(true);

    setTimeout(() => {
      scrollToBottom();
    }, 1200);

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const companyId = getCompanyId();
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(
        `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.AI_CHAT.MAIN_CHAT}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            channel_id: channelId,
            thread_id: selectedThread?.id,
            message_id: getLatestMessageId(messagesRef.current),
            user_query: "",
            is_drafter: true,
            company_id: companyId,
            user_timezone: userTimezone,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Generate Draft API failed: ${response.status}`);
      }

      const data = await response.json();

      // Handle the response format
      if (data.status === "success" && data.data && Array.isArray(data.data)) {
        // Prefer per-message citations/usage_metrics from data.data[i], fallback to usage_metrics inside data (main_chat API)
        const messagesWithCitations: ApiMessage[] = data.data.map((message: any, index: number) => {
          const rawCits = message.citations ?? message.citation ?? data.data?.[index]?.citations ?? data.data?.[index]?.citation ?? data.citations ?? data.citation;
          const citations: Citation[] = Array.isArray(rawCits)
            ? rawCits.map((c: any) =>
                c && typeof c === "object" && "name" in c
                  ? { name: String(c.name), type: c.type != null ? String(c.type) : "file" }
                  : { name: String(c), type: "file" }
              )
            : [];
          const usageMetrics = message.usage_metrics ?? (data as any).data?.usage_metrics ?? data.data?.[0]?.usage_metrics;
          return {
            ...message,
            citations: citations.length > 0 ? citations : message.citations || [],
            ...(usageMetrics != null ? { usage_metrics: usageMetrics } : {}),
          };
        });
        
        // Add all messages from the response to the messages list
        // Deduplicate by ID and sequence_number to prevent duplicates
        setMessages((prevMessages: ApiMessage[]) => {
          const existingIds = new Set(prevMessages.map((msg: ApiMessage) => msg.id));
          const existingSequences = new Set(prevMessages.map((msg: ApiMessage) => msg.sequence_number));
          const newMessages: ApiMessage[] = messagesWithCitations.filter((msg: ApiMessage) => 
            !existingIds.has(msg.id) && !existingSequences.has(msg.sequence_number)
          );
          return [...prevMessages, ...newMessages];
        });
        setIsGeneratingDraftCompleted(true);

        // Emit socket event for real-time updates
        if (channelId && selectedThread?.id) {
          try {
            data.data.forEach((message: any) => {
              const eventData = {
                type: SOCKET_KEYS.THREAD_MESSAGE_CREATED,
                data: {
                  channelId: channelId,
                  threadId: selectedThread?.id,
                  messageId: message.id,
                },
              };
              socketService.sendSocketMessage(eventData);
            });
          } catch (error) {
            // Handle error silently
          }
        }
      } else if (data.status === "failed") {
        // Handle API failure case
        showError(data.message || "Generate Draft failed");
        return; // Don't throw error, just show toast
      } else {
        showError("Invalid response format from Generate Draft API");
        return; // Don't throw error, just show toast
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while generating draft";
      showError(errorMessage);
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  // Generate Draft for Compose Box
  const handleGenerateDraftForCompose = async () => {
    if (!channelId || !selectedThread?.id || !currentEmailResponse) {
      showError("Missing required information for Generate Draft");
      return;
    }

    // Validate subscription before generating draft (pass channelId when inside channel)
    try {
      const validationData = await validateSubscription(channelId ? { channelId } : {});
      
      if (validationData && !validationData.canCreateMessage) {
        // Show restriction modal if message creation is not allowed
        setOpenMessageRestrictionModal(true);
        return;
      }
    } catch (error) {
      // On error, allow user to proceed (fail open approach)
      console.error('Error validating subscription:', error);
    }

    setIsGeneratingDraftForCompose(true);

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const companyId = getCompanyId();
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Convert HTML to plain text by removing HTML tags
      let userQuery = "";
      if (currentEmailResponse.originalMail) {
        // Create a temporary div to parse HTML and extract text
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = currentEmailResponse.originalMail;
        userQuery = tempDiv.textContent || tempDiv.innerText || "";
      } else {
        userQuery = currentEmailResponse.rawString || "";
      }

      const response = await fetch(
        `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.AI_CHAT.MAIN_CHAT}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            channel_id: channelId,
            thread_id: selectedThread?.id,
            message_id: getLatestMessageId(messagesRef.current),
            user_query: userQuery,
            is_drafter: true,
            company_id: companyId,
            user_timezone: userTimezone,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Generate Draft API failed: ${response.status}`);
      }

      const data = await response.json();

      // Handle the response format
      if (
        data.status === "success" &&
        data.data &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        const draftMsg = data.data[0];
        // Support multiple response shapes: id, message_id at item or root
        const messageId =
          draftMsg.id ?? draftMsg.message_id ?? (data as any).message_id ?? (data as any).id;
        let generatedContent = draftMsg.content;

        // Convert markdown to HTML if markdown syntax is detected
        generatedContent = convertMarkdownToHtmlIfNeeded(generatedContent ?? "");
        // Flatten <p> to <br> so compose editor shows newlines correctly (same as thread view)
        generatedContent = flattenParagraphsToBr(generatedContent);

        // Store the draft message_id to pass to send email API
        setDraftMessageId(messageId);

        // Store citations (Sources) and usage_metrics from main_chat response
        const rawCitations = data.citations ?? data.citation;
        const normalizedCitations: Citation[] = Array.isArray(rawCitations)
          ? rawCitations.map((c: any) =>
              c && typeof c === "object" && "name" in c
                ? { name: String(c.name), type: c.type != null ? String(c.type) : "file" }
                : { name: String(c), type: "file" }
            )
          : [];
        setDraftCitations(normalizedCitations.length > 0 ? normalizedCitations : null);
        // main_chat API now returns usage_metrics inside data only (data.data.usage_metrics or data.data[0].usage_metrics)
        setDraftUsageMetrics(
          (data as any).data?.usage_metrics ?? data.data?.[0]?.usage_metrics ?? null
        );

        // Update the compose content by prepending the generated content
        setComposeData((prevData) => {
          if (prevData) {
            const currentContent = prevData.content || "";
            const newContent =
              generatedContent +
              (currentContent ? "<br><br>" + currentContent : "");
            return {
              ...prevData,
              content: newContent,
            };
          }
          return prevData;
        });

        // Emit socket event for real-time updates (other clients/tabs add draft via socket handler)
        if (channelId && selectedThread?.id && messageId) {
          try {
            const eventData = {
              type: SOCKET_KEYS.THREAD_MESSAGE_CREATED,
              data: {
                channelId: channelId,
                threadId: selectedThread?.id,
                messageId: String(messageId),
              },
            };
            socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }

        // Add draft message to thread view in real-time; main_chat response has same shape as messageById API
        if (selectedThread?.id) {
          const messagePayload = {
            ...draftMsg,
            id: messageId != null ? String(messageId) : draftMsg.id ?? `draft-${Date.now()}`,
            content: generatedContent ?? draftMsg.content ?? "",
            citations: draftMsg.citations ?? (normalizedCitations.length > 0 ? normalizedCitations : draftMsg.citation),
            usage_metrics: draftMsg.usage_metrics ?? (data as any).data?.usage_metrics ?? data.data?.[0]?.usage_metrics ?? null,
          };
          const newDraftMessage = normalizeMessageFromApi(messagePayload);
          setMessages((prevMessages) => {
            const exists = prevMessages.some(
              (m) =>
                m.id === newDraftMessage.id ||
                (newDraftMessage.sequence_number != null &&
                  m.sequence_number === newDraftMessage.sequence_number)
            );
            return exists ? prevMessages : [...prevMessages, newDraftMessage];
          });
          // Scroll messages container to bottom after DOM has the new message (longer delay + rAF so layout is done)
          const scrollAfterDraft = () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const container = messagesContainerRef.current;
                if (container) {
                  container.scrollTop = container.scrollHeight;
                }
              });
            });
          };
          setTimeout(scrollAfterDraft, 350);
        }

        showSuccess("Draft generated successfully!");
      } else if (data.status === "failed") {
        showError(data.message || "Generate Draft failed");
      } else {
        showError("Invalid response format from Generate Draft API");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while generating draft";
      showError(errorMessage);
    } finally {
      setIsGeneratingDraftForCompose(false);
    }
  };

  const handleComposeSend = async (content: string) => {
    // On successful email send (e.g. edit & send draft): one silent messages API call, no loading UI; emit socket so other tabs update
    try {
      const threadId = selectedThreadIdRef.current;
      if (!threadId) return;

      // Clear draft state so next compose is clean
      setDraftMessageId(null);
      setDraftCitations(null);
      setDraftUsageMetrics(null);

      // Skip the selectedThread useEffect so it doesn't call fetchMessages (and show loading); we do one silent fetch here
      silentRefreshThreadIdRef.current = threadId;
      // Skip socket THREAD_UPDATED message refetch for this tab (we already refetch here)
      weJustSentForThreadIdRef.current = threadId;
      setTimeout(() => {
        weJustSentForThreadIdRef.current = null;
      }, 2000);

      // Refetch thread for updated message_count, then one silent messages API call (channel + thread id in URL)
      const updatedThread = await fetchThreadById(threadId);
      if (updatedThread) {
        setApiThreads((prev) => prev.map((t) => (t.id === threadId ? updatedThread : t)));
        setSelectedThread((prev) => (prev?.id === threadId ? updatedThread : prev));
        await fetchMessages(threadId, false, updatedThread.message_count ?? 0, true);
      } else {
        await fetchMessages(threadId, false, undefined, true);
      }

      // Emit socket so other clients/tabs refresh this thread
      if (channelId) {
        socketService.sendSocketMessage({
          type: SOCKET_KEYS.THREAD_UPDATED,
          data: { channelId, threadId, timestamp: Date.now() },
        });
      }
    } catch (error) {
      // Non-fatal: toast already shown in ComposeBox
    }
  };

  // Helper function to format reply subject (avoid duplicate "Re:")
  const formatReplySubject = (subject: string): string => {
    if (!subject) return "";
    
    // Strip all leading "Re:" prefixes (case-insensitive) to ensure we only have one
    // Handle variations like "Re:", "RE:", "re:", "Re: ", "RE: ", etc.
    // This regex matches one or more "Re:" prefixes at the start
    let cleanedSubject = subject.replace(/^(re:\s*)+/i, "").trim();
    
    // Add a single "Re: " prefix
    return `Re: ${cleanedSubject}`;
  };

  // Helper function to format forward subject (avoid duplicate "Fwd:")
  const formatForwardSubject = (subject: string): string => {
    if (!subject) return "";
    
    // Strip all leading "Fwd:" prefixes (case-insensitive) to ensure we only have one
    // Handle variations like "Fwd:", "FWD:", "fwd:", "Fwd: ", "FWD: ", etc.
    // This regex matches one or more "Fwd:" prefixes at the start
    let cleanedSubject = subject.replace(/^(fwd:\s*)+/i, "").trim();
    
    // Add a single "Fwd: " prefix
    return `Fwd: ${cleanedSubject}`;
  };

  // Reply handler
  const handleReply = (emailResponse: any) => {
    // Store the emailResponse for Generate Draft functionality
    setCurrentEmailResponse(emailResponse);
    setDraftMessageId(null); // Clear draft message_id for regular replies
    setDraftCitations(null);
    setDraftUsageMetrics(null);

    // Current inbox identity (dynamic: whatever channel/inbox the user is in)
    const currentInboxEmailReply = getCurrentInboxEmailFromChannelName(channelData?.name);
    const fromEmail = (emailResponse.from?.replace(/"/g, "") || "").trim().toLowerCase();
    // Treat as sent when folder is Sent or when sender is the current user (inbox)
    const isSentEmail =
      (emailResponse.emailFolder && String(emailResponse.emailFolder).toLowerCase() === "sent") ||
      (currentInboxEmailReply && fromEmail === currentInboxEmailReply);

    const isCurrentUserReply = (recipient: string): boolean =>
      Boolean(recipient && currentInboxEmailReply && extractEmailFromRecipient(recipient) === currentInboxEmailReply);

    // For sent emails: reply To = original To recipients (excluding current user). For received: To = sender.
    let replyTo: string[] = [];
    if (isSentEmail && emailResponse.toUsers) {
      const originalTo: string[] = String(emailResponse.toUsers)
        .split(",")
        .map((email: string) => email.trim().replace(/"/g, ""))
        .filter((email: string): email is string => Boolean(email && !isCurrentUserReply(email)));
      replyTo = [...new Set(originalTo)];
    } else if (emailResponse.from) {
      replyTo = [emailResponse.from.replace(/"/g, "").trim()];
    }

    // Format the original message content for Gmail-style reply
    const originalContent =
      emailResponse.originalMail || emailResponse.rawString || "";
    const formattedContent = `
<p><br></p>
<div data-collapsible-content data-collapsed="true">
  <div style="margin-bottom: 10px;">
    <strong>On ${new Date(
      emailResponse.created_at || Date.now()
    ).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })} ${emailResponse.from} wrote:</strong>
  </div>
  <div style="margin-bottom: 10px;">
    <strong>Subject:</strong> ${emailResponse.subject || ""}
  </div>
  <div style="margin-bottom: 10px;">
    <strong>To:</strong> ${emailResponse.toUsers || ""}
  </div>
  ${
    emailResponse.ccUsers
      ? `<div style="margin-bottom: 10px;"><strong>CC:</strong> ${emailResponse.ccUsers}</div>`
      : ""
  }
  <div style="margin-top: 15px;">
    ${originalContent.replace(/\n/g, "<br>")}
  </div>
</div>
    `.trim();

    const replyData = {
      to: replyTo,
      cc: [], // Reply should not prefill CC field
      bcc: [], // Reply should not prefill BCC field
      subject: formatReplySubject(emailResponse.subject || ""),
      content: formattedContent,
      gmail_thread_id: selectedThread?.email_thread_id,
    };
    setComposeData(replyData);
    setIsReplyOrForward(true);
    setIsForward(false);
    setIsComposeOpen(true);
  };

  // Reply All handler
  const handleReplyAll = (emailResponse: any) => {
    // Store the emailResponse for Generate Draft functionality
    setCurrentEmailResponse(emailResponse);
    setDraftMessageId(null); // Clear draft message_id for reply all
    setDraftCitations(null);
    setDraftUsageMetrics(null);

    // Format the original message content for Gmail-style reply
    const originalContent =
      emailResponse.originalMail || emailResponse.rawString || "";
    // Prepend empty paragraph so cursor has a clear position at top (matches Reply behavior)
    const formattedContent = `
<p><br></p>
<div data-collapsible-content data-collapsed="true">
  <div style="margin-bottom: 10px;">
    <strong>On ${new Date(
      emailResponse.created_at || Date.now()
    ).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })} ${emailResponse.from.replace(/"/g, "")} wrote:</strong>
  </div>
  <div style="margin-bottom: 10px;">
    <strong>Subject:</strong> ${emailResponse.subject || ""}
  </div>
  <div style="margin-bottom: 10px;">
    <strong>To:</strong> ${emailResponse.toUsers || ""}
  </div>
  ${
    emailResponse.ccUsers
      ? `<div style="margin-bottom: 10px;"><strong>CC:</strong> ${emailResponse.ccUsers}</div>`
      : ""
  }
  <div style="margin-top: 15px;">
    ${originalContent.replace(/\n/g, "<br>")}
  </div>
</div>
    `.trim();

    // Current inbox identity (dynamic: whatever channel/inbox the user is in)
    const currentInboxEmail = getCurrentInboxEmailFromChannelName(channelData?.name);

    const isCurrentUser = (recipient: string): boolean => {
      if (!recipient || !currentInboxEmail) return false;
      return extractEmailFromRecipient(recipient) === currentInboxEmail;
    };

    // Parse original recipients
    const originalTo: string[] = emailResponse.toUsers
      ? emailResponse.toUsers.split(",").map((email: string) => email.trim())
      : [];
    const originalCC: string[] = emailResponse.ccUsers
      ? emailResponse.ccUsers.split(",").map((email: string) => email.trim())
      : [];

    // For Reply All: Include sender + all original To, excluding current user (inbox From)
    const allToRecipients: string[] = [
      emailResponse.from.replace(/"/g, ""),
      ...originalTo,
    ].filter((recipient: string) => recipient && !isCurrentUser(recipient));

    // CC: original CC recipients except current user
    const allCCRecipients: string[] = originalCC.filter(
      (recipient: string) => recipient && !isCurrentUser(recipient)
    );

    // Remove duplicates
    const uniqueToRecipients: string[] = [...new Set(allToRecipients)];
    const uniqueCCRecipients: string[] = [...new Set(allCCRecipients)];

    const replyAllData = {
      to: uniqueToRecipients,
      cc: uniqueCCRecipients,
      subject: formatReplySubject(emailResponse.subject || ""),
      content: formattedContent,
      gmail_thread_id: selectedThread?.email_thread_id,
    };
    setComposeData(replyAllData);
    setIsReplyOrForward(true);
    setIsForward(false);
    setIsComposeOpen(true);
  };

  // Forward handler – pass attachments so the forwarded email includes original attachments
  const handleForward = (
    emailResponse: any,
    attachments?: Array<{
      attachment_id: string;
      filename: string;
      file_type: string;
      file_size: number;
    }> | null
  ) => {
    setDraftMessageId(null); // Clear draft message_id for forward
    setDraftCitations(null);
    setDraftUsageMetrics(null);
    // Format the original message content for Gmail-style forward
    const originalContent =
      emailResponse.originalMail || emailResponse.rawString || "";
    // Prepend empty paragraph so cursor has a clear position at top (matches Reply behavior)
    const formattedContent = `
<p><br></p>
  <div style="margin-bottom: 10px;">
    <strong>---------- Forwarded message ----------</strong>
  </div>
  <div style="margin-bottom: 10px;">
    <strong>From:</strong> ${emailResponse.from.replace(/"/g, "") || ""}
  </div>
  <div style="margin-bottom: 10px;">
    <strong>Date:</strong> ${new Date(
      emailResponse.created_at || Date.now()
    ).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}
  </div>
  <div style="margin-bottom: 10px;">
    <strong>Subject:</strong> ${emailResponse.subject || ""}
  </div>
  <div style="margin-bottom: 10px;">
    <strong>To:</strong> ${emailResponse.toUsers || ""}
  </div>
  ${
    emailResponse.ccUsers
      ? `<div style="margin-bottom: 10px;"><strong>CC:</strong> ${emailResponse.ccUsers}</div>`
      : ""
  }
  <div style="margin-top: 15px;">
    ${originalContent.replace(/\n/g, "<br>")}
  </div>
    `.trim();

    // Include original email attachments so recipient receives them when forwarding
    const attachmentList =
      attachments && attachments.length > 0
        ? attachments.map((a) => ({
            attachment_id: a.attachment_id,
            filename: a.filename,
            file_type: a.file_type,
            file_size: a.file_size,
          }))
        : undefined;

    const forwardData = {
      to: [],
      cc: [],
      bcc: [],
      subject: formatForwardSubject(emailResponse.subject || ""),
      content: formattedContent,
      gmail_thread_id: selectedThread?.email_thread_id,
      attachments: attachmentList,
    };
    setComposeData(forwardData);
    setIsReplyOrForward(true);
    setIsForward(true);
    setIsComposeOpen(true);
  };

  // Common Reply handler - replies to the last email in the thread
  const handleCommonReply = () => {
    // Find the last email message in the thread
    const lastEmailMessage = messages
      .filter((msg) => msg.emailResponse)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

    if (lastEmailMessage?.emailResponse) {
      handleReply(lastEmailMessage.emailResponse);
    } else {
      showError("No email found in this thread to reply to");
    }
  };

  // Common Reply All handler - replies to all recipients of the last email in the thread
  const handleCommonReplyAll = () => {
    // Find the last email message in the thread
    const lastEmailMessage = messages
      .filter((msg) => msg.emailResponse)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

    if (lastEmailMessage?.emailResponse) {
      handleReplyAll(lastEmailMessage.emailResponse);
    } else {
      showError("No email found in this thread to reply all");
    }
  };

  // Common Forward handler - forwards the last email in the thread
  const handleCommonForward = () => {
    // Find the last email message in the thread
    const lastEmailMessage = messages
      .filter((msg) => msg.emailResponse)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

    if (lastEmailMessage?.emailResponse) {
      handleForward(
        lastEmailMessage.emailResponse,
        lastEmailMessage.attachments ?? undefined
      );
    } else {
      showError("No email found in this thread to forward");
    }
  };

  // Whether to show Reply All (based on last email in thread) – uses shared helper
  const showReplyAllButton = useMemo(() => {
    const lastEmailMessage = messages
      .filter((msg) => msg.emailResponse)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    return shouldShowReplyAll(lastEmailMessage?.emailResponse);
  }, [messages]);

  // Edit & Send handler for draft messages
  const handleEditAndSend = (message: any) => {
    // Store the draft message_id to pass to send email API
    setDraftMessageId(message.id);
    // Existing draft has no citations/usage_metrics from main_chat; clear any stale values
    setDraftCitations(null);
    setDraftUsageMetrics(null);

    // Find the last email message in the thread to reply to
    const lastEmailMessage = messages
      .filter((msg) => msg.emailResponse)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

    if (lastEmailMessage?.emailResponse) {
      // Check if the draft content is already HTML (contains HTML tags)
      const isHtmlContent = (content: string) => {
        if (!content) return false;
        // Check for common HTML tags that indicate formatted content
        const htmlTagPattern = /<\/?(p|div|span|strong|b|em|i|u|ul|ol|li|br|h[1-6]|blockquote|a|img)[^>]*>/i;
        return htmlTagPattern.test(content);
      };

      // Use common markdown detection utility

      // Process draft content
      let draftContent = message.content || "";
      if (isHtmlContent(draftContent)) {
        // Content is already HTML, use as-is
        // No conversion needed
      } else if (hasMarkdown(draftContent)) {
        // Content is Markdown, convert to HTML using common utility then flatten to <br> for compose
        draftContent = flattenParagraphsToBr(markdownToHtml(draftContent));
      } else {
        // Plain text, just convert line breaks
        draftContent = draftContent
          .replace(/\r\n/g, "<br>")
          .replace(/\n/g, "<br>");
      }

      // Format the original message content for Gmail-style reply (same as handleReply)
      const originalContent =
        lastEmailMessage.emailResponse.originalMail ||
        lastEmailMessage.emailResponse.rawString ||
        "";
      const quotedContent = `
<p><br></p>
<div data-collapsible-content data-collapsed="true">
  <div style="margin-bottom: 10px;">
    <strong>On ${new Date(
      lastEmailMessage.emailResponse.created_at || Date.now()
    ).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })} ${lastEmailMessage.emailResponse.from} wrote:</strong>
  </div>
  <div style="margin-bottom: 10px;">
    <strong>Subject:</strong> ${lastEmailMessage.emailResponse.subject || ""}
  </div>
  <div style="margin-bottom: 10px;">
    <strong>To:</strong> ${lastEmailMessage.emailResponse.toUsers || ""}
  </div>
  ${
    lastEmailMessage.emailResponse.ccUsers
      ? `<div style="margin-bottom: 10px;"><strong>CC:</strong> ${lastEmailMessage.emailResponse.ccUsers}</div>`
      : ""
  }
  <div style="margin-top: 15px;">
    ${originalContent.replace(/\n/g, "<br>")}
  </div>
</div>
      `.trim();

      // Combine draft content with quoted content
      const combinedContent = draftContent + quotedContent;

      // Current inbox identity (dynamic: whatever channel/inbox the user is in)
      const currentInboxEmailEdit = getCurrentInboxEmailFromChannelName(channelData?.name);
      const fromEmailEdit = (lastEmailMessage.emailResponse.from?.replace(/"/g, "") || "").trim().toLowerCase();
      const isSentEmailEdit =
        (lastEmailMessage.emailResponse.emailFolder && String(lastEmailMessage.emailResponse.emailFolder).toLowerCase() === "sent") ||
        (currentInboxEmailEdit && fromEmailEdit === currentInboxEmailEdit);

      const isCurrentUserEdit = (recipient: string): boolean =>
        Boolean(recipient && currentInboxEmailEdit && extractEmailFromRecipient(recipient) === currentInboxEmailEdit);

      let replyToEdit: string[] = [];
      if (isSentEmailEdit && lastEmailMessage.emailResponse.toUsers) {
        const originalToEdit: string[] = String(lastEmailMessage.emailResponse.toUsers)
          .split(",")
          .map((email: string) => email.trim().replace(/"/g, ""))
          .filter((email: string): email is string => Boolean(email && !isCurrentUserEdit(email)));
        replyToEdit = [...new Set(originalToEdit)] as string[];
      } else if (lastEmailMessage.emailResponse.from) {
        replyToEdit = [lastEmailMessage.emailResponse.from.replace(/"/g, "").trim()];
      }

      const ccList: string[] = lastEmailMessage.emailResponse.ccUsers
        ? String(lastEmailMessage.emailResponse.ccUsers)
            .split(",")
            .map((email: string) => email.trim().replace(/"/g, ""))
            .filter((email: string): email is string => Boolean(email && !isCurrentUserEdit(email)))
        : [];

      // Set up compose data like a reply (From remains inbox via ComposeBox channelName)
      const replyData = {
        to: replyToEdit,
        cc: ccList,
        subject: formatReplySubject(lastEmailMessage.emailResponse.subject || ""),
        content: combinedContent,
        gmail_thread_id: selectedThread?.email_thread_id,
      };

      setComposeData(replyData);
      setIsReplyOrForward(true);
      setIsForward(false);
      setIsComposeOpen(true);
    } else {
      showError("No email found in this thread to reply to");
    }
  };

  const handleDeleteDraft = async (message: any) => {
    const draftId = String(message?.id || "");
    if (!draftId) {
      showError("Draft ID is missing");
      return;
    }

    if (deletingDraftById[draftId]) {
      return;
    }

    setDeletingDraftById((prev) => ({ ...prev, [draftId]: true }));

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      await hideDraft(draftId, token);

      // Remove hidden draft from the current thread view immediately.
      setMessages((prevMessages) =>
        prevMessages.filter((currentMessage) => String(currentMessage.id) !== draftId)
      );

      if (draftMessageId === draftId) {
        setDraftMessageId(null);
        setDraftCitations(null);
        setDraftUsageMetrics(null);
      }

      showSuccess("Draft Message Deleted Successfully!");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to hide draft";
      showError(errorMessage);
    } finally {
      setDeletingDraftById((prev) => {
        const updated = { ...prev };
        delete updated[draftId];
        return updated;
      });
    }
  };

  // Edit internal message handler
  const handleEditMessage = (message: ApiMessage) => {
    setIsEditing(true);
    setMessageToEdit(message);
    setEditedMessageContent(message.content);
    //populate attachment name and sizes
    if (message.attachments) {
      setEditedAttachments(message.attachments);
    } else {
      setEditedAttachments([]);
    }
    //Prefill the internal message input field with the content of the message being edited.
    setPostingMethod("internal-reply");
    setShowCloseIcon(false);
    setIsMessageBoxVisible(true);
    setNewComment(message.content);
    setEditorKey((prevKey) => prevKey + 1);
  };

  // Helper function to clean and process message content
  // Removes HTML tags, handles mentions, preserves newlines, and decodes HTML entities
  const cleanMessageContent = (content: string): string => {
    let cleanContent = content;

    // Replace mention HTML tags with plain text @mentions; prefer data-label for full name
    const mentionRegex =
      /<span[^>]*class="mention"[^>]*(?:data-label="([^"]*)")?[^>]*>@([^<]+)<\/span>/g;
    cleanContent = cleanContent.replace(mentionRegex, (_, label, inner) => `@${label || inner}`);

    // Preserve newlines by converting <br> and </p> tags to newlines before removing other HTML
    cleanContent = cleanContent.replace(/<br\s*\/?>/gi, "\n");
    cleanContent = cleanContent.replace(/<\/p>/gi, "\n");
    cleanContent = cleanContent.replace(/<p[^>]*>/gi, "");

    // Also handle any remaining HTML tags
    cleanContent = cleanContent.replace(/<[^>]*>/g, "");

    // Decode HTML entities (like &nbsp;)
    const textarea = document.createElement("textarea");
    textarea.innerHTML = cleanContent;
    cleanContent = textarea.value;

    // Clean up extra whitespace but preserve newlines
    cleanContent = cleanContent.replace(/[ \t]+/g, " "); // Replace multiple spaces/tabs with single space
    cleanContent = cleanContent.replace(/\n\s*\n\s*\n/g, "\n\n"); // Replace multiple newlines with double newlines
    cleanContent = cleanContent.trim();

    return cleanContent;
  };

  // Helper function to handle attachment uploads
  // Returns array of attachment IDs (existing + newly uploaded)
  const handleAttachmentUploads = async (
    existingAttachmentIds: string[],
    filesToUpload: File[],
    channelId: string | null,
    threadId: string | undefined
  ): Promise<string[]> => {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
    let attachmentIds: string[] = [...existingAttachmentIds];

    // If there are new files to upload, upload them
    if (filesToUpload.length > 0) {
      if (!channelId) {
        throw new Error('Channel ID is required to upload attachments');
      }
      const uploadResult = await uploadMessageAttachments(
        filesToUpload,
        token,
        channelId,
        threadId
      );
      // Add new attachment IDs to existing ones
      attachmentIds = [...attachmentIds, ...uploadResult.attachment_ids];
    }

    return attachmentIds;
  };

  // Update internal message handler
  const handleUpdateMessage = async () => {
    // Check if we have a message to edit and content
    if (!messageToEdit?.id) {
      showError("No message selected for editing");
      return;
    }

    // Get the current content from either editedMessageContent or newComment
    const messageToProcess = editedMessageContent || newComment;
    
    // Check if there's actual text content (not just HTML tags)
    const hasContent = messageToProcess.replace(/<[^>]*>/g, "").trim();
    if (!hasContent) {
      showError("Message content cannot be empty");
      return;
    }

    if (isPosting || isSelectedThreadArchived || isChannelArchived) {
      return;
    }

    setIsPosting(true);

    try {
      // Clean the content using helper function
      const cleanContent = cleanMessageContent(messageToProcess);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";

      // Get existing attachment IDs from edited attachments
      // Existing attachments have attachment_id property, new attachments have file property
      const existingAttachmentIds: string[] = editedAttachments && editedAttachments.length > 0
        ? editedAttachments
            .filter((att) => att.attachment_id || att.id) // Only include attachments that have an ID (existing attachments)
            .map((att) => att.attachment_id || att.id)
        : [];

      // Get new files to upload from edited attachments (files with 'file' property are new)
      // In edit mode, new files are added to editedAttachments, not selectedFiles
      const newFilesToUpload: File[] = editedAttachments && editedAttachments.length > 0
        ? editedAttachments
            .filter((att) => att.file) // Only include attachments that have a file property (new attachments)
            .map((att) => att.file)
        : [];

      // Handle attachment uploads using helper function
      let attachmentIds: string[] = [];
      try {
        attachmentIds = await handleAttachmentUploads(
          existingAttachmentIds,
          newFilesToUpload,
          channelId ?? null,
          selectedThread?.id
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachments';
        showError(errorMessage);
        setIsPosting(false);
        return;
      }

      // Prepare update payload
      const updatePayload: any = {
        message_id: messageToEdit.id,
        content: cleanContent,
        thread_id: selectedThread?.id,
      };

      // Add attachment_ids if any attachments exist
      if (attachmentIds.length > 0) {
        updatePayload.attachment_ids = attachmentIds;
      }

      // Call the update message API
      const updateResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.UPDATE_MESSAGE}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(updatePayload),
        }
      );

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to update message: ${updateResponse.status}`;
        throw new Error(errorMessage);
      }

      // Get the updated message data
      const updatedMessageData = await updateResponse.json();

      // Update the message in the state without a full refresh
      setMessages((prevMessages) =>
        prevMessages.map((message) =>
          message.id === messageToEdit.id ? updatedMessageData : message
        )
      );

      // Emit socket event for real-time updates
      if (channelId && selectedThread?.id) {
        try {
          const eventData = {
            type: SOCKET_KEYS.THREAD_MESSAGE_UPDATED,
            data: {
              channelId: channelId,
              threadId: selectedThread.id,
              messageId: updatedMessageData.id,
              content: updatedMessageData.content,
              updatedAt: updatedMessageData.updated_at,
              emailResponse: updatedMessageData.emailResponse,
              citations: updatedMessageData.citations,
            },
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }

      // Reset editing state
      setIsEditing(false);
      setMessageToEdit(null);
      setEditedMessageContent("");
      setEditedAttachments([]);
      setNewComment("");
      setSelectedFiles([]);
      setIsEditorFocused(false);
      setEditorKey((prev) => prev + 1);

      // Clear any pending debounced updates
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
        contentUpdateTimeoutRef.current = null;
      }

      // Close the editor UI after successful update
      setPostingMethod("ask-shay"); // Reset to default
      setIsMessageBoxVisible(false);
      setShowCloseIcon(true); // Reset for next time

      showSuccess("Message updated successfully!");
      setIsPosting(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update message";
      showError(errorMessage);
      setIsPosting(false);
      // Don't close UI on error - user can try again or cancel
    }
  };

  //Delete internal message handler
  const handleDeleteMessage = (message: ApiMessage) => {
    setMessageToDelete(message);
    setIsDeleteMessageConfirmOpen(true);
  };

  const handleCancelDeleteMessage = () => {
    setIsDeleteMessageConfirmOpen(false);
    setMessageToDelete(null);
  };

  const handleConfirmDeleteMessage = async () => {
    if (!messageToDelete) return;

    setIsDeletingMessage(true);

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      // Hide API only for sent, inbox, spam, no reply; other messages use delete API
      const emailFolder = String(messageToDelete.emailResponse?.emailFolder || "").toLowerCase();
      const useHideApi =
        messageToDelete.emailResponse &&
        ["sent", "inbox", "spam", "no reply"].includes(emailFolder);

      if (useHideApi) {
        // Call hide API: /api/v1/messages/message/{message_id}/hide
        const response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.HIDE_MESSAGE(messageToDelete.id)}`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        const response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.DELETE_MESSAGE}`,
          {
            method: "DELETE",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              message_id: messageToDelete.id,
              thread_id: selectedThread?.id,
            }),
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      // Drop the prompt and its paired AI row in one update (no full refetch needed)
      const deletedId = messageToDelete.id;
      setMessages((prevMessages) => {
        const removeIds = getMessageIdsToRemoveForDeletedPrompt(prevMessages, deletedId);
        return prevMessages.filter((message) => !removeIds.has(message.id));
      });
      // Emit socket event for real-time updates
      if (channelId && selectedThread?.id) {
        try {
          const eventData = {
            type: SOCKET_KEYS.THREAD_MESSAGE_DELETED,
            data: { channelId: channelId, threadId: selectedThread?.id, messageId: messageToDelete.id },
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }

      showSuccess(useHideApi ? "Email deleted successfully." : "Message deleted successfully!");
    } catch (error) {
      showError("Failed to delete message. Please try again.");
    } finally {
      setIsDeletingMessage(false);
      setIsDeleteMessageConfirmOpen(false);
      setMessageToDelete(null);
    }
  };

  // Sort threads by most recent activity (last_message_at or updated_at) descending for consistent list order
  const sortThreadsByLastActivity = (threadList: ApiThread[]) => {
    return [...threadList].sort((a, b) => {
      const aTime = new Date(a.last_message_at || a.updated_at || 0).getTime();
      const bTime = new Date(b.last_message_at || b.updated_at || 0).getTime();
      return bTime - aTime; // newest first
    });
  };

  // Filter threads based on selected email category, then sort by last activity
  const getFilteredThreads = () => {
    let filtered: ApiThread[];
    switch (selectedEmailCategory) {
      case "inbox":
        filtered = apiThreads.filter((t) => !t.is_archived);
        break;
      case "archived":
        filtered = apiThreads.filter((t) => t.is_archived);
        break;
      case "sent":
      case "spam":
      case "no_reply":
        // For these categories, the API already filters based on classification_type
        filtered = apiThreads;
        break;
      default:
        filtered = apiThreads;
    }
    return sortThreadsByLastActivity(filtered);
  };

  // State for thread creation loading
  const [isCreatingThread, setIsCreatingThread] = useState<boolean>(false);

  // State for channel data
  const [channelData, setChannelData] = useState<ChannelResponse | null>(null);
  const channelDataRef = useRef<ChannelResponse | null>(null);
  channelDataRef.current = channelData;

  const [channelLoading, setChannelLoading] = useState<boolean>(true);
  const [channelError, setChannelError] = useState<string | null>(null);

  // Derive channel type from channel data; default to 'email' for legacy channels
  // so classification_type (e.g. inbox) is sent to threads API when opening from supportSpace
  const channelType = channelData?.channel_type || 'email';

  /** Spam folder banner: null = closed; 10–1 = seconds left, then auto-closes */
  const [spamFolderCountdown, setSpamFolderCountdown] = useState<number | null>(null);
  /** Once we start the spam notice for a channel, do not restart on thread switches (only on re-entering Spam or changing channel) */
  const spamFolderNoticeShownForChannelRef = useRef<string | null>(null);

  // Clear header email metrics when leaving email channel so we don't show stale count
  useEffect(() => {
    if (!channelId || channelType !== "email") {
      setEmailMetricsForHeader([]);
    }
  }, [channelId, channelType]);

  // Sync label with selected category when user enters channel - ensures label shows on page load
  useEffect(() => {
    if (channelId && channelType === "email" && selectedEmailCategory) {
      const label = STANDARD_CATEGORY_LABELS[selectedEmailCategory];
      if (label) {
        setSelectedSidebarLabel(label);
      }
      // For custom labels, onCategorySelect provides the correct label when user clicks
    }
  }, [channelId, channelType, selectedEmailCategory]);

  // Start Spam notice once per "land" in Spam (per channel), not on every thread switch
  useEffect(() => {
    if (channelType !== "email" || selectedEmailCategory !== "spam") {
      setSpamFolderCountdown(null);
      spamFolderNoticeShownForChannelRef.current = null; // Re-show if user returns to Spam later
      return;
    }
    const channelKey = channelId ?? "";
    // Wait for route param so we do not treat "" and a real id as two separate "lands"
    if (!channelKey) {
      setSpamFolderCountdown(null);
      return;
    }
    const alreadyStartedForThisChannel =
      spamFolderNoticeShownForChannelRef.current === channelKey;
    if (threadsLoading || messagesLoading) {
      // First load in Spam: keep banner hidden until data is ready; later loads (other thread): do not clear countdown
      if (!alreadyStartedForThisChannel) {
        setSpamFolderCountdown(null);
      }
      return;
    }
    if (!alreadyStartedForThisChannel) {
      setSpamFolderCountdown(SPAM_FOLDER_NOTICE_COUNTDOWN_SECONDS);
      spamFolderNoticeShownForChannelRef.current = channelKey;
    }
  }, [channelId, channelType, selectedEmailCategory, threadsLoading, messagesLoading]);

  // Decrement spam banner every second; after 1 it closes
  useEffect(() => {
    if (spamFolderCountdown === null) return;
    const id = window.setTimeout(() => {
      setSpamFolderCountdown((prev) => {
        if (prev === null || prev <= 1) return null;
        return prev - 1;
      });
    }, 1000);
    return () => clearTimeout(id);
  }, [spamFolderCountdown]);

  // State for delete confirmation dialog
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] =
    useState<boolean>(false);
  const [threadToDelete, setThreadToDelete] = useState<ApiThread | null>(null);
  const [isDeletingThread, setIsDeletingThread] = useState<boolean>(false);

  // State for archive functionality
  const [showArchivedThreads, setShowArchivedThreads] =
    useState<boolean>(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] =
    useState<boolean>(false);
  const [threadToArchive, setThreadToArchive] = useState<ApiThread | null>(
    null
  );
  const [isArchivingThread, setIsArchivingThread] = useState<boolean>(false);
  const [isUnarchivingThread, setIsUnarchivingThread] =
    useState<boolean>(false);
  const fetchThreadsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchChannelDataTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // State for email filters
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [emailFilters, setEmailFilters] = useState<EmailFilters>({
    from_email: [],
    to_email: [],
    subject: '',
    body: '',
    has_attachment: null,
    date_from: '',
    date_to: '',
    is_archived: null,
  });
  
  // Refs to track API calls and prevent duplicates
  const lastFetchedChannelIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeRequestsRef = useRef<Set<string>>(new Set());
  const fetchAllChannelDataRef = useRef<(cid: string, signal: AbortSignal) => Promise<void>>(() => Promise.resolve());
  // Ref for channelId effect to call only fetchChannelData (avoids double fetchThreads with category effect)
  const fetchChannelDataRef = useRef<() => Promise<void>>(() => Promise.resolve());
  
  // Ref to temporarily store thread ID that should be selected after category update
  const pendingThreadSelectionRef = useRef<string | null>(null);

  // Full reload: keep ?classification= (Sent / Inbox / …) but clear modal + archived list filters and drop threadId so the first row becomes active
  useEffect(() => {
    if (!channelId) return;
    if (!isNavigationReload()) return;
    if (threadReloadStripDoneChannelIds.has(channelId)) return;
    threadReloadStripDoneChannelIds.add(channelId);

    setEmailFilters({ ...DEFAULT_EMAIL_FILTERS });
    setShowArchivedThreads(false);
    setBulkSelectedThreadIds([]);
    pendingThreadSelectionRef.current = null;
    setCurrentPage(1);
    setHasMore(true);

    const params = new URLSearchParams(
      typeof window !== "undefined" ? window.location.search : ""
    );
    const classification = params.get(THREAD_CLASSIFICATION_QUERY_KEY) ?? "inbox";
    const isEmailCh = (channelDataRef.current?.channel_type || "email") === "email";

    navigate(
      buildThreadsNavigate(channelId, undefined, params, classification, isEmailCh),
      { replace: true }
    );
  }, [channelId, navigate]);

  // When true, URL threadId effect must not fetch-by-id (category switch; URL may still have stale threadId)
  const categorySwitchInProgressRef = useRef<boolean>(false);
  // Prevent duplicate fetchThreadById calls from URL effect when apiThreads keeps changing
  const urlThreadIdFetchInProgressRef = useRef<Set<string>>(new Set());
  // AbortController for fetchThreads so only the latest category fetch wins (avoids double load + supports rapid category switch)
  const fetchThreadsAbortControllerRef = useRef<AbortController | null>(null);
  // When effect runs twice (e.g. Strict Mode) with same category, skip second fetch so thread list loads once
  const lastCategoryFetchKeyRef = useRef<string | null>(null);

  // Add ref for message container auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Debounced content update to improve typing performance
  const contentUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  // Helper function to fetch message details by ID
  const fetchMessageById = async (threadId: string, messageId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      if (!threadId) {
        throw new Error("No thread selected");
      }
      const response = await fetch(
        `${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_MESSAGES(
          threadId,
          messageId
        )}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const responseData = await response.json();
      // Extract the message from data.messages[0] as per the API response structure; normalize citations/usage_metrics
      const raw = responseData.data?.messages?.[0] || null;
      return raw ? normalizeMessageFromApi(raw) : null;
    } catch (error) {
      return null;
    }
  };

  // Socket connection on mount (regardless of channelId)
  useEffect(() => {
    // Connect to socket server
    const socket = socketService.connect(API_CONFIG.SOCKET_URL);

    // Wait for connection to be established and set up event listeners
    const waitForConnection = async () => {
      const isConnected = await socketService.waitForConnection(5000);

      if (isConnected && socket) {
        // Remove any existing listeners first to prevent duplicates
        socketService.off("shay_realtime");
        // Listen for shay_realtime events (common socket communication)
        socketService.on("shay_realtime", async (eventData) => {
          try {
            // Full socket payload for every shay_realtime message (filter: [ThreadDetail][socket])
            console.log("[ThreadDetail][socket] shay_realtime raw payload", {
              event: eventData,
              activeChannelIdThisPage: channelIdRef.current,
            });
            // Handle different event types
            switch (eventData.type) {
              case SOCKET_KEYS.CHANNEL_USER_UPDATE:
              case SOCKET_KEYS.CHANNEL_METRICS_UPDATE:
                // Refresh channel members for @mentions when user count changes
                if (channelIdRef.current && eventData.data.channelId === channelIdRef.current) {
                  await initializeChannelMembers(channelIdRef.current);
                }
                break;
              case SOCKET_KEYS.THREAD_CREATED:
                if (channelIdRef.current && eventData.data.channelId === channelIdRef.current) {
                  const threadId = eventData.data.threadId;
                  // Only fetch this one thread; skip if already fetching it (dedupe)
                  if (socketThreadFetchInFlightRef.current.has(threadId)) break;
                  const currentThreads = apiThreadsRef.current;
                  const threadExists = currentThreads.find(
                    (thread) => thread.id === threadId
                  );
                  if (!threadExists && fetchThreadByIdRef.current) {
                    socketThreadFetchInFlightRef.current.add(threadId);
                    try {
                      const newThread = await fetchThreadByIdRef.current(threadId);
                      if (newThread) {
                        setApiThreads((prevThreads) => [
                          newThread,
                          ...prevThreads,
                        ]);
                      }
                    } finally {
                      socketThreadFetchInFlightRef.current.delete(threadId);
                    }
                  }
                }
                break;
              case SOCKET_KEYS.THREAD_UPDATED:
                if (channelIdRef.current && eventData.data.channelId === channelIdRef.current) {
                  const threadId = eventData.data.threadId;
                  // Only fetch this one thread; skip if already fetching it (dedupe)
                  if (socketThreadFetchInFlightRef.current.has(threadId)) break;
                  const currentThreads = apiThreadsRef.current;
                  const threadExists = currentThreads.find(
                    (thread) => thread.id === threadId
                  );
                  if (threadExists && fetchThreadByIdRef.current) {
                    socketThreadFetchInFlightRef.current.add(threadId);
                    try {
                      const updatedThread = await fetchThreadByIdRef.current(threadId);
                      if (updatedThread) {
                        setApiThreads((prevThreads) =>
                          prevThreads.map((thread) =>
                            thread.id === threadId ? updatedThread : thread
                          )
                        );
                        // If this thread is open and we didn't just send from this tab, silently refresh messages (avoid duplicate call after handleComposeSend)
                        if (
                          selectedThreadIdRef.current === threadId &&
                          weJustSentForThreadIdRef.current !== threadId &&
                          fetchMessagesRef.current
                        ) {
                          fetchMessagesRef.current(threadId, false, updatedThread.message_count ?? 0, true);
                        }
                      }
                    } finally {
                      socketThreadFetchInFlightRef.current.delete(threadId);
                    }
                  }
                }
                break;
              case SOCKET_KEYS.THREAD_DELETED:
                if (channelIdRef.current && eventData.data.channelId === channelIdRef.current) {
                  const threadId = eventData.data.threadId;
                  setApiThreads((prevThreads) =>
                    prevThreads.filter((thread) => thread.id !== threadId)
                  );
                  // If the deleted thread was selected, clear selection
                  if (selectedThread?.id === threadId) {
                    clearTemporaryMessages();
                    setSelectedThread(null);
                    setMessages([]);
                    // Update URL to remove threadId (keep classification query so folder stays correct after delete)
                    if (channelIdRef.current) {
                      const locSearch =
                        typeof window !== "undefined" ? window.location.search : "";
                      const isEmailCh =
                        (channelDataRef.current?.channel_type || "email") === "email";
                      navigate(
                        buildThreadsNavigate(
                          channelIdRef.current,
                          undefined,
                          new URLSearchParams(locSearch),
                          selectedEmailCategoryRef.current,
                          isEmailCh
                        ),
                        { replace: true }
                      );
                    }
                  }
                }
                break;
              case SOCKET_KEYS.THREAD_MESSAGE_CREATED:
                if (
                  channelIdRef.current &&
                  eventData.data.channelId === channelIdRef.current
                ) {
                  const eventThreadId = eventData.data.threadId;
                  const messageId = eventData.data.messageId;
                  // Selected thread: update messages list only (fetch single message)
                  if (selectedThreadIdRef.current === eventThreadId) {
                    if (!messageId || messageId === "undefined") break;
                    const currentMessages = messagesRef.current;
                    const messageExists = currentMessages.find(
                      (message) => message.id === messageId
                    );
                    if (!messageExists && selectedThreadIdRef.current) {
                      const newMessage = await fetchMessageById(selectedThreadIdRef.current, messageId);
                      if (newMessage) {
                        setMessages((prevMessages) => {
                          const exists = prevMessages.some(m => m.id === newMessage.id || m.sequence_number === newMessage.sequence_number);
                          return exists ? prevMessages : [...prevMessages, newMessage];
                        });
                      }
                      setTimeout(() => scrollToBottom(), 100);
                    }
                  } else {
                    // Other thread: update only that thread in the list (fetch single thread, not all threads)
                    const currentThreads = apiThreadsRef.current;
                    const threadInList = currentThreads.some((t) => t.id === eventThreadId);
                    if (threadInList && fetchThreadByIdRef.current && !socketThreadFetchInFlightRef.current.has(eventThreadId)) {
                      socketThreadFetchInFlightRef.current.add(eventThreadId);
                      try {
                        const updatedThread = await fetchThreadByIdRef.current(eventThreadId);
                        if (updatedThread) {
                          setApiThreads((prevThreads) =>
                            prevThreads.map((t) => (t.id === eventThreadId ? updatedThread : t))
                          );
                        }
                      } finally {
                        socketThreadFetchInFlightRef.current.delete(eventThreadId);
                      }
                    }
                  }
                }
                break;
              case SOCKET_KEYS.THREAD_MESSAGE_UPDATED:
                if (channelIdRef.current && eventData.data.channelId === channelIdRef.current) {
                  const messageData = eventData.data;
                  const messageId = messageData.messageId;
                  const threadId = messageData.threadId;

                  // Only update message if it belongs to the currently selected thread
                  if (selectedThread?.id === threadId) {
                    setMessages((prevMessages) =>
                      prevMessages.map((message) =>
                        message.id === messageId
                          ? {
                              ...message,
                              content: messageData.content || message.content,
                              updated_at:
                                messageData.updatedAt || message.updated_at,
                              emailResponse:
                                messageData.emailResponse ||
                                message.emailResponse,
                              citations: messageData.citations || message.citations,
                              usage_metrics: messageData.usage_metrics ?? message.usage_metrics,
                            }
                          : message
                      )
                    );
                  }
                }
                break;
              case SOCKET_KEYS.THREAD_MESSAGE_DELETED:
                if (channelIdRef.current && eventData.data.channelId === channelIdRef.current) {
                  const messageId = eventData.data.messageId;
                  const threadId = eventData.data.threadId;

                  // Only remove message if it belongs to the currently selected thread
                  if (selectedThread?.id === threadId) {
                    setTimeout(() => {
                      setMessages((prevMessages) => {
                        const removeIds = getMessageIdsToRemoveForDeletedPrompt(
                          prevMessages,
                          messageId
                        );
                        return prevMessages.filter((message) => !removeIds.has(message.id));
                      });
                    }, 500);
                  }
                }
                break;
                case SOCKET_KEYS.EMBEDDING_STATUS_UPDATED:
                  // Embedding status updates are now handled by DataSourcesPanel
                  break;
            }
          } catch (error) {
            console.error("âŒ Error in shay_realtime event handler:", error);
          }
        });
      }
    };

    // Call the async function
    waitForConnection();

    // Cleanup on component unmount
    return () => {
      socketService.off("shay_realtime");
    };
  }, []); // Empty dependency array - runs only on mount

  // Function to scroll to bottom of messages
  // scrollContainerOnly: when true (default), only set container.scrollTop; false uses scrollIntoView which can move window to top
  const scrollToBottom = (forceInstant = false, scrollContainerOnly = true) => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
          hasScrolledToBottomRef.current = true;
        }
        // scrollIntoView scrolls window and can move page to top; only use when explicitly requested
        if (!scrollContainerOnly && messagesEndRef.current) {
          try {
            messagesEndRef.current.scrollIntoView({
              behavior: forceInstant ? "auto" : "smooth",
              block: "end",
              inline: "nearest",
            });
            hasScrolledToBottomRef.current = true;
          } catch (error) {
            messagesEndRef.current.scrollIntoView();
            hasScrolledToBottomRef.current = true;
          }
        }
      });
    });
  };

  // Debounced content update function
  const debouncedContentUpdate = (html: string) => {
    // Clear any existing timeout
    if (contentUpdateTimeoutRef.current) {
      clearTimeout(contentUpdateTimeoutRef.current);
    }

    // Set a new timeout for content update
    contentUpdateTimeoutRef.current = setTimeout(() => {
      // Update both newComment and editedMessageContent based on editing state
      if (isEditing) {
        setEditedMessageContent(html);
      }
      setNewComment(html);
    }, 100); // 100ms delay for better performance
  };

  useEffect(() => {
    if (messages.length > 0) {
      // Check if any message has emailResponse with isDraftGenerated set to true
      const draftGenerated = messages.some(
        (message) => message?.emailResponse?.isDraftGenerated === true
      );

      if (draftGenerated) {
        setIsGeneratingDraftCompleted(true);
      }
    }
  }, [messages]);

  // Function to fetch all channel data with deduplication
  const fetchAllChannelData = useCallback(async (channelId: string, signal: AbortSignal) => {
    const requestKey = `channel-${channelId}`;
    
    // Check if request is already in progress
    if (activeRequestsRef.current.has(requestKey)) {
      return;
    }
    
    // Add to active requests
    activeRequestsRef.current.add(requestKey);
    
    try {
      // Fetch all channel-related data in parallel
      await Promise.all([
        fetchThreads(),
        fetchChannelData()
      ]);
    } catch (error) {
      if (signal.aborted) {
        return;
      }
      console.error('Error fetching channel data:', error);
    } finally {
      // Remove from active requests
      activeRequestsRef.current.delete(requestKey);
    }
  }, [selectedEmailCategory, showArchivedThreads]);

  // Keep ref updated so channel effect always calls latest fetch (without re-running on category change)
  fetchAllChannelDataRef.current = fetchAllChannelData;

  // When channelId changes only: fetch channel data only (threads are loaded by category effect to avoid double load)
  useEffect(() => {
    if (!channelId) return;
    lastCategoryFetchKeyRef.current = null;
    setAccessDenied(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    if (fetchThreadsTimeoutRef.current) {
      clearTimeout(fetchThreadsTimeoutRef.current);
    }
    const timeoutId = setTimeout(() => {
      if (!abortController.signal.aborted) {
        fetchChannelDataRef.current?.();
      }
    }, 50);
    fetchThreadsTimeoutRef.current = timeoutId;
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [channelId]);

  // When only category or archived changes (or channelData first loads): fetch threads only (single source for fetchThreads)
  useEffect(() => {
    if (!channelId || !channelData) return;
    const fetchKey = `${channelId}-${selectedEmailCategory}-${showArchivedThreads}`;
    if (lastCategoryFetchKeyRef.current === fetchKey) return;
    lastCategoryFetchKeyRef.current = fetchKey;
    fetchThreads();
  }, [selectedEmailCategory, showArchivedThreads, channelData]);

  // Thread list is fetched only by the category effect (single source); no refetch here to avoid double load

  // Load thread from URL threadId when it changes (and fix deep link when thread is not in first page)
  useEffect(() => {
    if (!channelId) return;

    // If threadId exists in URL, find and select the thread
    if (threadId) {
      // Find the thread in the loaded threads
      const threadToSelect = apiThreads.find((thread) => thread.id === threadId);
      if (threadToSelect) {
        // Only update if it's different from currently selected thread to avoid unnecessary re-renders
        if (selectedThread?.id !== threadToSelect.id) {
          // Clear temporary messages when switching to a different thread
          if (selectedThread && selectedThread.id !== threadToSelect.id) {
            clearTemporaryMessages();
          }
          setSelectedThread(threadToSelect);
        }
      } else {
        // Do not fetch-by-id when we just switched category: URL may still have previous category threadId and list is for new category (e.g. empty spam)
        if (categorySwitchInProgressRef.current) return;
        // Active search/modal filters: URL thread may be absent from the filtered list — do not prepend via fetch-by-id
        if (hasActiveEmailListFilters(emailFilters)) {
          clearTemporaryMessages();
          if (apiThreads.length > 0) {
            setSelectedThread(apiThreads[0]);
          } else {
            setSelectedThread(null);
          }
          if (channelId) {
            const isEmailCh = (channelData?.channel_type || "email") === "email";
            navigate(
              buildThreadsNavigate(channelId, undefined, searchParams, selectedEmailCategory, isEmailCh),
              { replace: true }
            );
          }
          return;
        }
        // Avoid duplicate or repeated fetch-by-id when apiThreads keeps changing (e.g. during sync)
        if (urlThreadIdFetchInProgressRef.current.has(threadId)) return;
        // URL thread not in current list (e.g. deep link from email, thread on page 2+): fetch by id and prepend so user lands on correct thread
        const fetchFn = fetchThreadByIdRef.current;
        if (fetchFn) {
          console.log("[ThreadDetail][url-thread-effect]", {
            threadId,
            reason: "threadId not in list; fetch by id to prepend",
          });
          urlThreadIdFetchInProgressRef.current.add(threadId);
          fetchFn(threadId)
            .then((fetched) => {
              if (fetched) {
                setApiThreads((prev) => (prev.some((t) => t.id === fetched.id) ? prev : [fetched, ...prev]));
                setSelectedThread(fetched);
              }
            })
            .finally(() => {
              urlThreadIdFetchInProgressRef.current.delete(threadId);
            });
        }
      }
    } else {
      // URL no longer has threadId (e.g. after category switch); clear category-switch flag
      categorySwitchInProgressRef.current = false;
      // Only clear selection when list is empty so we don't overwrite selection set by fetchThreads
      if (selectedThread && !isComposeBoxOpen() && apiThreads.length === 0) {
        setSelectedThread(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, apiThreads, channelId, emailFilters]);

  // Message channels: search is client-side only — keep detail pane on a visible row (first match) or clear when nothing matches
  useEffect(() => {
    if (isComposeBoxOpen()) return;
    const channelType = channelData?.channel_type || "email";
    if (channelType !== "message") return;

    const q = emailFilters.body.trim();
    if (q === "") return;

    if (threadsLoading) return;

    const baseList = showArchivedThreads
      ? sortThreadsByLastActivity(apiThreads)
      : getFilteredThreads();

    const seen = new Set<string>();
    const uniqueIncoming = baseList.filter((t) => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });

    const matching = uniqueIncoming.filter((t) =>
      messageThreadMatchesClientSearch(t, emailFilters.body)
    );

    if (matching.length === 0) {
      if (selectedThread) {
        safeSetSelectedThread(null);
      }
      return;
    }

    if (
      !selectedThread ||
      !messageThreadMatchesClientSearch(selectedThread, emailFilters.body)
    ) {
      safeSetSelectedThread(matching[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getFilteredThreads/sortThreadsByLastActivity read latest category/archived state via closure
  }, [
    emailFilters.body,
    apiThreads,
    showArchivedThreads,
    selectedEmailCategory,
    channelData?.channel_type,
    threadsLoading,
    selectedThread,
    channelId,
  ]);

  // Fetch embedding status for the channel (optional signal to avoid race when channel changes)
  const fetchEmbeddingStatus = useCallback(async (signal?: AbortSignal) => {
    if (!channelId) {
      return;
    }

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.EMBEDDING_STATUS}${channelId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: EmbeddingStatusResponse = await response.json();
      setEmbeddingStatus(data);
    } catch (err) {
      // Ignore abort - request was cancelled because channel changed
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      // Silently fail - embedding status is not critical for page functionality
      console.error("Failed to fetch embedding status:", err);
      setEmbeddingStatus(null);
    }
  }, [channelId]);

  // Fetch embedding status when channelId changes; abort previous request to avoid race
  useEffect(() => {
    const controller = new AbortController();
    fetchEmbeddingStatus(controller.signal);
    return () => controller.abort();
  }, [fetchEmbeddingStatus]);

  // Reload only channel data and current thread messages when email sync completes (no thread list refetch to avoid double load)
  const reloadAllAPIs = async () => {
    try {
      console.log("[ThreadDetail][reloadAllAPIs]", {
        channelId,
        selectedThreadId: selectedThread?.id ?? null,
      });
      if (channelId && fetchChannelDataRef.current) {
        await fetchChannelDataRef.current();
      }
      if (selectedThread?.id) {
        await fetchMessages(selectedThread.id);
      }
    } catch (error) {
      console.error('Error reloading APIs after sync completion:', error);
    }
  };


  // Fetch threads from API - Updated to use apiThreads
  // When overrideClassification is set (e.g. after drag-drop to category), use it so refetch uses target category
  const fetchThreads = async (overrideFilters?: EmailFilters, overrideClassification?: string) => {
    if (!channelId) {
      setError("Channel ID is required");
      setLoading(false);
      return;
    }
    // Abort any in-flight fetch so only the latest category/request wins (avoids double load on category switch)
    if (fetchThreadsAbortControllerRef.current) {
      fetchThreadsAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    fetchThreadsAbortControllerRef.current = abortController;
    const signal = abortController.signal;

    try {
      setThreadsLoading(true);
      setError(null);

      // Use override filters if provided, otherwise use state filters
      const filtersToUse = overrideFilters || emailFilters;
      // Use override category when provided (e.g. after move) so we don't use stale selectedEmailCategory
      const effectiveCategory = overrideClassification ?? selectedEmailCategory;

      // Use email as default when channel_type missing so legacy email channels get classification_type
      const currentChannelType = channelData?.channel_type || 'email';
      const isEmailChannel = currentChannelType === 'email';

      const params = new URLSearchParams({
        limit: "10",
        offset: "0",
        // Use filter's is_archived if set, otherwise fallback to showArchivedThreads or category
        is_archived: filtersToUse.is_archived !== null 
          ? filtersToUse.is_archived.toString()
          : effectiveCategory === "archived" 
            ? 'true' 
            : showArchivedThreads.toString(),
        ...(effectiveCategory !== "archived" && isEmailChannel && {
          classification_type: effectiveCategory,
        }),
      });

      // Add filter parameters for email channels
      if (isEmailChannel) {
        // From email filter (multiple emails)
        filtersToUse.from_email.forEach((email) => {
          params.append('from_email', email);
        });
        
        // To email filter (multiple emails)
        filtersToUse.to_email.forEach((email) => {
          params.append('to_email', email);
        });
        
        // Subject filter
        if (filtersToUse.subject.trim() !== '') {
          params.append('subject', filtersToUse.subject.trim());
        }
        
        // Body/content filter
        if (filtersToUse.body.trim() !== '') {
          params.append('body', filtersToUse.body.trim());
        }
        
        // Has attachment filter
        if (filtersToUse.has_attachment !== null) {
          params.append('has_attachment', filtersToUse.has_attachment.toString());
        }
        
        // Date range filters
        if (filtersToUse.date_from) {
          params.append('date_from', filtersToUse.date_from);
        }
        if (filtersToUse.date_to) {
          params.append('date_to', filtersToUse.date_to);
        }
      }

      console.log("[ThreadDetail][fetchThreads LIST_THREADS]", {
        channelId,
        effectiveCategory,
        query: params.toString(),
      });

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.LIST_THREADS(
          channelId
        )}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal,
        }
      );

      if (signal.aborted) return;
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ThreadsResponse = await response.json();
      if (signal.aborted) return;

      // Threads already have id field from API
      const threadsWithId = data.data.threads;

      // When list is empty, show empty state only — avoid calling fetchThreadById or any other API
      if (threadsWithId.length === 0) {
        // skipUrlThreadIdFetchForEmptyCategoryRef.current = true;
        setApiThreads(threadsWithId);
        setCurrentPage(1);
        setHasMore(false);
        clearTemporaryMessages();
        setSelectedThread(null);
        pendingThreadSelectionRef.current = null;
        if (channelId) {
          const isEmailCh = currentChannelType === "email";
          navigate(
            buildThreadsNavigate(channelId, undefined, searchParams, effectiveCategory, isEmailCh),
            { replace: true }
          );
        }
        if (loading) setLoading(false);
        setThreadsLoading(false);
        return;
      }

      // Read threadId from current URL only (pathname), so after category switch we navigate to /threads/channelId
      // and urlThreadId is undefined here, ensuring we select the first thread in the new list
      const pathParts = typeof window !== "undefined" ? window.location.pathname.split("/") : [];
      const urlThreadId =
        pathParts.length >= 4 && pathParts[1] === "threads" ? pathParts[3] : undefined;

      // If URL has threadId but it's not in first page, fetch that thread and prepend so user lands on correct thread
      // Skip prepend when filters narrow the list — otherwise the URL thread may not match search/modal filters
      if (
        urlThreadId &&
        !threadsWithId.some((t) => t.id === urlThreadId) &&
        !hasActiveEmailListFilters(filtersToUse)
      ) {
        const fetchFn = fetchThreadByIdRef.current;
        console.log("[ThreadDetail][fetchThreads prepend]", {
          urlThreadId,
          reason: "URL thread not in first page of list response",
        });
        const fetchedThread = fetchFn ? await fetchFn(urlThreadId) : null;
        if (signal.aborted) return;
        if (fetchedThread) {
          // Prepend so the notification thread is visible and selected without changing pagination of the rest
          const threadsWithUrlThread = [fetchedThread, ...threadsWithId];
          setApiThreads(threadsWithUrlThread);
          setCurrentPage(1);
          setHasMore(data.data.threads.length === 10);
          safeSetSelectedThread(fetchedThread);
          if (loading) setLoading(false);
          setThreadsLoading(false);
          return;
        }
      }

      setApiThreads(threadsWithId);

      // Reset pagination state for first page
      setCurrentPage(1);
      setHasMore(data.data.threads.length === 10);

      // Do not select thread or navigate if user already clicked back (avoids re-entering channel after sync/async)
      if (userNavigatedBackRef.current) {
        if (loading) setLoading(false);
        return;
      }

      // Set thread selection based on pending selection or first thread
      if (threadsWithId.length > 0) {
        // Check if there's a pending thread selection
        if (pendingThreadSelectionRef.current) {
          const pendingThread = threadsWithId.find(thread => thread.id === pendingThreadSelectionRef.current);
          if (pendingThread) {
            safeSetSelectedThread(pendingThread);
            // Clear the pending selection after using it
            pendingThreadSelectionRef.current = null;
          } else {
            // Pending thread not found, select first thread
            safeSetSelectedThread(threadsWithId[0]);
            // Clear the pending selection since it's not available
            pendingThreadSelectionRef.current = null;
          }
        } else if (urlThreadId) {
          // URL threadId present: select it if in list; if not in first page, leave selection for effect to fetch-by-id and set
          const threadToSelect = threadsWithId.find((t) => t.id === urlThreadId);
          if (threadToSelect) {
            safeSetSelectedThread(threadToSelect);
          } else if (hasActiveEmailListFilters(filtersToUse)) {
            // URL thread is not in filtered results: align detail pane and route with the list
            safeSetSelectedThread(threadsWithId[0]);
            if (channelId) {
              const isEmailCh = currentChannelType === "email";
              navigate(
                buildThreadsNavigate(channelId, undefined, searchParams, effectiveCategory, isEmailCh),
                { replace: true }
              );
            }
          }
          // Else: url thread not in list (e.g. deep link from email); do not select first thread – effect will fetch by id and set
        } else {
          // No pending selection, select first thread
          safeSetSelectedThread(threadsWithId[0]);
        }
      } else {
        // If no threads available, clear selection
        clearTemporaryMessages();
        setSelectedThread(null);
        pendingThreadSelectionRef.current = null;
        // Update URL to remove threadId
        if (channelId) {
          const isEmailCh = currentChannelType === "email";
          navigate(
            buildThreadsNavigate(channelId, undefined, searchParams, effectiveCategory, isEmailCh),
            { replace: true }
          );
        }
      }

      // Set initial loading to false after first successful load
      if (loading) {
        setLoading(false);
      }
    } catch (err) {
      // Ignore abort – a newer fetchThreads (e.g. different category) is in progress
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      let errorMessage = `Failed to fetch ${LanguageConstants.thread_s.toLowerCase()}`;
      // Skip toaster when showing full-page Access Denied to avoid duplicate messages
      let skipToasterFor403 = false;

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to access this channel.";
              setAccessDenied(true);
              skipToasterFor403 = true;
              break;
            case "404":
              errorMessage = "Channel not found.";
              setChannelNotFound(true);
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load ${LanguageConstants.thread_s.toLowerCase()} (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      if (!skipToasterFor403) {
        showError(errorMessage);
      }
      // Clear loading so we don't show loading screen on error (e.g. 403, 404)
      setLoading(false);
    } finally {
      if (!signal.aborted) {
        setThreadsLoading(false);
        // Keep lastCategoryFetchKeyRef set so effect re-runs don't trigger duplicate fetch
      }
      if (fetchThreadsAbortControllerRef.current === abortController) {
        fetchThreadsAbortControllerRef.current = null;
      }
    }
  };

  // Load more threads for pagination
  const loadMoreThreads = async () => {
    if (!channelId || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);

      // Use email as default when channel_type missing so legacy email channels get classification_type
      const currentChannelType = channelData?.channel_type || 'email';
      const isEmailChannel = currentChannelType === 'email';

      const nextPage = currentPage + 1;
      const offset = (nextPage - 1) * 10; // Calculate offset based on page and page size
      const params = new URLSearchParams({
        limit: "10",
        offset: offset.toString(),
        // Use filter's is_archived if set, otherwise fallback to showArchivedThreads or category
        is_archived: emailFilters.is_archived !== null 
          ? emailFilters.is_archived.toString()
          : selectedEmailCategory == "archived" 
            ? 'true' 
            : showArchivedThreads.toString(),
        ...(selectedEmailCategory !== "archived" && isEmailChannel && {
          classification_type: selectedEmailCategory,
        }),
      });

      // Add filter parameters for email channels
      if (isEmailChannel) {
        // From email filter (multiple emails)
        emailFilters.from_email.forEach((email) => {
          params.append('from_email', email);
        });
        
        // To email filter (multiple emails)
        emailFilters.to_email.forEach((email) => {
          params.append('to_email', email);
        });
        
        // Subject filter
        if (emailFilters.subject.trim() !== '') {
          params.append('subject', emailFilters.subject.trim());
        }
        
        // Body/content filter
        if (emailFilters.body.trim() !== '') {
          params.append('body', emailFilters.body.trim());
        }
        
        // Has attachment filter
        if (emailFilters.has_attachment !== null) {
          params.append('has_attachment', emailFilters.has_attachment.toString());
        }
        
        // Date range filters
        if (emailFilters.date_from) {
          params.append('date_from', emailFilters.date_from);
        }
        if (emailFilters.date_to) {
          params.append('date_to', emailFilters.date_to);
        }
      }

      console.log("[ThreadDetail][loadMoreThreads LIST_THREADS]", {
        channelId,
        nextPage,
        query: params.toString(),
      });

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.LIST_THREADS(
          channelId
        )}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ThreadsResponse = await response.json();

      // Threads already have id field from API
      const threadsWithId = data.data.threads;

      // Append new threads to existing ones
      setApiThreads((prevThreads) => [...prevThreads, ...threadsWithId]);
      setCurrentPage(nextPage);

      // Check if we have more data to load
      setHasMore(data.data.threads.length === 10);
    } catch (err) {
      let errorMessage = `Failed to load more ${LanguageConstants.thread_s.toLowerCase()}`;

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to access this channel.";
              break;
            case "404":
              errorMessage = "Channel not found.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load more ${LanguageConstants.thread_s.toLowerCase()} (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoadingMore(false);
    }
  };

  // Archive confirmation dialog handlers
  const handleArchiveConfirmOpen = (thread: ApiThread) => {
    setThreadToArchive(thread);
    setIsArchiveConfirmOpen(true);
    handleThreadMenuClose();
  };

  const handleArchiveConfirmClose = () => {
    setIsArchiveConfirmOpen(false);
    setThreadToArchive(null);
    setIsArchivingThread(false);
  };

  const handleArchiveConfirm = async () => {
    if (!threadToArchive) return;

    try {
      setIsArchivingThread(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.UPDATE_THREAD(
          threadToArchive.id
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            is_archived: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      showSuccess("Thread archived successfully!");

      // Close the confirmation dialog
      handleArchiveConfirmClose();

      // Remove archived thread from local list only (no full refetch to avoid loading)
      const remaining = apiThreads.filter((t) => t.id !== threadToArchive.id);
      setApiThreads((prev) => prev.filter((t) => t.id !== threadToArchive.id));

      // If the archived thread was active, set latest in remaining list as active; otherwise keep same selection
      if (selectedThread?.id === threadToArchive.id) {
        clearTemporaryMessages();
        // Compute latest thread in remaining list using same filter/sort as current view
        let filteredRemaining: ApiThread[];
        switch (selectedEmailCategory) {
          case "inbox":
            filteredRemaining = remaining.filter((t) => !t.is_archived);
            break;
          case "archived":
            filteredRemaining = remaining.filter((t) => t.is_archived);
            break;
          default:
            filteredRemaining = remaining;
        }
        const latestInRemaining = sortThreadsByLastActivity(filteredRemaining)[0] ?? null;
        safeSetSelectedThread(latestInRemaining);
      }
    } catch (error) {
      showError("Failed to archive thread. Please try again.");
    } finally {
      setIsArchivingThread(false);
    }
  };

  const handleUnarchiveThread = async (thread: ApiThread) => {
    try {
      setIsUnarchivingThread(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.UPDATE_THREAD(
          thread.id
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            is_archived: false,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      showSuccess("Thread unarchived successfully!");

      // Clear active thread and URL so refetch will set latest from updated list (or show empty state)
      clearTemporaryMessages();
      setSelectedThread(null);
      pendingThreadSelectionRef.current = null;
      if (channelId) {
        const isEmailCh = (channelData?.channel_type || "email") === "email";
        navigate(
          buildThreadsNavigate(channelId, undefined, searchParams, selectedEmailCategory, isEmailCh),
          { replace: true }
        );
      }
      // Refresh the thread list; fetchThreads will set first (latest) thread as active or leave empty and show message
      fetchThreads();
    } catch (error) {
      showError("Failed to unarchive thread. Please try again.");
    } finally {
      setIsUnarchivingThread(false);
    }
  };

  // Fetch messages for a specific thread
  // When wasUnread is true and load succeeds, we mark thread as read in UI and decrement category unread count
  // messageCountOverride: use when refetching after send/update so we use the updated thread message count
  // silent: when true, fetch in background without showing loading UI (e.g. after edit & send)
  const fetchMessages = async (threadId: string, wasUnread?: boolean, messageCountOverride?: number, silent?: boolean) => {
    if (!threadId) return;
    const requestId = ++messagesFetchRequestIdRef.current;
    const isLatestRequest = () => requestId === messagesFetchRequestIdRef.current;
    const isCurrentThreadRequest = () => selectedThreadIdRef.current === threadId;

    try {
      if (!silent) {
        setMessagesLoading(true);
        setMessagesError(null);
      }

      // Use override when refetching after send/update; otherwise resolve by requested thread id
      const threadMessageCount = apiThreadsRef.current.find((t) => t.id === threadId)?.message_count ?? 0;
      const messageCount = messageCountOverride ?? threadMessageCount;
      const limit = 10;
      // Calculate offset to get the last 10 messages (highest sequence numbers)
      // For 11 messages: offset = 1 to get sequences 2-11 (last 10)
      // For 20 messages: offset = 10 to get sequences 11-20 (last 10)
      const offset = Math.max(0, messageCount - limit);

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_MESSAGES(
          threadId
        )}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: MessagesResponse = await response.json();
      // API returns latest messages first (index 0 = latest), but we want to display
      // oldest first (at top) and latest last (at bottom), so reverse the array.
      // Normalize so citations and usage_metrics from API are preserved for UI.
      const reversedMessages = data.data.messages.map(normalizeMessageFromApi).reverse();
      if (!isLatestRequest() || !isCurrentThreadRequest()) return;
      setMessages(reversedMessages);

      // Store external_app_id on thread for move-to-classification (drag-and-drop / API)
      // Prefer thread.email_thread_id from API, else first message: emailResponse.externalAppId ?? top-level externalAppId
      const fromThread = data.data.thread?.email_thread_id;
      const firstWithExtId = reversedMessages.find(
        (m) => m.emailResponse?.externalAppId ?? m.externalAppId
      );
      const externalAppId =
        fromThread ?? firstWithExtId?.emailResponse?.externalAppId ?? firstWithExtId?.externalAppId;
      if (externalAppId) {
        setApiThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, external_app_id: externalAppId } : t))
        );
      }

      // Reset messages pagination state
      setMessagesCurrentPage(1);
      messagesCurrentOffsetRef.current = offset;
      setMessagesCurrentOffset(offset);
      // Has more if there are older messages (if offset > 0, there are messages before the current set)
      setMessagesHasMore(offset > 0);

      if (reversedMessages.length > 0) {
        // Find the message with the highest sequence_number (latest message)
        const latestMessage = reversedMessages.reduce((latest, current) =>
          (current.sequence_number || 0) > (latest.sequence_number || 0) ? current : latest
        );
        setSelectedMessageId(latestMessage.id);
      }

      // Mark thread as read in UI and decrement category/label unread count when user opens an unread thread
      // (backend already marks as read; this keeps list and sidebar in sync without refresh)
      if (wasUnread) {
        setApiThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, is_read: true } : t))
        );
        // Flattened metrics use full keys (label_xxx, sublabel_xxx)
        setEmailMetricsForHeader((prev) =>
          prev.map((m) =>
            m.key === selectedEmailCategory
              ? { ...m, unread_count: Math.max(0, (m.unread_count ?? 0) - 1) }
              : m
          )
        );
      }
    } catch (err) {
      if (!isLatestRequest() || !isCurrentThreadRequest()) return;
      let errorMessage = "Failed to fetch messages";

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to access these messages.";
              break;
            case "404":
              errorMessage = `${LanguageConstants.thread} not found.`;
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load messages (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      if (!silent) {
        setMessagesError(errorMessage);
        showError(errorMessage);
      }
    } finally {
      if (!silent && isLatestRequest() && isCurrentThreadRequest()) setMessagesLoading(false);
    }
  };

  // Load more messages for pagination (same API used by main content and Ask Prism7 panel)
  const loadMoreMessages = async (options?: { fromPanel?: boolean }) => {
    if (!selectedThread?.id || messagesLoadingMore || !messagesHasMore) return;
    // Refs avoid stale state when this runs from a debounced scroll timeout scheduled before the first page finished loading
    if (messagesLoadingRef.current) return;
    if (messagesRef.current.length === 0) return;
    const fromPanel = options?.fromPanel === true;

    try {
      setMessagesLoadingMore(true);
      setMessagesError(null);

      // Store main content scroll position only when load is triggered from main content (panel preserves its own scroll)
      const container = fromPanel ? null : messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;
      const previousScrollTop = container?.scrollTop || 0;

      // Calculate new offset to fetch older messages (decrease offset to go backwards)
      // If current offset is 10 (showing sequences 11-20), next should be 0 (to get sequences 1-10)
      const limit = 10;
      const currentOffset = messagesCurrentOffsetRef.current;
      const newOffset = Math.max(0, currentOffset - limit);

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: newOffset.toString(),
      });

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_MESSAGES(
          selectedThread?.id!
        )}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: MessagesResponse = await response.json();

      // API returns latest first, so older messages (higher offset) need to be reversed.
      // Normalize so citations and usage_metrics from API are preserved for UI.
      // For offset 10: API returns [msg10 (older), ..., msg15 (oldest)]
      // After reverse: [msg15 (oldest), ..., msg10 (older)]
      const reversedOlderMessages = data.data.messages.map(normalizeMessageFromApi).reverse();
      // Prepend older messages at the top (they should appear before current messages)
      // Deduplicate by message ID to prevent duplicates
      setMessages((prevMessages: ApiMessage[]) => {
        const existingIds = new Set(prevMessages.map((msg: ApiMessage) => msg.id));
        const newMessages: ApiMessage[] = reversedOlderMessages.filter((msg: ApiMessage) => !existingIds.has(msg.id));
        return [...newMessages, ...prevMessages];
      });
      setMessagesCurrentPage((page) => page + 1);
      messagesCurrentOffsetRef.current = newOffset;
      setMessagesCurrentOffset(newOffset);

      // Check if we have more data to load: only false when we've actually loaded offset=0 (oldest page).
      // Do not require data.data.messages.length === limit — a partial page at non-zero offset (e.g. offset=4
      // returning 4 messages) must still allow one more load for offset=0, or we never fetch the oldest messages.
      setMessagesHasMore(newOffset > 0);

      // Mark that we just loaded more messages so auto-scroll effect does not run (point 3)
      justLoadedMoreRef.current = true;
      // Store scroll position so useEffect can restore it after DOM update (point 2: run after commit)
      scrollPreserveForLoadMoreRef.current = { previousScrollHeight, previousScrollTop };
    } catch (err) {
      let errorMessage = "Failed to load more messages";

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to access these messages.";
              break;
            case "404":
              errorMessage = `${LanguageConstants.thread} not found.`;
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load more messages (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      setMessagesError(errorMessage);
      showError(errorMessage);
    } finally {
      setMessagesLoadingMore(false);
    }
  };

  // Handle scroll to detect when user reaches top (for loading more messages)
  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (
      !container ||
      messagesLoading ||
      messagesLoadingMore ||
      !messagesHasMore ||
      messagesIsLoadingRef.current
    )
      return;

    const { scrollTop } = container;
    const threshold = 100; // Load more when 100px from top

    if (scrollTop < threshold) {
      // Clear any existing timeout
      if (messagesScrollTimeoutRef.current) {
        clearTimeout(messagesScrollTimeoutRef.current);
      }

      // Debounce the API call to prevent multiple rapid calls
      messagesScrollTimeoutRef.current = setTimeout(() => {
        if (
          !messagesIsLoadingRef.current &&
          !messagesLoadingRef.current &&
          messagesRef.current.length > 0 &&
          messagesHasMore
        ) {
          messagesIsLoadingRef.current = true;
          loadMoreMessages();
        }
      }, 150); // 150ms debounce
    }
  }, [messagesLoading, messagesLoadingMore, messagesHasMore, loadMoreMessages]);

  // Add scroll event listener for messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleMessagesScroll);
    return () => container.removeEventListener("scroll", handleMessagesScroll);
  }, [handleMessagesScroll]);

  // Reset loading ref when messagesLoadingMore changes
  useEffect(() => {
    if (!messagesLoadingMore) {
      messagesIsLoadingRef.current = false;
    }
  }, [messagesLoadingMore]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (messagesScrollTimeoutRef.current) {
        clearTimeout(messagesScrollTimeoutRef.current);
      }
    };
  }, []);

  // Fetch channel data
  const fetchChannelData = async () => {
    if (!channelId) {
      setChannelError("Channel ID is required");
      setChannelLoading(false);
      return;
    }

    try {
      setChannelLoading(true);
      setChannelError(null);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.GET_BY_ID(
          channelId
        )}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ChannelResponse = await response.json();
      
      // Default channel_type when API omits it: use 'email' so legacy email channels
      // (e.g. from supportSpace) get classification_type sent to threads API
      if (data.channel_type == null) {
        data.channel_type = 'email';
      }
      
      // Extract provider from API response: top-level provider, channel_settings.provider, or messaging_provider
      let messagingProvider =
        data.provider ??
        (data.channel_settings as { provider?: string } | undefined)?.provider ??
        data.messaging_provider ??
        (data as any).messagingProvider;
      let appAccountId = data.app_account_id || (data as any).appAccountId;
      
      // Check if data is in app_accounts array (for messaging channels)
      if (!messagingProvider && data.app_accounts && data.app_accounts.length > 0) {
        const firstAppAccount = data.app_accounts[0];
        messagingProvider = messagingProvider || firstAppAccount.provider || firstAppAccount.messaging_provider;
        appAccountId = appAccountId || firstAppAccount.id;
      }
      
      // Check if data is in connections array
      if (!messagingProvider && data.connections && data.connections.length > 0) {
        const firstConnection = data.connections[0];
        messagingProvider = messagingProvider || firstConnection.provider || firstConnection.messaging_provider;
        appAccountId = appAccountId || firstConnection.app_account_id || firstConnection.id;
      }
      
      // For WhatsApp channels, try to infer from channel name if provider is still missing
      if (!messagingProvider && data.name && /whatsapp|whats.*app/i.test(data.name)) {
        messagingProvider = 'whatsapp';
      }
      // Default provider to 'regular' when API omits it
      if (!messagingProvider) {
        messagingProvider = 'regular';
      }
      
      // Ensure messaging_provider and app_account_id are properly set
      const channelDataWithProvider: ChannelResponse = {
        ...data,
        messaging_provider: messagingProvider as 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams' | undefined,
        app_account_id: appAccountId,
      };
      
      setChannelData(channelDataWithProvider);
    } catch (err) {
      let errorMessage = "Failed to fetch channel data";
      // Skip toaster when showing full-page Access Denied to avoid duplicate messages
      let skipToasterFor403 = false;

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to access this channel.";
              setAccessDenied(true);
              setLoading(false);
              skipToasterFor403 = true;
              break;
            case "404":
              errorMessage = "Channel not found.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load channel data (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      setChannelError(errorMessage);
      if (!skipToasterFor403) {
        showError(errorMessage);
      }
    } finally {
      setChannelLoading(false);
    }
  };

  // Keep ref updated so channelId effect can call only fetchChannelData (no fetchThreads)
  fetchChannelDataRef.current = fetchChannelData;

  // Initialize channel members when channelId changes; store for mention display (exact boundaries)
  useEffect(() => {
    if (channelId) {
      const timeoutId = setTimeout(() => {
        initializeChannelMembers(channelId).then((members) => {
          setChannelMembersForMentions(members, channelId);
          setMentionUserObjects([
            PRISM7_MENTION_USER,
            ...mapMembersToMentionUsers(members),
          ]);
        });
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      setMentionUserObjects([]);
    }
  }, [channelId]);

  // Refs for preventing stale closures in socket event handlers
  const apiThreadsRef = useRef<ApiThread[]>([]);
  const selectedThreadIdRef = useRef<string | null>(null);
  const messagesRef = useRef<ApiMessage[]>([]);
  // Guard against out-of-order message fetch responses overriding current thread UI
  const messagesFetchRequestIdRef = useRef<number>(0);
  // Dedupe: avoid multiple fetchThreadById for same thread when multiple socket events arrive in quick succession
  const socketThreadFetchInFlightRef = useRef<Set<string>>(new Set());

  // Update refs when state changes
  useEffect(() => {
    apiThreadsRef.current = apiThreads;
  }, [apiThreads]);

  useEffect(() => {
    selectedThreadIdRef.current = selectedThread?.id || null;
  }, [selectedThread]);

  useEffect(() => {
    // Compose/send sets this to skip duplicate fetch while id is unchanged; if user leaves and returns, same id must still fetch — clear on every id change
    silentRefreshThreadIdRef.current = null;
    // Clear previous thread messages immediately so stale content never flashes during thread switch/create
    setMessages([]);
    setSelectedMessageId(null);
    setMessagesError(null);
    setMessagesCurrentPage(1);
    setMessagesCurrentOffset(0);
    messagesCurrentOffsetRef.current = 0;
    setMessagesHasMore(true);
  }, [selectedThread?.id]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messagesCurrentOffsetRef.current = messagesCurrentOffset;
  }, [messagesCurrentOffset]);

  useEffect(() => {
    messagesLoadingRef.current = messagesLoading;
  }, [messagesLoading]);


  // Fetch messages when selected thread id changes; refresh details first for correct offset (deps: id only — avoids loops when merging thread object)
  useEffect(() => {
    if (!selectedThread?.id) {
      setMessagesLoading(false);
      return;
    }
    if (silentRefreshThreadIdRef.current === selectedThread.id) {
      silentRefreshThreadIdRef.current = null;
      return;
    }
    isInitialLoadRef.current = true;
    hasScrolledToBottomRef.current = false;
    const threadId = selectedThread.id;
    const wasUnread = selectedThread.is_read === false;
    let cancelled = false;
    // Show loading immediately: messages are cleared in a sibling effect while thread-details await runs before fetchMessages (otherwise empty state flashes)
    setMessagesLoading(true);
    setMessagesError(null);

    (async () => {
      const details = await fetchMessagesThreadDetailsFromApi(threadId);
      if (cancelled || selectedThreadIdRef.current !== threadId) return;

      let messageCountOverride: number | undefined;
      if (details) {
        messageCountOverride = details.message_count;
        setApiThreads((prev) => {
          const existing = prev.find((t) => t.id === threadId);
          const merged = mergeMessagesApiDetailsIntoThread(existing, details);
          return existing ? prev.map((t) => (t.id === threadId ? merged : t)) : [...prev, merged];
        });
        setSelectedThread((prev) => {
          if (!prev || prev.id !== threadId) return prev;
          return mergeMessagesApiDetailsIntoThread(prev, details);
        });
      }

      const fetchFn = fetchMessagesRef.current;
      if (!fetchFn || cancelled || selectedThreadIdRef.current !== threadId) return;
      await fetchFn(threadId, wasUnread, messageCountOverride);
    })();

    return () => {
      cancelled = true;
      setMessagesLoading(false);
    };
  }, [selectedThread?.id]);

  // Disable browser scroll restoration on mount
  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Reset scroll position to top on mount
    window.scrollTo(0, 0);
    
    return () => {
      // Re-enable scroll restoration on unmount (optional)
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Scroll to bottom when messages are first loaded (initial load)
  useEffect(() => {
    if (messages.length > 0 && !messagesLoading && isInitialLoadRef.current) {
      // Use a longer timeout and requestAnimationFrame to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        // Force instant scroll on initial load to override any browser restoration
        scrollToBottom(true);
        isInitialLoadRef.current = false;
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [messagesLoading, messages.length]);

  // Restore scroll position after "load more" prepends messages (point 2: run after React commit so DOM has new height)
  useEffect(() => {
    const preserved = scrollPreserveForLoadMoreRef.current;
    const container = messagesContainerRef.current;
    if (!preserved || !container) return;
    scrollPreserveForLoadMoreRef.current = null;
    requestAnimationFrame(() => {
      const newScrollHeight = container.scrollHeight;
      const scrollDifference = newScrollHeight - preserved.previousScrollHeight;
      container.scrollTop = preserved.previousScrollTop + scrollDifference;
      // Clear justLoadedMoreRef after delay so auto-scroll effect does not run and scroll to bottom (point 3)
      if (loadMoreScrollTimeoutRef.current) clearTimeout(loadMoreScrollTimeoutRef.current);
      loadMoreScrollTimeoutRef.current = setTimeout(() => {
        justLoadedMoreRef.current = false;
        loadMoreScrollTimeoutRef.current = null;
      }, 300);
    });
    return () => {
      if (loadMoreScrollTimeoutRef.current) {
        clearTimeout(loadMoreScrollTimeoutRef.current);
        loadMoreScrollTimeoutRef.current = null;
      }
    };
  }, [messages.length]);

  // Auto-scroll to bottom when messages change (but not when loading more messages)
  useEffect(() => {
    // Don't auto-scroll if we're loading more messages or just loaded more (user is scrolling up to see older messages)
    if (messagesLoadingMore || justLoadedMoreRef.current) {
      return;
    }
    
    // Skip if this is the initial load (handled by the previous effect)
    if (isInitialLoadRef.current) {
      return;
    }
    
    // Use setTimeout to ensure DOM is updated before scrolling
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [messages, messagesLoadingMore]);

  // When compose box closes, messages container remounts with scrollTop=0; scroll to bottom so draft/latest messages are visible
  const prevComposeOpenRef = useRef<boolean>(false);
  useEffect(() => {
    const wasOpen = prevComposeOpenRef.current;
    prevComposeOpenRef.current = isComposeOpen;
    if (wasOpen && !isComposeOpen) {
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const container = messagesContainerRef.current;
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          });
        });
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [isComposeOpen]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Deep link: Auto-open channel settings when URL param is present (?openSettings=true&tab=automation)
  useEffect(() => {
    const openSettingsParam = searchParams.get('openSettings');
    const tabParam = searchParams.get('tab');
    const tabId = parseChannelSettingsTabParam(tabParam);
    const openAddSkill =
      searchParams.get('viewSkills') === 'true' ||
      searchParams.get('openAddSkill') === 'true' ||
      searchParams.get('addSkill') === 'true' ||
      (openSettingsParam === 'true' && tabId === 'skills');

    if (openSettingsParam === 'true' && channelId) {
      setInitialSettingsTabId(tabId ?? 'config');
      setIsSettingsDrawerOpen(true);
      // URL race fix — cleanup of openSettings/tab now happens centrally in
      // buildThreadsNavigate() the next time this component navigates, instead of racing a
      // 200ms timer against fetchThreads()'s own auto-select-first-thread navigate(). See that
      // function's own comment for why the timer approach silently lost the deep-link intent.
    }
    if (openAddSkill && channelId) {
      setInitialSettingsTabId('skills');
      setInitialOpenAddSkill(true);
      setIsSettingsDrawerOpen(true);
    }
  }, [searchParams, channelId]);

  // State for task panel open control
  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState<boolean>(false);
  
  // State for approval panel open control
  const [isApprovalPanelOpen, setIsApprovalPanelOpen] = useState<boolean>(false);

  // State for Ask Prism7 right panel (MUI Drawer)
  const [isAskPrism7PanelOpen, setIsAskPrism7PanelOpen] = useState<boolean>(false);
  // True while subscription validation runs on Ask Prism7 click (blocks double-open and shows button loading)
  const [askPrism7Validating, setAskPrism7Validating] = useState<boolean>(false);

  // When Ask Prism7 panel is open, scroll to bottom so user sees posted message and thinking state
  useEffect(() => {
    if (!isAskPrism7PanelOpen) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      });
    });
  }, [isAskPrism7PanelOpen, temporaryUserMessage, isAiThinking, isGeneratingDraft]);

  // Deep link: Auto-open task panel when URL param is present
  useEffect(() => {
    const openTaskParam = searchParams.get('openTask');

    if (openTaskParam === 'true' && channelId) {
      // Open task panel
      setIsTaskPanelOpen(true);
      // URL race fix — cleanup of openTask now happens centrally in buildThreadsNavigate() the
      // next time this component navigates, instead of racing a 200ms timer against
      // fetchThreads()'s own auto-select-first-thread navigate(). See that function's own
      // comment for why the timer approach silently lost the deep-link intent.
    }
  }, [searchParams, channelId]);

  // Deep link: Auto-open approval panel when URL param is present
  useEffect(() => {
    const openApprovalParam = searchParams.get('openApproval');

    if (openApprovalParam === 'true' && channelId) {
      // Open approval panel
      setIsApprovalPanelOpen(true);
      // URL race fix — cleanup of openApproval now happens centrally in buildThreadsNavigate()
      // the next time this component navigates, instead of racing a 200ms timer against
      // fetchThreads()'s own auto-select-first-thread navigate(). See that function's own
      // comment for why the timer approach silently lost the deep-link intent.
    }
  }, [searchParams, channelId]);

  // Standard folder key for MessageActionsMenu (from selected sidebar category; not from stale message.emailFolder) – must be before any conditional return (Rules of Hooks)
  const threadClassificationEmail = useMemo((): 'inbox' | 'sent' | 'spam' | 'no_reply' | null => {
    const base = getBaseClassificationKey(selectedEmailCategory ?? '');
    if (base === 'inbox' || base === 'sent' || base === 'spam' || base === 'no_reply') {
      return base;
    }
    return null;
  }, [selectedEmailCategory]);

  // Resolve external_app_id for a thread (for move-to / drag-drop) – must be before any conditional return (Rules of Hooks)
  const getExternalAppIdForThread = useCallback(
    (threadId: string): string | null => {
      const thread = apiThreads.find((t) => t.id === threadId);
      if (thread?.external_app_id) return thread.external_app_id;
      if (selectedThread?.id === threadId) {
        const first = messages.find((m) => m.emailResponse?.externalAppId ?? m.externalAppId);
        return first?.emailResponse?.externalAppId ?? first?.externalAppId ?? null;
      }
      return null;
    },
    [apiThreads, selectedThread?.id, messages]
  );

  // Ids currently shown in ThreadList (dedupe + message search) — must run before any early return (Rules of Hooks)
  const visibleBulkThreadIds = useMemo(() => {
    const threads = showArchivedThreads
      ? sortThreadsByLastActivity(apiThreads)
      : getFilteredThreads();
    const seen = new Set<string>();
    const uniqueThreads = threads.filter((t) => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
    const searchText = emailFilters.body;
    if (channelType !== "message" || searchText.trim() === "") {
      return uniqueThreads.map((t) => t.id);
    }
    return uniqueThreads
      .filter((t) => messageThreadMatchesClientSearch(t, searchText))
      .map((t) => t.id);
  }, [
    apiThreads,
    showArchivedThreads,
    selectedEmailCategory,
    channelType,
    emailFilters.body,
  ]);

  const handleBulkToggle = useCallback((threadId: string, checked: boolean) => {
    setBulkSelectedThreadIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(threadId);
      else next.delete(threadId);
      return Array.from(next);
    });
  }, []);

  const clearBulkSelection = useCallback(() => {
    setBulkSelectedThreadIds([]);
  }, []);

  const handleBulkSelectAllVisible = useCallback(() => {
    setBulkSelectedThreadIds((prev) => {
      const visible = visibleBulkThreadIds;
      if (visible.length === 0) return prev;
      const allSelected = visible.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !visible.includes(id));
      }
      return Array.from(new Set([...prev, ...visible]));
    });
  }, [visibleBulkThreadIds]);

  const bulkSelectAllCheckbox = useMemo(() => {
    const v = visibleBulkThreadIds;
    if (v.length === 0) {
      return { checked: false, indeterminate: false };
    }
    const n = v.filter((id) => bulkSelectedThreadIds.includes(id)).length;
    return {
      checked: n === v.length,
      indeterminate: n > 0 && n < v.length,
    };
  }, [visibleBulkThreadIds, bulkSelectedThreadIds]);

  // Matches ThreadList isViewingArchivedThreads: list context is authoritative when API omits is_archived on rows
  const bulkListIsArchivedView = useMemo(
    () =>
      selectedEmailCategory === "archived" ||
      showArchivedThreads ||
      emailFilters.is_archived === true,
    [selectedEmailCategory, showArchivedThreads, emailFilters.is_archived]
  );

  const threadsToBulkArchiveCount = useMemo(() => {
    return bulkSelectedThreadIds.filter((id) => {
      const t = apiThreads.find((x) => x.id === id);
      if (!t) return false;
      if (bulkListIsArchivedView) return false;
      return !t.is_archived;
    }).length;
  }, [bulkSelectedThreadIds, apiThreads, bulkListIsArchivedView]);

  const threadsToBulkUnarchiveCount = useMemo(() => {
    return bulkSelectedThreadIds.filter((id) => {
      const t = apiThreads.find((x) => x.id === id);
      if (!t) return false;
      if (bulkListIsArchivedView) return true;
      return !!t.is_archived;
    }).length;
  }, [bulkSelectedThreadIds, apiThreads, bulkListIsArchivedView]);

  // Access denied (403) - user is not a member of this channel
  if (accessDenied) {
    return <AccessDenied />;
  }

  // Channel not found (404) - same full-page design as access denied
  if (channelNotFound) {
    return <AccessDenied variant="channelNotFound" />;
  }

  // Loading state - only show full screen loading for initial page load.
  // Skip when the icon landing view is active (no panel open): Dashboard / Data Sources /
  // Settings icons don't depend on threads, so blocking that view with a spinner is wrong.
  if (loading && apiThreads.length === 0 && !channelIconLandingOpen) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading {LanguageConstants.thread_s.toLowerCase()}...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => fetchThreads()} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

  const openAnalyticsPanel = async () => {
    // Analytics panel functionality
  };

  // Function to handle source panel toggle
  const handleSourcePanelToggle = () => {
    const willClose = isSourcePanelOpen;
    const currentFlags = { isDashboardOpen, isSourcePanelOpen, isSettingsDrawerOpen };
    if (willClose) {
      const next = closeSourcePanelFlags(currentFlags);
      setIsSourcePanelOpen(next.isSourcePanelOpen);
      // Refetch embedding status when closing panel so Ask Prism7 button updates (e.g. after adding data)
      fetchEmbeddingStatus();
    } else if (isChannelIconLandingOpen(currentFlags)) {
      // Opening from the home screen (landing view): keep the landing cards visible as the
      // background at z-1300; the Data Sources backdrop (z-1340) and panel (z-1350) sit above it.
      // Do NOT force isDashboardOpen — that would replace the home screen with the Dashboard.
      setIsSourcePanelOpen(true);
      setIsSettingsDrawerOpen(false);
    } else {
      // Opening from an active panel state (e.g. Dashboard is already open):
      // keep Dashboard in the background and close Settings.
      const next = openSourcePanelFlags(currentFlags);
      setIsSourcePanelOpen(next.isSourcePanelOpen);
      setIsDashboardOpen(next.isDashboardOpen);
      setIsSettingsDrawerOpen(next.isSettingsDrawerOpen);
    }
  };

  const handleSettingsClick = () => {
    setIsSettingsDrawerOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsDrawerOpen(false);
    // Reset initial tab when drawer closes
    setInitialSettingsTabId(undefined);
  };

  const handleAddThreadClick = () => {
    setIsAddThreadModalOpen(true);
  };

  const handleAddThreadClose = () => {
    setIsAddThreadModalOpen(false);
    // Reset loading state when modal is closed
    setIsCreatingThread(false);
  };

  const handleAddThread = async (threadData: {
    name: string;
    description?: string;
  }) => {
    try {
      if (!channelId) {
        return;
      }

      // When subscription is disabled, validate via API before allowing thread creation (same limit as messages)
      if (DISABLE_SUBSCRIPTION) {
        const validationData = await validateSubscription({ channelId });
        if (!validationData?.canCreateMessage) {
          setMessageLimitModalOpen(true);
          return;
        }
      }

      // Set loading state
      setIsCreatingThread(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      
      // Check if this is a WhatsApp channel
      const isWhatsApp = channelData?.messaging_provider === 'whatsapp';
      
      let response;
      let createdThread;
      
      if (isWhatsApp) {
        // Use WhatsApp thread creation API
        response = await fetch(
          `${API_CONFIG.EMAIL_API_BASE_URL}${MESSAGING_API_ENDPOINTS.CREATE_WHATSAPP_THREAD}`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              channel_id: channelId,
              recipient_phone: threadData.name.trim(),
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const whatsappResponse = await response.json();
        
        // WhatsApp API returns { success, thread_id, message, channel_id, recipient_phone }
        // We need to construct a thread object that matches ApiThread interface
        createdThread = {
          id: whatsappResponse.thread_id,
          title: whatsappResponse.recipient_phone,
          description: "",
          message_count: 0,
          last_message_at: null,
          email_thread_id: whatsappResponse.thread_id,
          original_message_id: null,
          email_type: null,
          from_email_id: whatsappResponse.recipient_phone,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      } else {
        // Use regular thread creation API
        response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.CREATE_THREAD}`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              channel_id: channelId,
              title: threadData.name,
              description: threadData.description || "",
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        createdThread = await response.json();
      }

      // Show success message
      showSuccess(isWhatsApp ? "Chat created successfully!" : "Thread created successfully!");

      // Emit socket event for real-time updates
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.THREAD_CREATED,
            data: { channelId: channelId, threadId: createdThread.id },
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }

      // Close the modal after successful creation
      handleAddThreadClose();

      // Add the new thread to the top of the list without full refresh
      setApiThreads((prevThreads) => [createdThread, ...prevThreads]);

      // Set the newly created thread as selected; selectedThread effect will fetch messages
      safeSetSelectedThread(createdThread);
    } catch (error) {
      const isWhatsApp = channelData?.messaging_provider === 'whatsapp';
      let errorMessage = isWhatsApp ? "Failed to create chat" : "Failed to create thread";

      if (error instanceof Error) {
        if (error.message.includes("HTTP error! status:")) {
          const statusMatch = error.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "400":
              errorMessage = isWhatsApp
                ? "Invalid phone number. Please check your input and try again."
                : "Invalid thread data. Please check your input and try again.";
              break;
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage = isWhatsApp
                ? "You do not have permission to create chats in this channel."
                : "You do not have permission to create threads in this channel.";
              break;
            case "409":
              errorMessage = isWhatsApp
                ? "A chat with this phone number already exists."
                : "A thread with this name already exists.";
              break;
            case "422":
              errorMessage = isWhatsApp
                ? "Invalid phone number provided."
                : "Invalid thread information provided.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Creation failed (Error ${statusCode}). Please try again.`;
          }
        } else if (error.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      showError(errorMessage);
    } finally {
      // Reset loading state
      setIsCreatingThread(false);
    }
  };

  // Edit thread functionality
  const handleEditThread = async (threadData: {
    name: string;
    description: string;
  }) => {
    if (!editingThread) {
      return;
    }

    try {
      setIsEditingThread(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.UPDATE_THREAD(
          editingThread.id
        )}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            title: threadData.name,
            description: threadData.description,
            is_active: true,
            is_archived: false,
            ai_enabled: true,
            ai_summary: "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedThread = await response.json();

      // Show success message
      showSuccess("Thread updated successfully!");

      // Emit socket event for real-time updates
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.THREAD_UPDATED,
            data: { channelId: channelId, threadId: editingThread.id },
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }

      // Close the modal after successful update
      handleEditThreadClose();

      // Update the thread in the local state without full refresh
      setApiThreads((prevThreads) =>
        prevThreads.map((thread) =>
          thread.id === editingThread.id
            ? {
                ...thread,
                title: threadData.name,
                description: threadData.description,
              }
            : thread
        )
      );
    } catch (error) {

      let errorMessage = "Failed to update thread";

      if (error instanceof Error) {
        // Check if it's an HTTP error with status code
        if (error.message.includes("HTTP error! status:")) {
          const statusMatch = error.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "400":
              errorMessage =
                "Invalid thread data. Please check your input and try again.";
              break;
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to update this thread.";
              break;
            case "404":
              errorMessage = `${LanguageConstants.thread} not found. It may have been deleted.`;
              break;
            case "409":
              errorMessage = "Thread update conflict. Please try again.";
              break;
            case "422":
              errorMessage = "Invalid thread information provided.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Update failed (Error ${statusCode}). Please try again.`;
          }
        } else if (error.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      showError(errorMessage);
    } finally {
      setIsEditingThread(false);
    }
  };

  // Thread menu handlers
  const handleThreadMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    thread: ApiThread
  ) => {
    setThreadMenuAnchor(event.currentTarget);
    setSelectedThreadForMenu(thread);
  };

  const handleThreadMenuClose = () => {
    setThreadMenuAnchor(null);
    setSelectedThreadForMenu(null);
  };

  const handleMoveThreadSuccess = (targetChannelId: string) => {
    if (!threadToMove) return;
    // Remove the moved thread from the list
    const remainingThreads = apiThreads.filter((t) => t.id !== threadToMove.id);
    setApiThreads((prev) => prev.filter((t) => t.id !== threadToMove.id));
    // If we moved the active thread, activate the latest thread in the remaining list
    if (selectedThread?.id === threadToMove.id) {
      if (remainingThreads.length > 0) {
        const latestThread = sortThreadsByLastActivity(remainingThreads)[0];
        safeSetSelectedThread(latestThread);
      } else {
        safeSetSelectedThread(null);
      }
    }
    // If we moved an inactive thread, selectedThread is unchanged
    setThreadToMove(null);
    setIsMoveThreadModalOpen(false);
  };

  const handleEditThreadOpen = (thread: ApiThread) => {
    setEditingThread(thread);
    setEditThreadTitle(thread.title);
    setEditThreadDescription(thread.description || "");
    setIsEditThreadModalOpen(true);
    handleThreadMenuClose();
  };

  const handleEditThreadClose = () => {
    setIsEditThreadModalOpen(false);
    setEditingThread(null);
    setEditThreadTitle("");
    setEditThreadDescription("");
  };

  // Delete confirmation dialog handlers
  const handleDeleteConfirmOpen = (thread: ApiThread) => {
    setThreadToDelete(thread);
    setIsDeleteConfirmOpen(true);
    handleThreadMenuClose();
  };

  const handleDeleteConfirmClose = () => {
    setIsDeleteConfirmOpen(false);
    setThreadToDelete(null);
    setIsDeletingThread(false);
  };

  const handleDeleteConfirm = async () => {
    if (!threadToDelete) return;

    try {
      setIsDeletingThread(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.DELETE_THREAD(
          threadToDelete.id
        )}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      showSuccess("Thread deleted successfully!");

      // Emit socket event for real-time updates
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.THREAD_DELETED,
            data: { channelId: channelId, threadId: threadToDelete.id },
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }

      // Close the confirmation dialog
      handleDeleteConfirmClose();

      // Remove the thread from local state
      setApiThreads((prevThreads) =>
        prevThreads.filter((thread) => thread.id !== threadToDelete.id)
      );

      // Handle selection after deletion
      if (selectedThread?.id === threadToDelete.id) {
        // If the deleted thread was selected, select the first available thread or clear selection
        const remainingThreads = apiThreads.filter(
          (thread) => thread.id !== threadToDelete.id
        );
        if (remainingThreads.length > 0) {
          safeSetSelectedThread(remainingThreads[0]);
        } else {
          safeSetSelectedThread(null);
          setMessages([]);
        }
      }
    } catch (error) {

      let errorMessage = `Failed to delete ${LanguageConstants.thread.toLowerCase()}`;

      if (error instanceof Error) {
        if (error.message.includes("HTTP error! status:")) {
          const statusMatch = error.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to delete this thread.";
              break;
            case "404":
              errorMessage = `${LanguageConstants.thread} not found. It may have already been deleted.`;
              break;
            case "409":
              errorMessage = `Cannot delete ${LanguageConstants.thread.toLowerCase()}. It may be in use.`;
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Delete failed (Error ${statusCode}). Please try again.`;
          }
        } else if (error.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      showError(errorMessage);
    } finally {
      setIsDeletingThread(false);
    }
  };

  const executeBulkArchive = async () => {
    const idsToArchive = bulkSelectedThreadIds.filter((id) => {
      const t = apiThreads.find((x) => x.id === id);
      if (!t) return false;
      if (bulkListIsArchivedView) return false;
      return !t.is_archived;
    });
    if (idsToArchive.length === 0) {
      showError("No threads to archive.");
      setIsBulkArchiveConfirmOpen(false);
      return;
    }
    setIsBulkArchiving(true);
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
    let archivedOk: string[] = [];
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.ARCHIVE_THREADS_BULK}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ thread_ids: idsToArchive }),
        }
      );
      if (response.status === 401) {
        markSessionExpired();
        setIsBulkArchiving(false);
        return;
      }
      if (!response.ok) {
        let errMsg = "Failed to archive threads.";
        try {
          const errBody = (await response.json()) as { detail?: string; message?: string };
          errMsg = errBody.detail || errBody.message || errMsg;
        } catch {
          /* use default */
        }
        showError(errMsg);
        setIsBulkArchiving(false);
        setIsBulkArchiveConfirmOpen(false);
        return;
      }
      archivedOk = [...idsToArchive];
    } catch {
      showError("Failed to archive threads. Please try again.");
      setIsBulkArchiving(false);
      setIsBulkArchiveConfirmOpen(false);
      return;
    }
    if (archivedOk.length > 0) {
      const remaining = apiThreads.filter((t) => !archivedOk.includes(t.id));
      setApiThreads(remaining);
      clearTemporaryMessages();
      if (selectedThread && archivedOk.includes(selectedThread.id)) {
        let filteredRemaining: ApiThread[];
        switch (selectedEmailCategory) {
          case "inbox":
            filteredRemaining = remaining.filter((t) => !t.is_archived);
            break;
          case "archived":
            filteredRemaining = remaining.filter((t) => t.is_archived);
            break;
          default:
            filteredRemaining = remaining;
        }
        const latestInRemaining =
          sortThreadsByLastActivity(filteredRemaining)[0] ?? null;
        safeSetSelectedThread(latestInRemaining);
      }
      showSuccess(`${archivedOk.length} thread(s) archived successfully!`);
    }
    setBulkSelectedThreadIds((prev) =>
      prev.filter((id) => !archivedOk.includes(id))
    );
    setIsBulkArchiveConfirmOpen(false);
    setIsBulkArchiving(false);
  };

  const executeBulkUnarchive = async () => {
    const idsToUnarchive = bulkSelectedThreadIds.filter((id) => {
      const t = apiThreads.find((x) => x.id === id);
      if (!t) return false;
      if (bulkListIsArchivedView) return true;
      return !!t.is_archived;
    });
    if (idsToUnarchive.length === 0) {
      showError("No archived threads to restore.");
      setIsBulkUnarchiveConfirmOpen(false);
      return;
    }
    setIsBulkUnarchiving(true);
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
    let unarchivedOk: string[] = [];
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.UNARCHIVE_THREADS_BULK}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ thread_ids: idsToUnarchive }),
        }
      );
      if (response.status === 401) {
        markSessionExpired();
        setIsBulkUnarchiving(false);
        return;
      }
      if (!response.ok) {
        let errMsg = "Failed to unarchive threads.";
        try {
          const errBody = (await response.json()) as { detail?: string; message?: string };
          errMsg = errBody.detail || errBody.message || errMsg;
        } catch {
          /* use default */
        }
        showError(errMsg);
        setIsBulkUnarchiving(false);
        setIsBulkUnarchiveConfirmOpen(false);
        return;
      }
      unarchivedOk = [...idsToUnarchive];
    } catch {
      showError("Failed to unarchive threads. Please try again.");
      setIsBulkUnarchiving(false);
      setIsBulkUnarchiveConfirmOpen(false);
      return;
    }
    if (unarchivedOk.length > 0) {
      const viewingArchivedOnly =
        selectedEmailCategory === "archived" ||
        showArchivedThreads ||
        emailFilters.is_archived === true;
      const remaining = viewingArchivedOnly
        ? apiThreads.filter((t) => !unarchivedOk.includes(t.id))
        : apiThreads.map((t) =>
            unarchivedOk.includes(t.id) ? { ...t, is_archived: false } : t
          );
      setApiThreads(remaining);
      clearTemporaryMessages();
      if (selectedThread && unarchivedOk.includes(selectedThread.id)) {
        if (viewingArchivedOnly) {
          let filteredRemaining: ApiThread[];
          switch (selectedEmailCategory) {
            case "inbox":
              filteredRemaining = remaining.filter((t) => !t.is_archived);
              break;
            case "archived":
              filteredRemaining = remaining.filter((t) => t.is_archived);
              break;
            default:
              filteredRemaining = remaining;
          }
          const latestInRemaining =
            sortThreadsByLastActivity(filteredRemaining)[0] ?? null;
          safeSetSelectedThread(latestInRemaining);
        } else {
          safeSetSelectedThread({
            ...selectedThread,
            is_archived: false,
          });
        }
      }
      showSuccess(`${unarchivedOk.length} thread(s) unarchived successfully!`);
    }
    setBulkSelectedThreadIds((prev) =>
      prev.filter((id) => !unarchivedOk.includes(id))
    );
    setIsBulkUnarchiveConfirmOpen(false);
    setIsBulkUnarchiving(false);
  };

  const executeBulkDelete = async () => {
    if (bulkSelectedThreadIds.length === 0) {
      setIsBulkDeleteConfirmOpen(false);
      return;
    }
    setIsBulkDeleting(true);
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
    const toDelete = [...bulkSelectedThreadIds];
    let deletedOk: string[] = [];
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.DELETE_THREADS_BULK}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ thread_ids: toDelete }),
        }
      );
      if (response.status === 401) {
        markSessionExpired();
        setIsBulkDeleting(false);
        return;
      }
      if (!response.ok) {
        let errMsg = "Failed to delete threads.";
        try {
          const errBody = (await response.json()) as { detail?: string; message?: string };
          errMsg = errBody.detail || errBody.message || errMsg;
        } catch {
          /* use default */
        }
        showError(errMsg);
        setIsBulkDeleting(false);
        setIsBulkDeleteConfirmOpen(false);
        return;
      }
      deletedOk = [...toDelete];
      if (channelId) {
        for (const threadId of deletedOk) {
          try {
            socketService.sendSocketMessage({
              type: SOCKET_KEYS.THREAD_DELETED,
              data: { channelId, threadId },
            });
          } catch {
            /* non-fatal */
          }
        }
      }
    } catch {
      showError("Failed to delete threads. Please try again.");
      setIsBulkDeleting(false);
      setIsBulkDeleteConfirmOpen(false);
      return;
    }
    if (deletedOk.length > 0) {
      const remainingThreads = apiThreads.filter((t) => !deletedOk.includes(t.id));
      setApiThreads(remainingThreads);
      if (selectedThread && deletedOk.includes(selectedThread.id)) {
        if (remainingThreads.length > 0) {
          safeSetSelectedThread(remainingThreads[0]);
        } else {
          safeSetSelectedThread(null);
          setMessages([]);
        }
      }
      showSuccess(`${deletedOk.length} thread(s) deleted successfully!`);
    }
    setBulkSelectedThreadIds((prev) =>
      prev.filter((id) => !deletedOk.includes(id))
    );
    setIsBulkDeleteConfirmOpen(false);
    setIsBulkDeleting(false);
  };

  // Get the selected thread data from API threads
  const currentSelectedThread = selectedThread;

  // Check if the selected thread is archived
  const isSelectedThreadArchived = currentSelectedThread?.is_archived || false;

  // Check if the channel is archived
  const isChannelArchived = channelData?.is_archived || false;

  // Function to get user avatar color
  const getUserAvatarColor = (userId: string) => {
    const colors = [
      "#1976d2",
      "#4caf50",
      "#ff9800",
      "#e91e63",
      "#9c27b0",
      "#f44336",
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Helper function to fetch thread details by ID
  const fetchThreadById = async (threadId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const params = new URLSearchParams({
        thread_id: threadId
      });
      console.log("[ThreadDetail][fetchThreadById LIST_THREADS]", {
        threadId,
        channelId: channelId || "",
        query: params.toString(),
      });
      const response = await fetch(
        `${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.LIST_THREADS(
          channelId || ""
        )}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ThreadsResponse = await response.json();
      if (data.data.threads.length > 0) {
        return data.data.threads[0];
      }
    } catch (error) {
      return null;
    }
  };
  // Keep socket handler ref updated so shay_realtime can call fetchThreadById/fetchMessages without TDZ/stale closure (inline, not a hook, so hook order stays fixed)
  fetchThreadByIdRef.current = fetchThreadById;
  fetchMessagesRef.current = fetchMessages;

  // Function to handle moving email to inbox
  const handleMoveToInbox = async (message: ApiMessage) => {
    // Prefer emailResponse.externalAppId, fallback to top-level externalAppId (when emailResponse absent)
    const externalAppId = message.emailResponse?.externalAppId ?? message.externalAppId;
    if (!externalAppId) {
      showError("External app ID not found for this email");
      return;
    }

    const messageId = message.id;
    setMovingToInbox(prev => ({ ...prev, [messageId]: true }));

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const moveRequest: MoveToInboxRequest = {
        external_app_id: externalAppId
      };

      const response: MoveToInboxResponse = await moveToInbox(moveRequest, token);

      if (response.success) {
        showSuccess(`Email moved to inbox. ${response.emails_moved} emails moved.`);
        
        // Store thread ID to be selected after category update
        if (response.thread_id) {
          pendingThreadSelectionRef.current = response.thread_id;
        }
        
        // Update category based on API response (this will trigger useEffect and refetch threads)
        setSelectedEmailCategory(response.new_classification);
        // Keep URL classification in sync so refresh stays on the correct folder
        if (channelId && response.new_classification) {
          const isEmailCh = (channelData?.channel_type || "email") === "email";
          navigate(
            buildThreadsNavigate(
              channelId,
              selectedThread?.id,
              searchParams,
              response.new_classification,
              isEmailCh
            ),
            { replace: true }
          );
        }
        // Keep per-message folder chips / move button in sync without refetching messages
        setMessages((prev) => applyNewClassificationToMessages(prev, response.new_classification));

        // Update header email metrics so total count reflects moved emails (inbox +N, previous category -N)
        const moved = response.emails_moved;
        const prevKey = response.previous_classification;
        const newKey = response.new_classification;
        if (moved > 0 && (prevKey || newKey)) {
          setEmailMetricsForHeader((prev) =>
            prev.map((m) => {
              if (m.key === prevKey) {
                return {
                  ...m,
                  count: Math.max(0, (m.count ?? 0) - moved),
                  unread_count: Math.max(0, (m.unread_count ?? 0) - moved),
                };
              }
              if (m.key === newKey) {
                return {
                  ...m,
                  count: (m.count ?? 0) + moved,
                  unread_count: (m.unread_count ?? 0) + moved,
                };
              }
              return m;
            })
          );
        }
      }
    } catch (error) {
      console.error("Error moving email to inbox:", error);
      showError(error instanceof Error ? error.message : "Failed to move email to inbox");
    } finally {
      setMovingToInbox(prev => ({ ...prev, [messageId]: false }));
    }
  };

  // Move whole thread to target classification (inbox, spam, no_reply) – same API for all
  const handleMoveToClassification = async (classification: EmailClassification) => {
    // Prefer emailResponse.externalAppId, fallback to top-level externalAppId (e.g. when emailResponse absent)
    const firstWithExtId = messages.find((m) => m.emailResponse?.externalAppId ?? m.externalAppId);
    const externalAppId =
      firstWithExtId?.emailResponse?.externalAppId ?? firstWithExtId?.externalAppId;
    if (!externalAppId || !channelId) {
      showError("Cannot move thread: missing thread or channel context");
      return;
    }
    setMovingToClassification(classification);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const payload: MoveToClassificationRequest = {
        external_app_id: externalAppId,
        channel_id: channelId,
      };
      const response = await moveToClassification(classification, payload, token);
      if (response.success) {
        const moved = response.emails_moved ?? 0;
        showSuccess(moved > 0 ? `Thread moved. ${moved} email(s) moved to ${classification}.` : "Thread moved.");
        if (response.thread_id) {
          pendingThreadSelectionRef.current = response.thread_id;
        }
        if (response.new_classification) {
          setSelectedEmailCategory(response.new_classification);
          if (channelId) {
            const isEmailCh = (channelData?.channel_type || "email") === "email";
            navigate(
              buildThreadsNavigate(
                channelId,
                selectedThread?.id,
                searchParams,
                response.new_classification,
                isEmailCh
              ),
              { replace: true }
            );
          }
        }
        setMessages((prev) => applyNewClassificationToMessages(prev, response.new_classification));
        const prevKey = response.previous_classification;
        const newKey = response.new_classification;
        if (moved > 0 && (prevKey || newKey)) {
          setEmailMetricsForHeader((prev) =>
            prev.map((m) => {
              if (m.key === prevKey) {
                return {
                  ...m,
                  count: Math.max(0, (m.count ?? 0) - moved),
                  unread_count: Math.max(0, (m.unread_count ?? 0) - moved),
                };
              }
              if (m.key === newKey) {
                return {
                  ...m,
                  count: (m.count ?? 0) + moved,
                  unread_count: (m.unread_count ?? 0) + moved,
                };
              }
              return m;
            })
          );
        }
      }
    } catch (error) {
      console.error("Error moving thread to classification:", error);
      showError(error instanceof Error ? error.message : "Failed to move thread");
    } finally {
      setMovingToClassification(null);
    }
  };

  // Drag-and-drop: move thread to category (from thread list drop on sidebar category)
  const handleThreadDropOnCategory = async (
    targetCategory: string,
    payload: { threadId: string; channelId: string; external_app_id: string | null }
  ) => {
    const classification = targetCategory as EmailClassification;
    if (!["inbox", "spam", "no_reply"].includes(classification)) return;
    if (payload.channelId !== channelId) return;
    const externalAppId =
      payload.external_app_id ?? getExternalAppIdForThread(payload.threadId);
    if (!externalAppId) {
      const isViewingThisThread = selectedThread?.id === payload.threadId;
      if (isViewingThisThread) {
        showError("Move isn't available for this conversation yet. Try again in a moment.");
      } else {
        showError("Please open this conversation first, then try moving it to the new category.");
      }
      return;
    }
    setMovingToClassification(classification);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await moveToClassification(
        classification,
        { external_app_id: externalAppId, channel_id: payload.channelId },
        token
      );
      if (response.success) {
        const moved = response.emails_moved ?? 0;
        showSuccess(
          moved > 0 ? `Thread moved. ${moved} email(s) moved to ${classification}.` : "Thread moved."
        );
        if (response.thread_id) {
          pendingThreadSelectionRef.current = response.thread_id;
        }
        if (response.new_classification) {
          setSelectedEmailCategory(response.new_classification);
          if (channelId) {
            const isEmailCh = (channelData?.channel_type || "email") === "email";
            navigate(
              buildThreadsNavigate(
                channelId,
                selectedThread?.id,
                searchParams,
                response.new_classification,
                isEmailCh
              ),
              { replace: true }
            );
          }
        }
        setMessages((prev) => applyNewClassificationToMessages(prev, response.new_classification));
        const prevKey = response.previous_classification;
        const newKey = response.new_classification;
        if (moved > 0 && (prevKey || newKey)) {
          setEmailMetricsForHeader((prev) =>
            prev.map((m) => {
              if (m.key === prevKey) {
                return {
                  ...m,
                  count: Math.max(0, (m.count ?? 0) - moved),
                  unread_count: Math.max(0, (m.unread_count ?? 0) - moved),
                };
              }
              if (m.key === newKey) {
                return {
                  ...m,
                  count: (m.count ?? 0) + moved,
                  unread_count: (m.unread_count ?? 0) + moved,
                };
              }
              return m;
            })
          );
        }
        // Refetch thread list for the target category so we show only that category's threads (avoid stale closure)
        void fetchThreads(undefined, response.new_classification);
      }
    } catch (error) {
      console.error("Error moving thread (drag-and-drop):", error);
      showError(error instanceof Error ? error.message : "Failed to move thread");
    } finally {
      setMovingToClassification(null);
    }
  };

  // Function to format content with basic markdown-like styling
  const formatContent = (content: string) => {
    return content.split("\n").map((line, index) => {
      if (line.startsWith("###")) {
        return (
          <Typography
            key={index}
            variant="h6"
            sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
          >
            {line.replace("###", "").trim()}
          </Typography>
        );
      } else if (line.startsWith("â€¢")) {
        return (
          <Typography key={index} variant="body1" sx={{ ml: 2, mb: 0.5 }}>
            {line}
          </Typography>
        );
      } else if (line.trim() === "") {
        return <Box key={index} sx={{ mb: 1 }} />;
      } else {
        return (
          <Typography key={index} variant="body1" sx={{ mb: 1 }}>
            {line}
          </Typography>
        );
      }
    });
  };

  // Function to handle back navigation (stay on supportSpace; do not re-enter channel when sync/async completes)
  const handleBackClick = () => {
    userNavigatedBackRef.current = true;
    navigate("/supportSpace");
  };

  // Function to handle cancel button click
  const handleCancelClick = () => {
    // Clear any pending debounced updates
    if (contentUpdateTimeoutRef.current) {
      clearTimeout(contentUpdateTimeoutRef.current);
      contentUpdateTimeoutRef.current = null;
    }

    // Clear the state
    setNewComment("");
    setSelectedFiles([]);

    // Reset focus state
    setIsEditorFocused(false);

    // Force the RichTextEditor to completely re-render by changing the key
    setEditorKey((prev) => prev + 1);
  };



  // Attachment upload validation handler
  // Validates subscription before allowing attachment upload (pass channelId when inside channel)
  const handleAttachmentValidation = async (): Promise<boolean> => {
    try {
      const validationData = await validateSubscription(channelId ? { channelId } : {});
      
      if (validationData && !validationData.canCreateAttachment) {
        // Show restriction modal if attachment creation is not allowed
        setOpenAttachmentRestrictionModal(true);
        return false; // Block attachment upload
      }
      
      return true; // Allow attachment upload
    } catch (error) {
      // On error, allow user to proceed (fail open approach)
      console.error('Error validating subscription for attachment:', error);
      return true;
    }
  };

  // Message posting functionality
  const handlePostMessage = async (messageOverride?: string) => {
    // Use provided message or fall back to newComment state
    const messageToProcess = messageOverride || newComment;
    
    // Check if there's actual text content (not just HTML tags)
    const hasContent = messageToProcess.replace(/<[^>]*>/g, "").trim();
    if (
      !selectedThread?.id ||
      !hasContent ||
      isPosting ||
      isSelectedThreadArchived ||
      isChannelArchived
    )
      return;

    // Determine the effective posting method early
    const effectivePostingMethod = postingMethod;
    
    // Subscription check for customer-facing posts only; internal-reply (staff notes) skips Zoho validation
    // Pass channelId so backend can enforce per-channel limits; show Upgrade plan modal if not allowed
    const shouldValidateSubscriptionBeforePost =
      effectivePostingMethod === "ask-shay" || effectivePostingMethod === "channel-reply";
    if (shouldValidateSubscriptionBeforePost) {
      try {
        const validationData = await validateSubscription(channelId ? { channelId } : {});
        if (validationData && !validationData.canCreateMessage) {
          setOpenMessageRestrictionModal(true);
          return;
        }
      } catch (error) {
        console.error('Error validating subscription:', error);
        // On error, allow user to proceed (fail open)
      }
    }
    // When subscription is disabled, validate via API before allowing message post (not for internal staff notes)
    if (DISABLE_SUBSCRIPTION && channelId && effectivePostingMethod !== "internal-reply") {
      const validationData = await validateSubscription({ channelId });
      if (!validationData?.canCreateMessage) {
        setMessageLimitModalOpen(true);
        return;
      }
    }

    setIsPosting(true);

    try {
      // Clean the content using helper function
      const cleanContent = cleanMessageContent(messageToProcess);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";

      // Clear the input immediately (for ask-shay we restore @Prism7 default after success in the success path below)
      if (effectivePostingMethod !== "ask-shay") {
        setNewComment("");
      }
      setIsEditorFocused(false);
      setEditorKey((prev) => prev + 1);

      // Clear any pending debounced updates
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
        contentUpdateTimeoutRef.current = null;
      }

      // Set temporary user message for display during API progress
      setTemporaryUserMessage(cleanContent);

      // Use email as default when channel_type missing (legacy email channels)
      const currentChannelType = channelData?.channel_type || 'email';
      
      // Check if this is a WhatsApp channel
      // Check both 'provider' field (from API response) and 'messaging_provider' field (normalized)
      const channelProvider = (channelData as any)?.provider || channelData?.messaging_provider;
      const isWhatsAppChannel = channelProvider === 'whatsapp';
      
      // For WhatsApp: only "channel-reply" (Reply button) uses send_message API (direct to WhatsApp)
      // "ask-shay" for WhatsApp uses main_chat API same as email channels; "internal-reply" uses /api/v1/messages/
      if (isWhatsAppChannel && effectivePostingMethod === "channel-reply") {
        // Validate that we have channel_id before proceeding
        if (!channelId) {
          setIsPosting(false);
          throw new Error('WhatsApp channel is missing channel_id. Please reconnect the WhatsApp account.');
        }

        // Extract recipient phone number from thread title
        let recipientPhoneNumber = '';
        
        if (selectedThread?.title) {
          // Extract phone number from thread title (e.g., "WhatsApp: +1234567890" or "828681237004563")
          const phoneMatch = selectedThread.title.match(/(\+?\d{10,15})/);
          if (phoneMatch) {
            recipientPhoneNumber = phoneMatch[1];
          }
        }
        
        // Validate recipient is present
        if (!recipientPhoneNumber || recipientPhoneNumber.trim() === '') {
          setIsPosting(false);
          throw new Error('Recipient phone number is required for WhatsApp messages. Please ensure the thread title contains a valid phone number.');
        }

        // Send WhatsApp message using messaging API
        const whatsappRequest: SendMessageRequest = {
          provider: 'whatsapp',
          channel_id: channelId,
          content: cleanContent,
          recipient: recipientPhoneNumber.trim(), // Phone number from thread/messages
          thread_id: selectedThread?.email_thread_id || undefined, // WhatsApp thread ID for replies
        };

        try {
          const whatsappResponse = await sendMessage(whatsappRequest, token);
          
          if (whatsappResponse.success) {
            // Clear temporary user message
            setTemporaryUserMessage(null);
            // For Reply (channel-reply), close message box so user sees action buttons again
            if (effectivePostingMethod === "channel-reply") {
              setIsMessageBoxVisible(false);
            }
            // Silently refresh messages to show the sent message (no loading UI)
            if (selectedThread?.id) {
              await fetchMessages(selectedThread.id, false, undefined, true);
            }
            showSuccess("WhatsApp message sent successfully!");
            setIsPosting(false);
            return;
          } else {
            throw new Error(whatsappResponse.message || whatsappResponse.error || 'Failed to send WhatsApp message');
          }
        } catch (whatsappError) {
          setIsPosting(false);
          throw whatsappError;
        }
      }

      // Auto-close editor for Ask Prism7 mode
      if (effectivePostingMethod === "ask-shay") {
        setIsMessageBoxVisible(false);
        setShowCloseIcon(true); // Reset for next time
        setTimeout(() => {
          scrollToBottom();
        }, 1200);
      }

      if (effectivePostingMethod === "internal-reply") {
        // Upload attachments and keep full result so we can show filename/size before API returns them
        let attachmentIds: string[] = [];
        let uploadedAttachmentDetails: Array<{ attachment_id: string; filename: string; content_type?: string; size_bytes?: number }> = [];
        try {
          const filesToUpload = selectedFiles.map((sf) => sf.file).filter(Boolean) as File[];
          if (filesToUpload.length > 0 && channelId) {
            const uploadResult = await uploadMessageAttachments(
              filesToUpload,
              token,
              channelId,
              selectedThread?.id
            );
            attachmentIds = uploadResult.attachment_ids ?? [];
            // Keep attachment metadata so we can show file names immediately (API create often returns only ids)
            uploadedAttachmentDetails = uploadResult.attachments ?? [];
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachments';
          showError(errorMessage);
          setIsPosting(false);
          return;
        }

        // Internal reply for all channels (email, regular message, WhatsApp), call /api/v1/messages/
        const messagePayload: any = {
          content: cleanContent,
          thread_id: selectedThread?.id,
          message_type: "user",
        };

        // Add attachment_ids if any attachments were uploaded
        if (attachmentIds.length > 0) {
          messagePayload.attachment_ids = attachmentIds;
        }

        const messageResponse = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.CREATE_MESSAGE}`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(messagePayload),
          }
        );

        if (!messageResponse.ok) {
          throw new Error(`Failed to post message: ${messageResponse.status}`);
        }

        // Get the posted user message data
        const userMessageData = await messageResponse.json();

        // Clear temporary user message
        setTemporaryUserMessage(null);

        // Emit socket event for real-time updates (other clients/tabs receive and add message via socket handler)
        if (channelId && selectedThread?.id) {
          try {
            const eventData = {
              type: SOCKET_KEYS.THREAD_MESSAGE_CREATED,
              data: {
                channelId: channelId,
                threadId: selectedThread?.id,
                messageId: userMessageData.id,
              },
            };
            socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }

        // Build attachments for display: use upload result so filename/size show immediately (no refresh needed)
        const attachmentsForDisplay =
          uploadedAttachmentDetails.length > 0
            ? uploadedAttachmentDetails.map((a) => ({
                attachment_id: a.attachment_id,
                filename: a.filename,
                file_type: a.content_type,
                file_size: a.size_bytes,
              }))
            : (userMessageData.attachments ?? null);

        // Add new message to thread in real-time from create response (no extra fetch; works for all channels including WhatsApp)
        if (selectedThread?.id && userMessageData?.id) {
          const maxSeq = messagesRef.current.reduce(
            (max, m) => Math.max(max, m.sequence_number ?? 0),
            0
          );
          const currentUserBean = user?.id
            ? {
                id: user.id,
                email: user.email ?? "",
                name: user.name ?? "",
                full_name: user.full_name,
                role: "",
              }
            : { id: "", email: "", name: "", role: "" };
          const messagePayload = {
            id: userMessageData.id,
            content: userMessageData.content ?? cleanContent,
            message_type: userMessageData.message_type ?? "user",
            thread_id: userMessageData.thread_id ?? selectedThread.id,
            channel_id: userMessageData.channel_id ?? channelId ?? "",
            user_id: userMessageData.user_id ?? user?.id ?? "",
            query_id: userMessageData.query_id ?? null,
            userBean: userMessageData.userBean ?? userMessageData.user_bean ?? currentUserBean,
            is_ai_processed: userMessageData.is_ai_processed ?? false,
            ai_provider: userMessageData.ai_provider ?? null,
            ai_model: userMessageData.ai_model ?? null,
            ai_processing_time: userMessageData.ai_processing_time ?? null,
            is_visible: userMessageData.is_visible ?? true,
            is_pinned: userMessageData.is_pinned ?? false,
            sequence_number: userMessageData.sequence_number ?? maxSeq + 1,
            attachments: attachmentsForDisplay,
            created_at: userMessageData.created_at ?? new Date().toISOString(),
            updated_at: userMessageData.updated_at ?? userMessageData.created_at ?? new Date().toISOString(),
            ...(userMessageData.citations != null && { citations: userMessageData.citations }),
            ...(userMessageData.usage_metrics != null && { usage_metrics: userMessageData.usage_metrics }),
          };
          const newMessage = normalizeMessageFromApi(messagePayload);
          setMessages((prevMessages) => {
            const exists = prevMessages.some(
              (m) => m.id === newMessage.id || m.sequence_number === newMessage.sequence_number
            );
            return exists ? prevMessages : [...prevMessages, newMessage];
          });
          setTimeout(() => scrollToBottom(), 100);
        }
      } else {
        // If ask-shay, only call /shay/main_chat (main_chat API handles saving user message)
        setIsAiThinking(true);

        try {
          // Call the AI endpoint - it will handle saving the user message and return both user and AI messages
          const companyId = getCompanyId();
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          
          const aiResponse = await fetch(
            `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.AI_CHAT.MAIN_CHAT}`,
            {
              method: "POST",
              headers: {
                accept: "application/json",
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                channel_id: channelId,
                thread_id: selectedThread?.id,
                message_id: getLatestMessageId(messagesRef.current),
                user_query: cleanContent,
                is_drafter: false,
                company_id: companyId,
                user_timezone: userTimezone,
              }),
            }
          );

          if (!aiResponse.ok) {
            throw new Error(`AI API failed: ${aiResponse.status}`);
          }

          const aiData = await aiResponse.json();

          // Handle the new response format with data array
          if (
            aiData.status === "success" &&
            aiData.data &&
            Array.isArray(aiData.data)
          ) {
            // Clear temporary user message now that we have the real messages from API
            setTemporaryUserMessage(null);
            // Prefer per-message usage_metrics from aiData.data[i], fallback to usage_metrics inside data (main_chat API)
            const usageMetricsFromData = (aiData as any).data?.usage_metrics ?? (Array.isArray((aiData as any).data) ? (aiData as any).data?.[0]?.usage_metrics : undefined);
            const aiMessagesWithMetrics: ApiMessage[] = (aiData.data as any[]).map((msg: any) => ({
              ...msg,
              usage_metrics: msg.usage_metrics ?? usageMetricsFromData,
            }));
            
        // Add all messages from the response to the messages list (includes user message + AI response)
        // Citations are already included inside each message
        // Deduplicate by ID and sequence_number to prevent duplicates
        setMessages((prevMessages: ApiMessage[]) => {
          const existingIds = new Set(prevMessages.map((msg: ApiMessage) => msg.id));
          const existingSequences = new Set(prevMessages.map((msg: ApiMessage) => msg.sequence_number));
          const aiMessages: ApiMessage[] = aiMessagesWithMetrics;
          const newMessages: ApiMessage[] = aiMessages.filter((msg: ApiMessage) => 
            !existingIds.has(msg.id) && !existingSequences.has(msg.sequence_number)
          );
          return [...prevMessages, ...newMessages];
        });

            // Emit socket events for real-time updates for each message
            if (channelId && selectedThread?.id) {
              try {
                aiData.data.forEach((message: any) => {
                  const eventData = {
                    type: SOCKET_KEYS.THREAD_MESSAGE_CREATED,
                    data: {
                      channelId: channelId,
                      threadId: selectedThread?.id,
                      messageId: message.id,
                    },
                  };
                  socketService.sendSocketMessage(eventData);
                });
              } catch (error) {
                // Handle error silently
              }
            }
            // Restore @Prism7 in the input for the next message (Ask Prism7 panel)
            setNewComment(ASK_PRISM7_DEFAULT_CONTENT);
            setEditorKey((prev) => prev + 1);
          } else {
            // Handle error case - create error message
            // Clear temporary user message
            setTemporaryUserMessage(null);
            
            const errorMessage =
              "I'm sorry, I'm currently unable to process your request. Please try again later or contact support if the issue persists.";

            const errorMessageResponse = await fetch(
              `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.CREATE_MESSAGE}`,
              {
                method: "POST",
                headers: {
                  accept: "application/json",
                  "Content-Type": "application/json",
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                  content: errorMessage,
                  thread_id: selectedThread?.id,
                  message_type: "system",
                }),
              }
            );

            if (errorMessageResponse.ok) {
              const errorMessageData = await errorMessageResponse.json();
              setMessages((prevMessages) => [
                ...prevMessages,
                errorMessageData,
              ]);

              // Emit socket event for real-time updates
              if (channelId && selectedThread?.id) {
                try {
                  const eventData = {
                    type: SOCKET_KEYS.THREAD_MESSAGE_CREATED,
                    data: {
                      channelId: channelId,
                      threadId: selectedThread?.id,
                      messageId: errorMessageData.id,
                    },
                  };
                  socketService.sendSocketMessage(eventData);
                } catch (error) {
                  // Handle error silently
                }
              }
            }
            // Restore @Prism7 for next message when API returned non-success
            setNewComment(ASK_PRISM7_DEFAULT_CONTENT);
            setEditorKey((prev) => prev + 1);
          }
        } catch (aiError) {

          // Clear temporary user message
          setTemporaryUserMessage(null);

          // Handle AI error gracefully
          const errorMessage =
            "I'm sorry, I'm currently unable to process your request. Please try again later.";

          const errorMessageResponse = await fetch(
            `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.CREATE_MESSAGE}`,
            {
              method: "POST",
              headers: {
                accept: "application/json",
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                content: errorMessage,
                thread_id: selectedThread?.id,
                message_type: "system",
              }),
            }
          );

          if (errorMessageResponse.ok) {
            const errorMessageData = await errorMessageResponse.json();
            setMessages((prevMessages) => [...prevMessages, errorMessageData]);

            // Emit socket event for real-time updates
            if (channelId && selectedThread?.id) {
              try {
                const eventData = {
                  type: SOCKET_KEYS.THREAD_MESSAGE_CREATED,
                  data: {
                    channelId: channelId,
                    threadId: selectedThread?.id,
                    messageId: errorMessageData.id,
                  },
                };
                socketService.sendSocketMessage(eventData);
              } catch (error) {
                // Handle error silently
              }
            }
          }
          // Restore @Prism7 for next message when main_chat threw
          setNewComment(ASK_PRISM7_DEFAULT_CONTENT);
          setEditorKey((prev) => prev + 1);
        } finally {
          setIsAiThinking(false);
        }
      }
    } catch (error) {

      // Clear temporary user message
      setTemporaryUserMessage(null);

      let errorMessage = "Failed to post message";

      if (error instanceof Error) {
        if (error.message.includes("Failed to post message:")) {
          const statusMatch = error.message.match(
            /Failed to post message: (\d+)/
          );
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "400":
              errorMessage =
                "Invalid message content. Please check your input and try again.";
              break;
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage =
                "You do not have permission to post messages in this thread.";
              break;
            case "404":
              errorMessage = `${LanguageConstants.thread} not found.`;
              break;
            case "422":
              errorMessage = "Invalid message information provided.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to post message (Error ${statusCode}). Please try again.`;
          }
        } else if (error.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      showError(errorMessage);

      // Clear the editor on error; for Ask Prism7 restore @Prism7 so user can retry
      if (effectivePostingMethod === "ask-shay") {
        setNewComment(ASK_PRISM7_DEFAULT_CONTENT);
      } else {
        setNewComment("");
      }
      setIsEditorFocused(false);
      setEditorKey((prev) => prev + 1);

      // Clear any pending debounced updates
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
        contentUpdateTimeoutRef.current = null;
      }
    } finally {
      setIsPosting(false);
    }
  };

  // Function to process email HTML: unescape entity-escaped HTML, then make all links open in new tab (existing behavior)
  const processEmailHTML = (html: string): string => {
    if (!html || typeof html !== "string") return "";

    // If the API returned entity-escaped HTML (e.g. "&lt;style&gt;..."), decode once so we render HTML instead of raw text
    let processed = html;
    if (processed.startsWith("&lt;!") || processed.startsWith("&lt;html") || processed.startsWith("&lt;style") || processed.startsWith("&lt;head") || processed.startsWith("&lt;body")) {
      processed = processed
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&");
    }

    // Add target="_blank" to all anchor tags (existing behavior so existing things won't break)
    return processed.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
      if (attributes.includes("target=")) {
        return match.replace(
          /target\s*=\s*["'][^"']*["']/gi,
          'target="_blank"'
        );
      }
      return `<a ${attributes} target="_blank">`;
    });
  };

  const renderEmailView = (emailResponse: any, citations?: Citation[], isDraft?: boolean, usageMetrics?: DraftUsageMetricsData | null, borderPosition: "top" | "bottom" = "top") => {
    if (!emailResponse) return null;

    // Use emailResponse.originalMail for email body (e.g. calendar invites with HTML)
    const originalMail = emailResponse.originalMail;

    // Check if originalMail exists and is a string
    if (!originalMail || typeof originalMail !== "string") {
      return (
        <Box
          sx={{
            border: `1px solid ${muiTheme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: muiTheme.palette.background.paper,
            overflow: "hidden",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            p: 2,
            minHeight: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No email content available
          </Typography>
        </Box>
      );
    }

    // Run through processEmailHTML: unescape entity-escaped HTML and add target="_blank" to links (existing behavior)
    const processedHTML = processEmailHTML(originalMail);

    // Check if the email is HTML by looking for HTML tags
    const isHtmlEmail = (content: string) => {
      if (!content) return false;

      // Structural HTML patterns (doctype, html, head, style, body) - check FIRST so that
      // full HTML documents (e.g. calendar invites) are always treated as HTML even when
      // they contain angle-bracketed URLs or emails in the body (e.g. <https://meet.google.com/...>, <user@domain.com>)
      const structuralHtmlPatterns = [
        /<!DOCTYPE\s+[^>]*>/i,
        /<html[\s>]/i,
        /<head[\s>]/i,
        /<body[\s>]/i,
        /<style[\s>]/i,
      ];
      if (structuralHtmlPatterns.some((pattern) => pattern.test(content))) {
        return true;
      }

      // Exclude plain-text that only has URLs/emails in angle brackets (no real HTML structure)
      const nonHtmlPatterns = [
        /<https?:\/\/[^>]+>/i, // URLs in angle brackets like <https://example.com>
        /<[^>]*@[^>]*>/i, // Email addresses in angle brackets like <user@domain.com>
        /\[image:\s*[^\]]+\]/i, // Image references like [image: Facebook]
        /<[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}>/i, // Email addresses
      ];
      if (nonHtmlPatterns.some((pattern) => pattern.test(content))) {
        return false;
      }

      // Use regex patterns to detect actual HTML tags with attributes
      const htmlTagPatterns = [
        /<!DOCTYPE\s+[^>]*>/i,
        /<html[^>]*>/i,
        /<body[^>]*>/i,
        /<div[^>]*>/i,
        /<p[^>]*>/i, // Matches <p>, <p class="active">, etc.
        /<br[^>]*\/?>/i, // Matches <br>, <br/>, <br class="clear">, etc.
        /<span[^>]*>/i,
        /<table[^>]*>/i,
        /<tr[^>]*>/i,
        /<td[^>]*>/i,
        /<th[^>]*>/i,
        /<ul[^>]*>/i,
        /<li[^>]*>/i,
        /<a\s+href\s*=/i, // Only match <a> tags with href attribute
        /<img[^>]*>/i,
        /<strong[^>]*>/i,
        /<b[^>]*>/i,
        /<i[^>]*>/i,
        /<em[^>]*>/i,
        /<h[1-6][^>]*>/i, // Matches <h1>, <h2>, etc. with attributes
        /<form[^>]*>/i,
        /<input[^>]*>/i,
        /<button[^>]*>/i,
        /<script[^>]*>/i,
        /<style[^>]*>/i,
      ];

      // Check for HTML tag patterns but exclude email address patterns
      return htmlTagPatterns.some((pattern) => {
        const matches = content.match(pattern);
        if (!matches) return false;

        const match = matches[0];
        const matchIndex = content.indexOf(match);

        // For <p> and <br> tags, make sure it's not part of an email address
        if (match.match(/<p[^>]*>/i) || match.match(/<br[^>]*\/?>/i)) {
          // Check if there's an @ symbol nearby (indicating email address)
          const beforeTag = content.substring(
            Math.max(0, matchIndex - 50),
            matchIndex
          );
          const afterTag = content.substring(
            matchIndex + match.length,
            Math.min(content.length, matchIndex + match.length + 50)
          );
          const context = beforeTag + afterTag;

          // If there's an @ symbol in the context, it's likely an email address, not HTML
          if (context.includes("@")) {
            return false;
          }
        }

        return true;
      });
    };

    // Parse HTML to extract collapsible content sections
    const parseEmailContent = (content: string) => {
      if (!content) return { parts: [] };

      const parts: Array<{ type: 'regular' | 'collapsible'; content: string; isCollapsed?: boolean }> = [];
      
      // Find all collapsible div start tags
      const collapsibleStartRegex = /<div\s+[^>]*data-collapsible-content[^>]*(?:data-collapsed="([^"]*)")?[^>]*>/gi;
      const matches: Array<{ start: number; end: number; isCollapsed: boolean; content: string }> = [];
      
      let match;
      while ((match = collapsibleStartRegex.exec(content)) !== null) {
        const startIndex = match.index;
        const startTag = match[0];
        const isCollapsed = match[1] === 'true' || match[1] === undefined;
        
        // Find the matching closing tag by counting div tags
        let depth = 1;
        let currentIndex = startIndex + startTag.length;
        
        while (depth > 0 && currentIndex < content.length) {
          const nextOpen = content.indexOf('<div', currentIndex);
          const nextClose = content.indexOf('</div>', currentIndex);
          
          if (nextClose === -1) break;
          
          if (nextOpen !== -1 && nextOpen < nextClose) {
            depth++;
            currentIndex = nextOpen + 4;
          } else {
            depth--;
            if (depth === 0) {
              const collapsibleContent = content.substring(startIndex + startTag.length, nextClose);
              matches.push({
                start: startIndex,
                end: nextClose + 6, // +6 for '</div>'
                isCollapsed,
                content: collapsibleContent,
              });
              break;
            }
            currentIndex = nextClose + 6;
          }
        }
      }

      // Sort matches by start index
      matches.sort((a, b) => a.start - b.start);

      // Build parts array
      let lastIndex = 0;
      for (const collapsibleMatch of matches) {
        // Add regular content before the collapsible section
        if (collapsibleMatch.start > lastIndex) {
          const regularContent = content.substring(lastIndex, collapsibleMatch.start);
          if (regularContent.trim()) {
            parts.push({ type: 'regular', content: regularContent });
          }
        }

        // Add collapsible content
        parts.push({
          type: 'collapsible',
          content: collapsibleMatch.content,
          isCollapsed: collapsibleMatch.isCollapsed,
        });

        lastIndex = collapsibleMatch.end;
      }

      // Add remaining regular content
      if (lastIndex < content.length) {
        const regularContent = content.substring(lastIndex);
        if (regularContent.trim()) {
          parts.push({ type: 'regular', content: regularContent });
        }
      }

      // If no collapsible content found, return the whole content as regular
      if (parts.length === 0) {
        parts.push({ type: 'regular', content });
      }

      return { parts };
    };

    const emailContent = isHtmlEmail(processedHTML)
      ? processHtmlEmail(processedHTML)
      : LinkifyText(processedHTML, "", muiTheme);
    
    const { parts } = parseEmailContent(emailContent);

    return (
      <Box
        sx={{
          border: `1px solid ${muiTheme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: muiTheme.palette.background.paper,
          overflow: "hidden",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {/* Email Body - Only Original Content; copy puts plain text on clipboard (no HTML tags) */}
        <Box
          sx={{
            p: 2,
            backgroundColor: muiTheme.palette.background.paper,
            minHeight: "200px",
          }}
          className="email-content-scope"
          onCopy={(e) => {
            e.preventDefault();
            const plainText = window.getSelection()?.toString() ?? "";
            e.clipboardData.setData("text/plain", plainText);
          }}
        >
          {parts.map((part, index) => {
            if (part.type === 'collapsible') {
              return (
                <CollapsibleQuotedEmail
                  key={`collapsible-${index}`}
                  content={part.content}
                  isCollapsed={part.isCollapsed}
                />
              );
            }
            // Regular content: use shared typography styles and part content (already linkified/processed)
            return (
              <Typography
                key={`regular-${index}`}
                component="div"
                sx={{
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  "& p": { margin: "0.5rem 0", lineHeight: 1.6, fontSize: "0.875rem", fontFamily: "inherit" },
                  "& div, & span": { fontSize: "0.875rem", fontFamily: "inherit" },
                  "& ul, & ol": { margin: "0.5rem 0", paddingLeft: "1.5rem", fontSize: "0.875rem", fontFamily: "inherit" },
                  "& li": { margin: "0.25rem 0", fontSize: "0.875rem", fontFamily: "inherit" },
                  // Draft messages: omit pre-wrap to avoid excessive vertical whitespace from API newlines
                  ...(isHtmlEmail(processedHTML) || isDraft
                    ? {}
                    : { whiteSpace: "pre-wrap" }),
                }}
                dangerouslySetInnerHTML={{
                  __html: part.content,
                }}
              />
            );
          })}
          {/* Display citations (Sources) for Draft / Email Messages */}
          {citations && citations.length > 0 && (
            <CitationsDisplay citations={citations} borderPosition={borderPosition} />
          )}
          {/* Confidence score and token usage for Draft / Email Messages */}
          <DraftUsageMetrics usageMetrics={usageMetrics ?? undefined} borderPosition={borderPosition} />
          {/* <Box 
            sx={{ 
              '& *': { 
                maxWidth: '100% !important',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                fontFamily: 'inherit'
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block'
              },
              '& table': {
                maxWidth: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                wordWrap: 'break-word',
                wordBreak: 'break-word'
              },
              '& td, & th': {
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '200px'
              },
              '& p': {
                margin: '0 0 12px 0',
                lineHeight: 1.5,
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              },
              '& div': {
                lineHeight: 1.5,
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              },
              '& span': {
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              },
              '& a': {
                color: '#1976d2',
                textDecoration: 'underline',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              },
              '& a:hover': {
                textDecoration: 'none'
              },
              '& .zmail_signature_below': {
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: `1px solid ${muiTheme.palette.divider}`
              }
            }}
            dangerouslySetInnerHTML={{ __html: processedHTML }}
          /> */}
        </Box>

        {/* Email Footer with Reply/Forward Buttons */}
        {/* <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${muiTheme.palette.divider}`,
            backgroundColor: muiTheme.palette.background.default,
            display: "flex",
            gap: 1,
            justifyContent: "flex-start",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplyIcon />}
            disabled={isSelectedThreadArchived || isChannelArchived}
            onClick={() => handleReply(emailResponse)}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              "&.Mui-disabled": {
                color: muiTheme.palette.action.disabled,
                borderColor: muiTheme.palette.action.disabled,
              },
            }}
          >
            Reply
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplyAllIcon />}
            disabled={isSelectedThreadArchived || isChannelArchived}
            onClick={() => handleReplyAll(emailResponse)}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              "&.Mui-disabled": {
                color: muiTheme.palette.action.disabled,
                borderColor: muiTheme.palette.action.disabled,
              },
            }}
          >
            Reply All
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplyIcon sx={{ transform: "scaleX(-1)" }} />}
            disabled={isSelectedThreadArchived || isChannelArchived}
            onClick={() => handleForward(emailResponse)}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              "&.Mui-disabled": {
                color: muiTheme.palette.action.disabled,
                borderColor: muiTheme.palette.action.disabled,
              },
            }}
          >
            Forward
          </Button>
        </Box> */}
      </Box>
    );
  };

  const renderMessageWithMentions = (content: string, messageType?: string) => {
    // First clean the content to remove HTML tags and decode entities
    let cleanContent = content;

    // Replace mention HTML tags with plain text @mentions; prefer data-label for full name
    const mentionRegex =
      /<span[^>]*class="mention"[^>]*(?:data-label="([^"]*)")?[^>]*>@([^<]+)<\/span>/g;
    cleanContent = cleanContent.replace(mentionRegex, (_, label, inner) => `@${label || inner}`);

    // Preserve newlines by converting <br> and </p> tags to newlines before removing other HTML
    cleanContent = cleanContent.replace(/<br\s*\/?>/gi, "\n");
    cleanContent = cleanContent.replace(/<\/p>/gi, "\n");
    cleanContent = cleanContent.replace(/<p[^>]*>/gi, "");

    // Also handle any remaining HTML tags
    cleanContent = cleanContent.replace(/<[^>]*>/g, "");

    // Decode HTML entities (like &nbsp;)
    const textarea = document.createElement("textarea");
    textarea.innerHTML = cleanContent;
    cleanContent = textarea.value;

    // Clean up extra whitespace but preserve newlines
    cleanContent = cleanContent.replace(/[ \t]+/g, " "); // Replace multiple spaces/tabs with single space
    cleanContent = cleanContent.replace(/\n\s*\n\s*\n/g, "\n\n"); // Replace multiple newlines with double newlines
    cleanContent = cleanContent.trim();

    // For AI responses, render as markdown
    if (messageType === "system") {
      return (
        <Box
          sx={{
            fontSize: "0.875rem",
            fontFamily: "inherit",
            "& p": { margin: "0.5rem 0", lineHeight: 1.6, fontSize: "0.875rem", fontFamily: "inherit" },
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              margin: "1rem 0 0.5rem 0",
              fontWeight: 600,
              color: "text.primary",
            },
            "& h1": { fontSize: "1.5rem" },
            "& h2": { fontSize: "1.3rem" },
            "& h3": { fontSize: "1.1rem" },
            "& ul, & ol": { margin: "0.5rem 0", paddingLeft: "1.5rem", fontSize: "0.875rem", fontFamily: "inherit" },
            "& li": { margin: "0.25rem 0", fontSize: "0.875rem", fontFamily: "inherit" },
            "& blockquote": {
              borderLeft: `4px solid ${muiTheme.palette.divider}`,
              paddingLeft: "1rem",
              margin: "0.5rem 0",
              fontStyle: "italic",
              color: "text.secondary",
            },
            "& code": {
              backgroundColor: isDarkMode
                ? muiTheme.palette.grey[800]
                : muiTheme.palette.grey[100],
              padding: "2px 4px",
              borderRadius: "4px",
              fontFamily: "monospace",
              fontSize: "0.9em",
            },
            "& pre": {
              backgroundColor: isDarkMode
                ? muiTheme.palette.grey[800]
                : muiTheme.palette.grey[100],
              padding: "1rem",
              borderRadius: "8px",
              overflow: "auto",
              margin: "0.5rem 0",
            },
            "& pre code": {
              backgroundColor: "transparent",
              padding: 0,
            },
            "& table": {
              borderCollapse: "collapse",
              width: "100%",
              margin: "0.5rem 0",
            },
            "& th, & td": {
              border: `1px solid ${muiTheme.palette.divider}`,
              padding: "0.5rem",
              textAlign: "left",
            },
            "& th": {
              backgroundColor: isDarkMode
                ? muiTheme.palette.grey[800]
                : muiTheme.palette.grey[100],
              fontWeight: 600,
            },
            "& a": {
              color: muiTheme.palette.primary.main,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            },
            "& strong": { fontWeight: 600 },
            "& em": { fontStyle: "italic" },
            "& hr": {
              border: "none",
              borderTop: `1px solid ${muiTheme.palette.divider}`,
              margin: "1rem 0",
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <Typography variant="body2" component="p" sx={{ fontSize: "0.875rem", fontFamily: "inherit" }}>
                  {children}
                </Typography>
              ),
              h1: ({ children }) => (
                <Typography variant="h5" component="h1">
                  {children}
                </Typography>
              ),
              h2: ({ children }) => (
                <Typography variant="h6" component="h2">
                  {children}
                </Typography>
              ),
              h3: ({ children }) => (
                <Typography variant="subtitle1" component="h3" fontWeight={600}>
                  {children}
                </Typography>
              ),
              h4: ({ children }) => (
                <Typography variant="subtitle2" component="h4" fontWeight={600}>
                  {children}
                </Typography>
              ),
              h5: ({ children }) => (
                <Typography variant="body1" component="h5" fontWeight={600} sx={{ fontSize: "0.875rem", fontFamily: "inherit" }}>
                  {children}
                </Typography>
              ),
              h6: ({ children }) => (
                <Typography variant="body2" component="h6" fontWeight={600}>
                  {children}
                </Typography>
              ),
              ul: ({ children }) => (
                <Box component="ul" sx={{ m: 0, p: 0 }}>
                  {children}
                </Box>
              ),
              ol: ({ children }) => (
                <Box component="ol" sx={{ m: 0, p: 0 }}>
                  {children}
                </Box>
              ),
              li: ({ children }) => (
                <Typography 
                  variant="body2" 
                  component="li"
                  sx={{ fontSize: "0.875rem", fontFamily: "inherit" }}
                >
                  {children}
                </Typography>
              ),
              blockquote: ({ children }) => (
                <Box
                  component="blockquote"
                  sx={{
                    borderLeft: `4px solid ${muiTheme.palette.divider}`,
                    pl: 2,
                    my: 1,
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {children}
                </Box>
              ),
              code: ({ children, className }) => {
                const isInline = !className;
                return isInline ? (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      backgroundColor: isDarkMode
                        ? muiTheme.palette.grey[800]
                        : muiTheme.palette.grey[100],
                      px: 0.5,
                      borderRadius: 1,
                      fontFamily: "monospace",
                      fontSize: "0.9em",
                    }}
                  >
                    {children}
                  </Typography>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: isDarkMode
                        ? muiTheme.palette.grey[800]
                        : muiTheme.palette.grey[100],
                      p: 2,
                      borderRadius: 2,
                      overflow: "auto",
                      my: 1,
                    }}
                  >
                    <Typography
                      component="code"
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {children}
                    </Typography>
                  </Box>
                );
              },
              table: ({ children }) => (
                <Box
                  component="table"
                  sx={{
                    borderCollapse: "collapse",
                    width: "100%",
                    my: 1,
                  }}
                >
                  {children}
                </Box>
              ),
              th: ({ children }) => (
                <Box
                  component="th"
                  sx={{
                    border: `1px solid ${muiTheme.palette.divider}`,
                    p: 1,
                    textAlign: "left",
                    backgroundColor: isDarkMode
                      ? muiTheme.palette.grey[800]
                      : muiTheme.palette.grey[100],
                    fontWeight: 600,
                  }}
                >
                  {children}
                </Box>
              ),
              td: ({ children }) => (
                <Box
                  component="td"
                  sx={{
                    border: `1px solid ${muiTheme.palette.divider}`,
                    p: 1,
                    textAlign: "left",
                  }}
                >
                  {children}
                </Box>
              ),
              a: ({ children, href }) => (
                <Typography
                  component="a"
                  variant="body2"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: muiTheme.palette.primary.main,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {children}
                </Typography>
              ),
              strong: ({ children }) => (
                <Typography 
                  component="span" 
                  variant="body2" 
                  fontWeight={600}
                  sx={{ fontSize: "0.875rem", fontFamily: "inherit" }}
                >
                  {children}
                </Typography>
              ),
              em: ({ children }) => (
                <Typography 
                  component="span" 
                  variant="body2" 
                  fontStyle="italic"
                  sx={{ fontSize: "0.875rem", fontFamily: "inherit" }}
                >
                  {children}
                </Typography>
              ),
              hr: () => (
                <Box
                  sx={{
                    border: "none",
                    borderTop: `1px solid ${muiTheme.palette.divider}`,
                    my: 2,
                  }}
                />
              ),
            }}
          >
            {cleanContent}
          </ReactMarkdown>
        </Box>
      );
    }

    // For user messages: list-based parsing so only known names are highlighted (exact boundaries, no extra word)
    return (
      <Box
        sx={{
          whiteSpace: "pre-wrap",
          lineHeight: 1.6,
          fontSize: "0.875rem",
          fontFamily: "inherit",
        }}
      >
        <MentionText
          text={cleanContent}
          userObjects={mentionUserObjects}
          mentionStyle={{
            backgroundColor: muiTheme.palette.primary.light + "20",
            color: muiTheme.palette.primary.main,
            fontWeight: 700,
            padding: "2px 4px",
            borderRadius: "4px",
          }}
        />
      </Box>
    );
  };

  return (
    <Box
      data-testid="thread-detail-page-container"
      sx={{
        // Use 100% so we fill Layout's wrapper and avoid double scrollbar when subscription banner is shown
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top Header - Fixed */}
      <Box
        data-testid="thread-detail-page-header"
        sx={{
          px: 1.5,
          py: 0.8,
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
          backgroundColor: muiTheme.palette.background.paper,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left side - Breadcrumb and title */}
        <Box data-testid="header-left-section" sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            data-testid="header-back-button"
            size="small"
            onClick={handleBackClick}
            sx={{ color: "primary.main" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-testid="header-channel-info">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                data-testid="header-channel-name"
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "300px", // Limit channel name width
                  display: "block",
                }}
                title={channelData?.name || LanguageConstants.topic} // Show full name on hover
              >
                {/* Show channel name whenever we have it; only show "Loading..." when we have no channelData yet (avoids blink on category switch) */}
                {channelData?.name
                  ? capitalizeChannelName(channelData.name)
                  : channelLoading
                    ? "Loading..."
                    : LanguageConstants.topic}
              </Typography>
              {/* Channel type chip (Email/Message/Voice with provider icon) */}
              {(channelData || !channelLoading) && channelType && (
                <ChannelTypeChip
                  channelType={channelType}
                  emailProvider={(channelData as { provider?: string })?.provider}
                  messagingProvider={channelData?.messaging_provider}
                />
              )}
              {/* Email Sync Status Indicator */}
              <EmailSyncStatusIndicator
                channelData={channelData}
                channelId={channelId}
                onSyncComplete={reloadAllAPIs}
                hasThreads={getFilteredThreads().length > 0}
              />
              {/* Total Emails count next to sync chip (email channels only) */}
              {channelType === 'email' && selectedEmailCategory && (() => {
                const selectedMetric = emailMetricsForHeader.find((m) => m.key === selectedEmailCategory);
                const totalCount = selectedMetric?.count;
                return (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 500 }}>
                    Total Emails: <Box component="span" sx={{ fontWeight: 600, ml: 0.25 }} data-testid="header-label-total-count">{totalCount !== undefined ? totalCount : "—"}</Box>
                  </Typography>
                );
              })()}
            </Box>
            <Typography data-testid="header-workspace-name" variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {/* Prefer last-known workspace when channelData exists to avoid blink on category switch */}
              {channelData?.workspace_name != null
                ? (
                  <>
                    {LanguageConstants.group}: <Box component="span" sx={{ fontWeight: 600 }}>{channelData.workspace_name}</Box>
                  </>
                )
                : channelLoading
                  ? "Loading..."
                  : ""}
            </Typography>
          </Box>
        </Box>

        {/* Right side - Action buttons */}
        <Box data-testid="header-right-section" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {channelType !== 'message' && (
            <Tooltip title="Dashboard">
              <IconButton
                data-testid="header-analytics-button"
                size="small"
                onClick={handleDashboardToggle}
              >
                <AnalyticsIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Data Sources">
            <IconButton
              data-testid="header-data-sources-button"
              size="small"
              onClick={handleSourcePanelToggle}
            >
              <Badge
                badgeContent={embeddingStatus?.total_datasources || 0}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    top: -3,
                    fontSize: "0.7rem",
                    padding: "4px",
                    color: "#fff",
                  },
                }}
              >
                <SourceIcon
                  sx={{
                    color:
                      embeddingStatus && embeddingStatus.total_datasources > 0
                        ? muiTheme.palette.primary.main
                        : "inherit",
                  }}
                />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Settings">
            <IconButton data-testid="header-settings-button" size="small" onClick={handleSettingsClick}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Gap with background color */}
      <Box
        data-testid="header-divider"
        sx={{
          height: "6px",
          backgroundColor: isDarkMode
            ? muiTheme.palette.background.default
            : "#f5f5f5",
          width: "100%",
          flexShrink: 0,
        }}
      />

      {/* Main Content Area */}
      <Box
        ref={mainContentAreaRef}
        data-testid="main-content-area"
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          position: "relative",
          zIndex: isSourcePanelOpen ? 0 : 1,
          wordWrap: "break-word",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {/* Left Panel - Email Classifier - Fixed (Only for email channels) */}
        <EmailClassificationPanel
          channelId={channelId ?? null}
          channelType={channelType}
          selectedEmailCategory={selectedEmailCategory}
          onThreadDropOnCategory={channelType === 'email' && channelId ? handleThreadDropOnCategory : undefined}
          onCategoryChange={(category) => {
            // Skip if same category to avoid unnecessary clear/refetch
            if (category === selectedEmailCategory) return;
            // Signal URL threadId effect to ignore stale threadId until navigate clears it (avoids showing previous category thread in empty category)
            categorySwitchInProgressRef.current = true;
            setSelectedEmailCategory(category);
            // On category switch: clear old list, clear selection, reset pagination, and navigate to channel so
            // we load the latest list for the new category and then activate the first (latest) item and show its detail
            setApiThreads([]);
            setBulkSelectedThreadIds([]);
            setSelectedThread(null);
            clearTemporaryMessages();
            setCurrentPage(1);
            setHasMore(true);
            setThreadsLoading(true);
            pendingThreadSelectionRef.current = null;
            if (channelId) {
              const isEmailCh = channelType === "email";
              navigate(
                buildThreadsNavigate(channelId, undefined, searchParams, category, isEmailCh),
                { replace: true }
              );
            }
          }}
          onCategorySelect={(category, label) => {
            setSelectedSidebarLabel(label);
          }}
          onMetricsLoaded={setEmailMetricsForHeader}
          externalMetrics={emailMetricsForHeader}
          zIndex={isSourcePanelOpen ? 0 : 1}
        />

        {/* Center Panel - Thread List — width controlled by drag on thread-list-divider */}
        <Box
          data-testid="thread-list-panel"
          sx={{
            flex: `0 0 ${threadListPanelWidth}px`,
            width: threadListPanelWidth,
            minWidth: THREAD_LIST_PANEL_MIN_PX,
            maxWidth: THREAD_LIST_PANEL_MAX_PX,
            overflow: "auto",
            position: "relative",
            zIndex: isSourcePanelOpen ? 0 : 1,
            backgroundColor: muiTheme.palette.background.paper,
            borderRight: `1px solid #f0f0f0`,
          }}
        >
          <ThreadList
            threads={showArchivedThreads ? sortThreadsByLastActivity(apiThreads) : getFilteredThreads()}
            selectedThreadId={selectedThread?.id || null}
            loading={loading}
            threadsLoading={threadsLoading}
            showArchivedThreads={showArchivedThreads}
            isViewingArchivedThreads={
              showArchivedThreads ||
              selectedEmailCategory === "archived" ||
              emailFilters.is_archived === true
            }
            onThreadSelect={handleThreadSelect}
            onNewThread={handleAddThreadClick}
            onComposeClick={handleComposeOpen}
            onThreadMenuOpen={handleThreadMenuOpen}
            onShowArchivedChange={(showArchived) => {
              setShowArchivedThreads(showArchived);
            }}
            onThreadEdit={handleEditThreadOpen}
            onThreadArchive={(thread) => {
              if (thread.is_archived) {
                handleUnarchiveThread(thread);
              } else {
                handleArchiveConfirmOpen(thread);
              }
            }}
            onThreadDelete={handleDeleteConfirmOpen}
            onThreadMove={(thread) => {
              setThreadToMove(thread);
              setIsMoveThreadModalOpen(true);
            }}
            isChannelArchived={isChannelArchived}
            channelType={channelType}
            messagingProvider={channelData?.messaging_provider}
            channelId={channelId}
            // Pagination props
            currentPage={currentPage}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMoreThreads}
            // Filter props
            onFilterClick={handleFilterClick}
            activeFilterCount={getActiveFilterCount()}
            // Search props
            onSearch={handleSearch}
            searchText={emailFilters.body}
            bulkSelectedIds={bulkSelectedThreadIds}
            onBulkToggle={isChannelAdmin ? handleBulkToggle : undefined}
            onBulkSelectAllVisible={
              isChannelAdmin ? handleBulkSelectAllVisible : undefined
            }
            bulkSelectAllCheckbox={bulkSelectAllCheckbox}
          />
        </Box>

        {/* Draggable divider: grip icon shows thread list vs message pane can be resized */}
        <Tooltip title="Drag to widen or narrow the thread list" placement="left" enterDelay={300}>
          <Box
            data-testid="thread-list-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize thread list"
            onPointerDown={handleThreadListResizePointerDown}
            sx={{
              width: "10px",
              flexShrink: 0,
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "col-resize",
              touchAction: "none",
              userSelect: "none",
              backgroundColor: isDarkMode
                ? muiTheme.palette.background.default
                : "#f5f5f5",
              borderLeft: `1px solid ${isDarkMode ? muiTheme.palette.divider : "#e0e0e0"}`,
              borderRight: `1px solid ${isDarkMode ? muiTheme.palette.divider : "#e0e0e0"}`,
              "&:hover": {
                backgroundColor: isDarkMode
                  ? alpha(muiTheme.palette.primary.main, 0.2)
                  : "#e8e8e8",
                "& .thread-list-divider-grip-bar": {
                  bgcolor: muiTheme.palette.primary.main,
                  opacity: 1,
                },
              },
            }}
          >
            {/* Two parallel vertical lines — standard horizontal resize affordance */}
            <Box
              className="thread-list-divider-grip"
              aria-hidden
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "3px",
                pointerEvents: "none",
              }}
            >
              <Box
                className="thread-list-divider-grip-bar"
                sx={{
                  width: "2px",
                  height: "18px",
                  borderRadius: "1px",
                  bgcolor: isDarkMode
                    ? muiTheme.palette.grey[400]
                    : muiTheme.palette.grey[500],
                  opacity: isDarkMode ? 0.85 : 0.55,
                }}
              />
              <Box
                className="thread-list-divider-grip-bar"
                sx={{
                  width: "2px",
                  height: "18px",
                  borderRadius: "1px",
                  bgcolor: isDarkMode
                    ? muiTheme.palette.grey[400]
                    : muiTheme.palette.grey[500],
                  opacity: isDarkMode ? 0.85 : 0.55,
                }}
              />
            </Box>
          </Box>
        </Tooltip>

        {/* Right Panel - Content Area with Fixed Message Box */}
        <Box
          data-testid="thread-detail-panel"
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            zIndex: isSourcePanelOpen ? 0 : 1,
            wordWrap: "break-word",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {isComposeOpen ? (
            channelType === 'email' ? (
              <ComposeBox
                data-testid="compose-box"
                isOpen={isComposeOpen}
                onClose={handleComposeClose}
                onSend={handleComposeSend}
                isReplyOrForward={isReplyOrForward}
                isForward={isForward}
                channelName={channelData?.name}
                emailThreadId={selectedThread?.email_thread_id || undefined}
                channelType={channelType}
                channelId={channelId}
                threadId={selectedThread?.id}
                initialData={
                  composeData
                    ? {
                        ...composeData,
                        gmail_thread_id: composeData.gmail_thread_id || undefined,
                        content: draftContent || composeData.content,
                      }
                    : draftContent
                    ? {
                        content: draftContent,
                      }
                    : undefined
                }
                emailResponse={currentEmailResponse}
                onGenerateDraft={handleGenerateDraftForCompose}
                isGeneratingDraft={isGeneratingDraftForCompose}
                draftMessageId={draftMessageId}
                draftCitations={draftCitations}
                draftUsageMetrics={draftUsageMetrics}
              />
            ) : null
          ) : isChannelAdmin && bulkSelectedThreadIds.length > 0 ? (
            <Box
              data-testid="bulk-thread-actions-panel"
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                gap: 3,
                backgroundColor: muiTheme.palette.background.paper,
              }}
            >
              <Typography variant="h6" color="text.primary">
                {bulkSelectedThreadIds.length} Thread
                {bulkSelectedThreadIds.length === 1 ? "" : "s"} selected
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                {threadsToBulkArchiveCount > 0 && (
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={isBulkArchiving}
                    onClick={() => setIsBulkArchiveConfirmOpen(true)}
                  >
                    Archive
                  </Button>
                )}
                {threadsToBulkUnarchiveCount > 0 && (
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={isBulkUnarchiving}
                    onClick={() => setIsBulkUnarchiveConfirmOpen(true)}
                  >
                    Unarchive
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  disabled={isBulkDeleting}
                  onClick={() => setIsBulkDeleteConfirmOpen(true)}
                >
                  Delete
                </Button>
                <Button variant="outlined" onClick={clearBulkSelection}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : selectedThread?.id ? (
            <>
              {/* Section 1: Thread Name/Email Subject Box */}
              <Box
                data-testid="thread-header-box"
                sx={{
                  backgroundColor: muiTheme.palette.background.paper,
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: "1fr 340px",
                  alignItems: "center",
                  minHeight: "48px",
                  boxSizing: "border-box",
                  gap: "12px",
                  padding: "0 8px",
                  // When spam notice shows, separate the white subject row from the gutter strip below
                  borderBottom:
                    channelType === "email" &&
                    selectedEmailCategory === "spam" &&
                    spamFolderCountdown !== null
                      ? `1px solid ${muiTheme.palette.divider}`
                      : "none",
                }}
              >
                {/* First Column - Thread Title */}
                <Box
                  data-testid="thread-title-section"
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    boxSizing: "border-box",
                  }}
                >
                  {(() => {
                    // const selectedThread = apiThreads.find(t => t.id === selectedThread?.id);
                    return selectedThread ? (
                      <Typography
                        data-testid="thread-title"
                        variant="h6"
                        sx={{
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          maxWidth: "90%",
                          margin: 0,
                          lineHeight: 1.2,
                          display: "block",
                        }}
                        title={selectedThread?.title}
                      >
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.940rem", fontWeight: 600 }}
                        >
                          {channelType === 'message' ? 'Thread:' : 'Subject:'}
                        </Typography>
                        {/* Email: show label as chip before subject (same light bg + dark text as message chips) */}
                        {channelType === 'email' && selectedSidebarLabel && (() => {
                          const categoryKey = getBaseClassificationKey(selectedEmailCategory ?? '').toLowerCase().replace(/\s+/g, '_');
                          return (
                            <Chip
                              label={selectedSidebarLabel}
                              size="small"
                              sx={{
                                ml: 0.5,
                                mr: 0.5,
                                height: 20,
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                border: "none",
                                borderRadius: "4px",
                                backgroundColor: EMAIL_LABEL_CHIP_BG[categoryKey] ?? muiTheme.palette.grey[300],
                                color: EMAIL_LABEL_CHIP_TEXT[categoryKey] ?? muiTheme.palette.grey[800],
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          );
                        })()}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.940rem",
                            fontWeight: 500,
                            ml: 0.5,
                          }}
                        >
                          {selectedThread?.title
                            ? capitalizeFirstLetter(selectedThread?.title)
                            : ""}
                        </Typography>
                      </Typography>
                    ) : null;
                  })()}
                </Box>

                {/* Second Column - Ask Prism7, Task and Approval Icons */}
                <Box
                  data-testid="thread-actions-section"
                  sx={{
                    width: "340px",
                    minWidth: "340px",
                    maxWidth: "340px",
                    overflow: "visible",
                    boxSizing: "border-box",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  {/* Ask Prism7 - validate subscription before opening drawer (same rules as posting a message) */}
                  <AskPrism7Button
                    hasEmbeddings={hasCompletedEmbeddings(embeddingStatus)}
                    loading={askPrism7Validating}
                    disabled={
                      isSelectedThreadArchived ||
                      isChannelArchived ||
                      !hasCompletedEmbeddings(embeddingStatus) ||
                      askPrism7Validating
                    }
                    onOpen={async () => {
                      setAskPrism7Validating(true);
                      try {
                        // First check: show ChannelRestrictionModal when API denies messages (fail-open on thrown errors)
                        try {
                          const validationData = await validateSubscription(
                            channelId ? { channelId } : {}
                          );
                          if (validationData && !validationData.canCreateMessage) {
                            setOpenMessageRestrictionModal(true);
                            return;
                          }
                        } catch (error) {
                          console.error("Error validating subscription:", error);
                        }
                        // Second check when subscription UI is disabled: message limit dialog
                        if (DISABLE_SUBSCRIPTION && channelId) {
                          const validationData = await validateSubscription({ channelId });
                          if (!validationData?.canCreateMessage) {
                            setMessageLimitModalOpen(true);
                            return;
                          }
                        }
                        setPostingMethod("ask-shay");
                        setNewComment(ASK_PRISM7_DEFAULT_CONTENT);
                        setIsAskPrism7PanelOpen(true);
                      } finally {
                        setAskPrism7Validating(false);
                      }
                    }}
                  />
                  <TaskPanel
                    threadId={selectedThread?.id}
                    channelId={channelId}
                    selectedMessageId={selectedMessageId}
                    messages={messages}
                    onTaskUpdate={() => {
                      // Callback for when tasks are updated
                    }}
                    onSelectedMessageIdChange={(messageId) => setSelectedMessageId(messageId)}
                    open={isTaskPanelOpen}
                    onOpenChange={setIsTaskPanelOpen}
                  />

                  <ApprovalPanelButton
                    threadId={selectedThread?.id}
                    channelId={channelId}
                    selectedMessageId={selectedMessageId}
                    messages={messages}
                    onApprovalUpdate={() => {
                      // Callback for when approvals are updated
                    }}
                    open={isApprovalPanelOpen}
                    onOpenChange={setIsApprovalPanelOpen}
                  />
                </Box>
              </Box>

              {/* Spam folder: own strip below subject (gutter bg), not part of the white subject row */}
              {channelType === "email" &&
                selectedEmailCategory === "spam" &&
                spamFolderCountdown !== null && (
                  <Box
                    data-testid="spam-folder-notice-region"
                    sx={{
                      width: "100%",
                      flexShrink: 0,
                      boxSizing: "border-box",
                      backgroundColor: isDarkMode
                        ? muiTheme.palette.background.default
                        : "#f5f5f5",
                      pt: { xs: 0.5, sm: 0.75 },
                      pb: { xs: 0.5, sm: 0.5 },
                      px: 0,
                    }}
                  >
                  <Box
                    component="aside"
                    data-testid="spam-folder-notice-banner"
                    aria-label={`${STANDARD_CATEGORY_LABELS.spam} folder notice`}
                    sx={(theme) => ({
                      width: "100%",
                      flexShrink: 0,
                      boxSizing: "border-box",
                      px: 0,
                      py: 0,
                      borderRadius: 0,
                      border: `1px solid ${alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.55 : 0.5)}`,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.info.main, 0.34)
                          : "#dbeafe",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? `0 2px 6px ${alpha(theme.palette.common.black, 0.3)}`
                          : `0 1px 3px ${alpha(theme.palette.info.main, 0.3)}`,
                    })}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        gap: { xs: 0.5, sm: 0.75 },
                        // Match thread-header-box horizontal padding so width aligns with subject row content
                        px: "8px",
                        py: { xs: "6px", sm: "7px" },
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ color: "info.main", flexShrink: 0, mt: 0.15, fontSize: "1rem" }}
                        aria-hidden
                      />
                      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color="text.primary"
                          sx={{ mb: 0.15, lineHeight: 1.2, fontSize: "0.8125rem" }}
                        >
                          {STANDARD_CATEGORY_LABELS.spam} folder
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ lineHeight: 1.35, fontSize: "0.75rem" }}
                        >
                          {SPAM_FOLDER_NOTICE}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.15,
                          flexShrink: 0,
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label="Close notice"
                          onClick={() => setSpamFolderCountdown(null)}
                          sx={{
                            padding: "2px",
                            color: "text.secondary",
                            "&:hover": {
                              bgcolor: alpha(muiTheme.palette.common.black, 0.07),
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.4,
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                            sx={{ fontSize: "0.625rem", lineHeight: 1.15 }}
                          >
                            {SPAM_FOLDER_NOTICE_COUNTDOWN_LABEL}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                            sx={{
                              fontWeight: 600,
                              fontVariantNumeric: "tabular-nums",
                              fontSize: "0.625rem",
                              lineHeight: 1.15,
                            }}
                            aria-live="polite"
                            aria-label={`${SPAM_FOLDER_NOTICE_COUNTDOWN_LABEL} ${spamFolderCountdown ?? 0} seconds`}
                          >
                            {spamFolderCountdown ?? ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                            sx={{ fontSize: "0.625rem", lineHeight: 1.15 }}
                          >
                            s
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  </Box>
                )}

              {/* Gap between Section 1 and Section 2 */}
              <Box
                data-testid="thread-header-divider"
                sx={{
                  height: "6px",
                  backgroundColor: isDarkMode
                    ? muiTheme.palette.background.default
                    : "#f5f5f5",
                  flexShrink: 0,
                }}
              />

              {/* Section 2: Messages Content Area */}
              <Box
                data-testid="messages-container"
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflow: "auto",
                  p: 1,
                  backgroundColor: muiTheme.palette.background.paper,
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {messagesLoading ? (
                  <Box
                    data-testid="messages-loading"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      Loading messages...
                    </Typography>
                  </Box>
                ) : messagesError ? (
                  <Alert data-testid="messages-error" severity="error" sx={{ mb: 2 }}>
                    {messagesError}
                  </Alert>
                ) : messages.length === 0 && !temporaryUserMessage && !isAiThinking && !isGeneratingDraft ? (
                  <Box
                    data-testid="messages-empty"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Loading more messages indicator */}
                    {messagesLoadingMore && (
                      <Box
                        data-testid="messages-loading-more"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 2,
                          borderBottom: `1px solid ${muiTheme.palette.divider}`,
                        }}
                      >
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading more messages...
                        </Typography>
                      </Box>
                    )}

                    {/* Test Markdown Button - Remove in production */}

                    {/* Chat Messages */}
                    <ThreadMessageList
                      messages={messages}
                      renderMessage={(() => {
                        const f = (message: ApiMessage) => {
                        const isEdited = message.updated_at !== message.created_at;
                        const displayTimestamp = isEdited ? message.updated_at : message.created_at;
                        return (
                            <Box
                              data-testid={`message-${message.id}`}
                              onMouseEnter={() => setHoveredMessageId(message.id)}
                              onMouseLeave={() => setHoveredMessageId(null)}
                              sx={{
                                mb: 2,
                                position: "relative",
                              }}
                            >
                          {/* Message bubble */}
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              backgroundColor:
                                message.message_type === "system"
                                  ? isDarkMode
                                    ? muiTheme.palette.grey[800]
                                    : muiTheme.palette.grey[100]
                                  : isDarkMode
                                  ? muiTheme.palette.grey[700]
                                  : muiTheme.palette.grey[50],
                              border: `1px solid ${muiTheme.palette.divider}`,
                              position: "relative",
                              "&:hover": {
                                backgroundColor:
                                  message.message_type === "system"
                                    ? isDarkMode
                                      ? muiTheme.palette.grey[700]
                                      : muiTheme.palette.grey[200]
                                    : isDarkMode
                                    ? muiTheme.palette.grey[600]
                                    : muiTheme.palette.grey[100],
                              },
                            }}
                          >
                            {/* Message header with avatar and info */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor:
                                    message.message_type === "system"
                                      ? "#2196f3"
                                      : message.emailResponse
                                      ? getAvatarColor(
                                          message.emailResponse.from || ""
                                        )
                                      : "#4caf50",
                                  fontSize: "0.875rem",
                                  fontWeight: "bold",
                                  mt: 0.5, // Small top margin to align with first line
                                }}
                              >
                                {message.message_type === "system"
                                  ? "S"
                                  : (message.emailResponse
                                      ? getFirstAlphabeticChar(
                                          message.emailResponse.from || ""
                                        )
                                      : (() => {
                                          const label =
                                            message.userBean?.full_name?.trim() ||
                                            message.userBean?.name?.trim() ||
                                            "";
                                          const ch = label.charAt(0) || message.userBean?.email?.charAt(0) || "U";
                                          return ch.toUpperCase();
                                        })())}
                              </Avatar>
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {message.emailResponse
                                      ? message.emailResponse.from?.replace(
                                          /"/g,
                                          ""
                                        )
                                      : message.message_type === "system"
                                      ? "Prism7"
                                      : displayNameFromUserBean(message.userBean) || "User"}
                                  </Typography>
                                  {message.emailResponse ? (
                                    <>
                                    <Chip
                                      label={
                                        message.emailResponse.emailFolder ||
                                        "Email"
                                      }
                                      size="small"
                                      sx={(() => {
                                        const folderKey = EMAIL_LABEL_DISPLAY_TO_KEY[message.emailResponse?.emailFolder ?? ""] ?? "";
                                        return {
                                          height: 20,
                                          fontSize: "0.75rem",
                                          borderRadius: "4px",
                                          backgroundColor: folderKey ? EMAIL_LABEL_CHIP_BG[folderKey] : (isDarkMode ? muiTheme.palette.grey[600] : "#e3f2fd"),
                                          color: folderKey ? EMAIL_LABEL_CHIP_TEXT[folderKey] : (isDarkMode ? muiTheme.palette.grey[100] : "#1976d2"),
                                          border: "none",
                                        };
                                      })()}
                                    />
                                    {/* Display classification label with # icon to match classification panel sub-label style */}
                                    {message.emailResponse.label && (
                                      <Chip
                                        icon={<Box component="span" sx={{ fontSize: "0.75rem", fontWeight: 600, opacity: 0.9, lineHeight: 1 }}>#</Box>}
                                        label={message.emailResponse.label}
                                        size="small"
                                        sx={(() => {
                                          const labelKey = EMAIL_LABEL_DISPLAY_TO_KEY[message.emailResponse?.label ?? ""] ?? "";
                                          return {
                                            height: 20,
                                            fontSize: "0.75rem",
                                            backgroundColor: labelKey ? EMAIL_LABEL_CHIP_BG[labelKey] : (isDarkMode ? muiTheme.palette.grey[600] : muiTheme.palette.grey[300]),
                                            color: labelKey ? EMAIL_LABEL_CHIP_TEXT[labelKey] : (isDarkMode ? muiTheme.palette.grey[100] : muiTheme.palette.grey[800]),
                                            border: "none",
                                            ml: 0.5,
                                            fontWeight: 500,
                                            "& .MuiChip-icon": {
                                              marginLeft: "4px",
                                              marginRight: "-2px",
                                              fontSize: "0.75rem",
                                              color: "inherit",
                                            },
                                            "& .MuiChip-label": {
                                              paddingLeft: "4px",
                                              paddingRight: "6px",
                                            },
                                          };
                                        })()}
                                      />
                                    )}
                                    {(message.emailResponse.emailFolder === "Spam" || 
                                      message.emailResponse.emailFolder === "No Reply") && (
                                      <Button
                                        size="small"
                                        variant={isDarkMode ? "contained" : "outlined"}
                                        startIcon={movingToClassification === "inbox" ? <CircularProgress size={12} /> : <InboxIcon />}
                                        onClick={() => handleMoveToClassification("inbox")}
                                        disabled={movingToClassification !== null}
                                        sx={{
                                          height: 20,
                                          fontSize: "0.65rem",
                                          minWidth: "fit-content",
                                          px: 2,
                                          py: 0,
                                          ml: 1,
                                          whiteSpace: "nowrap",
                                          flexShrink: 0,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          ...(isDarkMode
                                            ? {
                                                backgroundColor: "#e3f2fd",
                                                color: "#1976d2",
                                                border: "none",
                                                "&:hover": {
                                                  backgroundColor: "#bbdefb",
                                                  border: "none",
                                                },
                                              }
                                            : {
                                                borderColor: muiTheme.palette.primary.main,
                                                color: muiTheme.palette.primary.main,
                                                "&:hover": {
                                                  backgroundColor: muiTheme.palette.primary.main + "15",
                                                  borderColor: muiTheme.palette.primary.main,
                                                },
                                              }),
                                          "& .MuiButton-startIcon": {
                                            marginRight: "4px",
                                            marginLeft: 0,
                                            display: "flex",
                                            alignItems: "center",
                                          },
                                        }}
                                      >
                                        {movingToClassification === "inbox" ? "Moving..." : "Move to Inbox"}
                                      </Button>
                                    )}
                                    </>

                                  ) : (
                                    <Chip
                                      label={
                                        message.message_type === "system"
                                          ? "AI Response"
                                          : message.is_ai_processed &&
                                            message.message_type === "email"
                                          ? "Draft Message"
                                          : "User Message"
                                      }
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.75rem",
                                        backgroundColor:
                                          message.message_type === "system"
                                            ? "#e3f2fd"
                                            : "#e8f5e8",
                                        color:
                                          message.message_type === "system"
                                            ? "#1976d2"
                                            : "#2e7d32",
                                        border: "none",
                                      }}
                                    />
                                  )}

                                </Box>
                                <Tooltip
                                  title={formatDetailedTooltip(
                                    displayTimestamp
                                  )}
                                  placement="left"
                                  arrow
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {formatRelativeTime(displayTimestamp)}
                                    {isEdited && (
                                      <>
                                        <FiberManualRecordIcon
                                          sx={{
                                            width: 6,
                                            height: 6,
                                            mx: 0.5,
                                            verticalAlign: 'middle',
                                          }}
                                        />
                                        Edited
                                      </>
                                    )}
                                  </Typography>
                                </Tooltip>
                                {/* Email metadata - Only show for email channels */}
                                {channelType === 'email' && message.emailResponse && (
                                  <>
                                    {message.emailResponse.toUsers !=
                                      message.emailResponse.linkedEmail && (
                                      <CollapsibleField
                                        label="To"
                                        value={message.emailResponse.toUsers}
                                      />
                                    )}
                                    {message.emailResponse.ccUsers && (
                                      <CollapsibleField
                                        label="Cc"
                                        value={message.emailResponse.ccUsers}
                                      />
                                    )}
                                    {message.emailResponse.bccUsers && (
                                      <CollapsibleField
                                        label="Bcc"
                                        value={message.emailResponse.bccUsers}
                                      />
                                    )}
                                  </>
                                )}
                              </Box>
                              <MessageActionsMenu
                                message={message}
                                channelType={channelType}
                                isHovered={hoveredMessageId === message.id}
                                isSelectedThreadArchived={isSelectedThreadArchived}
                                isChannelArchived={isChannelArchived}
                                onReply={handleReply}
                                onReplyAll={handleReplyAll}
                                onForward={handleForward}
                                onEditAndSend={handleEditAndSend}
                                onDeleteDraft={(draftMessage) => {
                                  void handleDeleteDraft(draftMessage);
                                }}
                                isDeletingDraft={!!deletingDraftById[String(message.id)]}
                                onEditMessage={handleEditMessage}
                                onDelete={(msg) => handleDeleteMessage(msg as ApiMessage)}
                                onDeleteMessage={(msg) => handleDeleteMessage(msg as ApiMessage)}
                                messages={messages}
                                threadClassification={threadClassificationEmail}
                                onMoveToClassification={handleMoveToClassification}
                                movingToClassification={movingToClassification}
                              />
                            </Box>

                            {/* Message content */}
                            {channelType === 'email' && message.emailResponse ? (
                              (() => {
                                // For draft we just generated, use in-memory citations/metrics if API hasn't returned them yet
                                const isCurrentDraft = message.id === draftMessageId;
                                // Pass citations and usage_metrics whenever present (sent emails can have usage_metrics with is_ai_processed false)
                                const citationsToShow =
                                  (isCurrentDraft ? draftCitations : null) ??
                                  message.citations ??
                                  message.emailResponse?.citations;
                                const usageMetricsToShow =
                                  (isCurrentDraft ? draftUsageMetrics : null) ??
                                  message.usage_metrics ??
                                  message.emailResponse?.usage_metrics;
                                return renderEmailView(
                                  message.emailResponse,
                                  Array.isArray(citationsToShow) ? citationsToShow : undefined,
                                  !!(message.is_ai_processed && message.message_type === "email"),
                                  usageMetricsToShow ?? undefined,
                                  "top"
                                );
                              })()
                            ) : (
                              <>
                                <Typography
                                  variant="body2"
                                  component="div"
                                  sx={{ 
                                    lineHeight: 1.6, 
                                    color: "text.primary",
                                    fontSize: "0.875rem",
                                    fontFamily: "inherit"
                                  }}
                                  onCopy={(e) => {
                                    e.preventDefault();
                                    const plainText = window.getSelection()?.toString() ?? "";
                                    e.clipboardData.setData("text/plain", plainText);
                                  }}
                                >
                                  {(message.message_type === "system" || message.message_type === "user")
                                    ? renderMessageWithMentions(message.content ?? "", message.message_type)
                                    : (
                                      <Box
                                        sx={{
                                          lineHeight: 1.6,
                                          fontSize: "0.875rem",
                                          fontFamily: "inherit",
                                          // Markdown HTML has literal \n between <p> tags; pre-wrap would render those as extra line breaks on top of .p + p margin (showing 3+ lines for one blank). Use normal so only CSS controls spacing.
                                          whiteSpace: hasMarkdown(message.content ?? "") ? "normal" : "pre-wrap",
                                        }}
                                        dangerouslySetInnerHTML={{
                                          __html: LinkifyText(
                                            message.content ?? "",
                                            "",
                                            muiTheme
                                          ),
                                        }}
                                      />
                                    )
                                  }
                                </Typography>
                                {/* Display citations (Sources) for @prism7 AI and email messages when present */}
                                {(message.message_type === "system" || message.message_type === "email") &&
                                  (() => {
                                    const citationsFromMessage =
                                      message.citations ?? message.emailResponse?.citations;
                                    const hasCitations =
                                      Array.isArray(citationsFromMessage) && citationsFromMessage.length > 0;
                                    return hasCitations ? (
                                      <CitationsDisplay citations={citationsFromMessage} borderPosition="top" />
                                    ) : null;
                                  })()}
                                {/* Display usage metrics for @prism7 AI and email messages when present (e.g. sent emails with usage_metrics) */}
                                {(message.message_type === "system" || message.message_type === "email") && (
                                  <DraftUsageMetrics
                                    usageMetrics={
                                      message.id === draftMessageId
                                        ? draftUsageMetrics ?? message.usage_metrics ?? message.emailResponse?.usage_metrics
                                        : message.usage_metrics ?? message.emailResponse?.usage_metrics
                                    }
                                    borderPosition="top"
                                  />
                                )}
                              </>
                            )}

                            {/* Attachments */}
                            {message.attachments && message.attachments.length > 0 && (
                              <EmailAttachments attachments={message.attachments} />
                            )}
                          </Box>
                        </Box>
                        );
                        };
                        renderOneMessageRef.current = f;
                        return f;
                      })()}
                      temporaryUserMessage={temporaryUserMessage}
                      isAiThinking={isAiThinking}
                      isGeneratingDraft={isGeneratingDraft}
                      user={user}
                      messagesEndRef={messagesEndRef}
                    />
                  </>
                )}
              </Box>


              {/* Gap between Section 2 and Section 3 */}
              <Box
                sx={{
                  height: "6px",
                  backgroundColor: isDarkMode
                    ? muiTheme.palette.background.default
                    : "#f5f5f5",
                  flexShrink: 0,
                }}
              />

              {/* Section 3: Bottom Action Buttons or Chat Input */}
              <Box
                sx={{
                  backgroundColor: muiTheme.palette.background.paper,
                  flexShrink: 0,
                  p: 0.5,
                  borderRadius: 2,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {/* Archived Channel/Thread Notice */}
                {(isChannelArchived || isSelectedThreadArchived) && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: muiTheme.palette.warning.light + "20",
                      border: `1px solid ${muiTheme.palette.warning.main}`,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ArchiveIcon
                      sx={{
                        color: muiTheme.palette.warning.main,
                        fontSize: "1.2rem",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: muiTheme.palette.warning.dark,
                        fontWeight: "medium",
                      }}
                    >
                      {isChannelArchived
                        ? "This channel is archived. You cannot post new messages."
                        : "This thread is archived. You cannot post new messages."}
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons - For all channels; message input only for internal-reply/channel-reply (ask-shay uses panel) */}
                {!isMessageBoxVisible || (postingMethod !== "internal-reply" && postingMethod !== "channel-reply") ? (
                  <Box
                    data-testid="thread-action-buttons"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                      p: 0.2,
                      width: "100%",
                      boxSizing: "border-box",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Email-specific buttons: Reply, Reply All, Forward - only for email channels */}
                    {channelType === 'email' && (
                      <>
                        <Button
                          data-testid="thread-reply-button"
                          variant="outlined"
                          size="small"
                          startIcon={<ReplyIcon />}
                          onClick={handleCommonReply}
                          sx={{
                            color: muiTheme.palette.primary.main,
                            borderColor: muiTheme.palette.primary.main,
                            backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[800] : "white",
                            fontSize: "0.75rem",
                            padding: "6px 12px",
                            minWidth: "auto",
                            maxWidth: "fit-content",
                            flex: "0 0 auto",
                            "&:hover": {
                              backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[700] : muiTheme.palette.primary.main + "08",
                              borderColor: muiTheme.palette.primary.main,
                            },
                            "&.Mui-disabled": {
                              color: muiTheme.palette.text.disabled,
                              borderColor: muiTheme.palette.action.disabled,
                              backgroundColor:
                                muiTheme.palette.action.disabledBackground,
                            },
                          }}
                        >
                          Reply
                        </Button>
                        {showReplyAllButton && (
                        <Button
                          data-testid="thread-reply-all-button"
                          variant="outlined"
                          size="small"
                          startIcon={<ReplyAllIcon />}
                          onClick={handleCommonReplyAll}
                          disabled={isSelectedThreadArchived || isChannelArchived}
                          sx={{
                            color: muiTheme.palette.primary.main,
                            borderColor: muiTheme.palette.primary.main,
                            backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[800] : "white",
                            fontSize: "0.75rem",
                            padding: "6px 12px",
                            minWidth: "auto",
                            maxWidth: "fit-content",
                            flex: "0 0 auto",
                            "&:hover": {
                              backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[700] : muiTheme.palette.primary.main + "08",
                              borderColor: muiTheme.palette.primary.main,
                            },
                            "&.Mui-disabled": {
                              color: muiTheme.palette.text.disabled,
                              borderColor: muiTheme.palette.action.disabled,
                              backgroundColor:
                                muiTheme.palette.action.disabledBackground,
                            },
                          }}
                        >
                          Reply All
                        </Button>
                        )}
                        <Button
                          data-testid="thread-forward-button"
                          variant="outlined"
                          size="small"
                          startIcon={<ReplyIcon sx={{ transform: "scaleX(-1)" }} />}
                          onClick={handleCommonForward}
                          disabled={isSelectedThreadArchived || isChannelArchived}
                          sx={{
                            color: muiTheme.palette.primary.main,
                            borderColor: muiTheme.palette.primary.main,
                            backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[800] : "white",
                            fontSize: "0.75rem",
                            padding: "6px 12px",
                            minWidth: "auto",
                            maxWidth: "fit-content",
                            flex: "0 0 auto",
                            "&:hover": {
                              backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[700] : muiTheme.palette.primary.main + "08",
                              borderColor: muiTheme.palette.primary.main,
                            },
                            "&.Mui-disabled": {
                              color: muiTheme.palette.text.disabled,
                              borderColor: muiTheme.palette.action.disabled,
                              backgroundColor:
                                muiTheme.palette.action.disabledBackground,
                            },
                          }}
                        >
                          Forward
                        </Button>
                      </>
                    )}
                    {/* WhatsApp channel: Reply button to show typing box and send via send_message API */}
                    {channelType === "message" &&
                      ((channelData as any)?.provider === "whatsapp" ||
                        channelData?.messaging_provider === "whatsapp") && (
                      <Button
                        data-testid="thread-whatsapp-reply-button"
                        variant="outlined"
                        size="small"
                        startIcon={<ReplyIcon />}
                        disabled={isSelectedThreadArchived || isChannelArchived}
                        onClick={() => {
                          setPostingMethod("channel-reply");
                          setShowCloseIcon(false);
                          setIsMessageBoxVisible(true);
                          setSelectedFiles([]); // Clear any previous attachments so they don't carry over
                        }}
                        sx={{
                          color: muiTheme.palette.primary.main,
                          borderColor: muiTheme.palette.primary.main,
                          backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[800] : "white",
                          fontSize: "0.75rem",
                          padding: "6px 12px",
                          minWidth: "auto",
                          maxWidth: "fit-content",
                          flex: "0 0 auto",
                          "&:hover": {
                            backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[700] : muiTheme.palette.primary.main + "08",
                            borderColor: muiTheme.palette.primary.main,
                          },
                          "&.Mui-disabled": {
                            color: muiTheme.palette.text.disabled,
                            borderColor: muiTheme.palette.action.disabled,
                            backgroundColor:
                              muiTheme.palette.action.disabledBackground,
                          },
                        }}
                      >
                        Reply
                      </Button>
                    )}
                    {/* {!isGeneratingDraftCompleted && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                        disabled={isSelectedThreadArchived || isChannelArchived || isGeneratingDraft}
                        onClick={handleGenerateDraft}
                      sx={{ 
                        color: muiTheme.palette.primary.main,
                        borderColor: muiTheme.palette.primary.main,
                        backgroundColor: 'white',
                        fontSize: '0.75rem',
                          padding: '6px 12px',
                          minWidth: 'auto',
                          maxWidth: 'fit-content',
                          flex: '0 0 auto',
                        '&:hover': {
                          backgroundColor: muiTheme.palette.primary.main + '08',
                          borderColor: muiTheme.palette.primary.main
                        },
                        '&.Mui-disabled': {
                          color: muiTheme.palette.text.disabled,
                          borderColor: muiTheme.palette.action.disabled,
                          backgroundColor: muiTheme.palette.action.disabledBackground,
                        }
                      }}
                    >
                        {isGeneratingDraft ? 'Generating...' : 'Generate Draft'}
                    </Button>
                    )} */}
                    <Button
                      data-testid="thread-internal-reply-button"
                      variant="outlined"
                      size="small"
                      startIcon={<ChatIcon />}
                      disabled={isSelectedThreadArchived || isChannelArchived}
                      onClick={() => {
                        setPostingMethod("internal-reply");
                        setShowCloseIcon(false);
                        setIsMessageBoxVisible(true);
                        setSelectedFiles([]); // Clear any previous attachments so they don't carry over
                      }}
                      sx={{
                        color: muiTheme.palette.primary.main,
                        borderColor: muiTheme.palette.primary.main,
                        backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[800] : "white",
                        fontSize: "0.75rem",
                        padding: "6px 12px",
                        minWidth: "auto",
                        maxWidth: "fit-content",
                        flex: "0 0 auto",
                        "&:hover": {
                          backgroundColor: muiTheme.palette.mode === "dark" ? muiTheme.palette.grey[700] : muiTheme.palette.primary.main + "08",
                          borderColor: muiTheme.palette.primary.main,
                        },
                        "&.Mui-disabled": {
                          color: muiTheme.palette.text.disabled,
                          borderColor: muiTheme.palette.action.disabled,
                          backgroundColor:
                            muiTheme.palette.action.disabledBackground,
                        },
                      }}
                    >
                      Internal Message
                    </Button>
                  </Box>
                ) : (
                  /* Message Input Box - For internal-reply / channel-reply only (ask-shay uses AskPrism7Panel) */
                  <Box data-testid="message-input-box" sx={{ position: "relative" }}>
                        {/* Close Button - Only show if showCloseIcon is true */}
                        {showCloseIcon && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mb: 1,
                            }}
                          >
                            <IconButton
                              data-testid="message-input-close-button"
                              size="small"
                              onClick={() => {
                                setPostingMethod("ask-shay"); // Reset to default
                                setIsMessageBoxVisible(false);
                                setShowCloseIcon(true); // Reset for next time
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "text.primary" },
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        )}

                        <RichTextEditor
                      key={editorKey}
                      editable={!isSelectedThreadArchived && !isChannelArchived}
                      renderControls={() => (
                        <MenuControlsContainer>
                          <MenuButtonBold />
                          <MenuButtonItalic />
                          <MenuButtonUnderline />
                          <MenuButtonStrikethrough />
                          <MenuDivider />
                          <MenuButtonBulletedList />
                          <MenuButtonOrderedList />
                          <MenuDivider />
                          <MenuButtonUndo />
                          <MenuButtonRedo />
                        </MenuControlsContainer>
                      )}
                      content={isEditing ? editedMessageContent : newComment || ""}
                      onUpdate={({ editor }) => {
                        if (!isSelectedThreadArchived && !isChannelArchived) {
                          const html = editor.getHTML();
                          debouncedContentUpdate(html);
                        }
                      }}
                      onCreate={({ editor }) => {
                        // Focus/position cursor when prefilled (ask-shay uses panel; this branch is internal-reply/channel-reply only)
                        if (newComment === ASK_PRISM7_DEFAULT_CONTENT) {
                          setTimeout(() => {
                            editor.commands.focus();
                            editor.commands.setTextSelection(editor.state.doc.content.size);
                          }, 100);
                        }
                      }}
                      onFocus={() =>
                        !isSelectedThreadArchived &&
                        !isChannelArchived &&
                        setIsEditorFocused(true)
                      }
                      onBlur={() => setIsEditorFocused(false)}
                      extensions={[
                        StarterKit.configure({
                          heading: false,
                          codeBlock: false,
                          blockquote: false,
                          code: false,
                          underline: false,
                        }),
                        Underline,
                        Mention.configure(mentionSuggestionOptions),
                        Placeholder.configure({
                          placeholder: isChannelArchived
                            ? "This channel is archived. You cannot post new messages."
                            : isSelectedThreadArchived
                            ? "This thread is archived. You cannot post new messages."
                            : "Type your message here... Use @ to mention someone",
                        }),
                      ]}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor:
                            isSelectedThreadArchived || isChannelArchived
                              ? muiTheme.palette.grey[100]
                              : muiTheme.palette.background.default,
                          fontSize: "0.875rem",
                          opacity:
                            isSelectedThreadArchived || isChannelArchived
                              ? 0.6
                              : 1,
                        },
                        "& .ProseMirror": {
                          minHeight:
                            isEditorFocused ||
                            (newComment &&
                              newComment.replace(/<[^>]*>/g, "").trim())
                              ? "120px"
                              : "80px",
                          maxHeight:
                            isEditorFocused ||
                            (newComment &&
                              newComment.replace(/<[^>]*>/g, "").trim())
                              ? "200px"
                              : "120px",
                          padding: "12px",
                          transition:
                            "min-height 0.3s ease, max-height 0.3s ease",
                          overflowY: "auto",
                          overflowX: "hidden",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "4px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#c1c1c1",
                            borderRadius: "4px",
                            "&:hover": {
                              background: "#a8a8a8",
                            },
                          },
                          "&:empty::before": {
                            content: '""',
                            display: "none",
                          },
                        },
                      }}
                    />

                    {/* Attachment Input and Buttons: one row when no attachments, two rows when attachments present */}
                    {((isEditing ? editedAttachments : selectedFiles).length === 0) ? (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, gap: 1 }}>
                        <MessageAttachmentInput
                          selectedFiles={isEditing ? editedAttachments : selectedFiles}
                          onFilesChange={isEditing ? setEditedAttachments : setSelectedFiles}
                          onError={(error) => showError(error)}
                          disabled={isPosting || isSelectedThreadArchived || isChannelArchived}
                          onAttachClick={handleAttachmentValidation}
                        />
                        <Box data-testid="message-input-actions" sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      <Button
                        data-testid="message-input-cancel-button"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          // Reset editing state if in edit mode
                          if (isEditing) {
                            setIsEditing(false);
                            setMessageToEdit(null);
                            setEditedMessageContent("");
                            setEditedAttachments([]);
                          }
                          setPostingMethod("ask-shay"); // Reset to default
                          handleCancelClick();
                          setIsMessageBoxVisible(false);
                          setShowCloseIcon(true); // Reset for next time
                        }}
                        disabled={isPosting}
                        sx={{
                          color: muiTheme.palette.primary.main,
                          borderColor: muiTheme.palette.primary.main,
                          backgroundColor: isDarkMode
                            ? "transparent"
                            : muiTheme.palette.background.paper,
                          fontSize: "0.75rem",
                          padding: "4px 12px",
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: muiTheme.palette.action.hover,
                            borderColor: muiTheme.palette.primary.main,
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="message-input-send-button"
                        variant="contained"
                        size="small"
                        disabled={
                          (isEditing 
                            ? !(editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim()
                            : !newComment.replace(/<[^>]*>/g, "").trim()) ||
                          isPosting ||
                          isSelectedThreadArchived ||
                          isChannelArchived
                        }
                        onClick={async () => {
                          if (isEditing && messageToEdit) {
                            await handleUpdateMessage();
                          } else {
                            await handlePostMessage();
                            setPostingMethod("ask-shay"); // Reset to default
                            setIsMessageBoxVisible(false);
                            setShowCloseIcon(true); // Reset for next time
                          }
                        }}
                        sx={{
                          backgroundColor: (isEditing 
                            ? (editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim()
                            : newComment.replace(/<[^>]*>/g, "").trim())
                            ? muiTheme.palette.primary.main
                            : muiTheme.palette.grey[300],
                          color: "white",
                          fontSize: "0.75rem",
                          padding: "4px 12px",
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: (isEditing 
                              ? (editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim()
                              : newComment.replace(/<[^>]*>/g, "").trim())
                              ? muiTheme.palette.primary.dark
                              : muiTheme.palette.grey[400],
                          },
                          "&.Mui-disabled": {
                            backgroundColor: muiTheme.palette.grey[300],
                            color: "white",
                          },
                        }}
                      >
                        {isPosting ? (
                          <>
                            <CircularProgress
                              size={12}
                              sx={{ mr: 0.5, color: "white" }}
                            />
                            Posting...
                          </>
                        ) : isEditing ? (
                            "Update"
                        ): (
                          "Post"
                        )}
                      </Button>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, maxHeight: 72, overflowY: "auto", overflowX: "auto", mt: 1, mb: 1, pr: 0.5 }}>
                          <MessageAttachmentInput
                            selectedFiles={isEditing ? editedAttachments : selectedFiles}
                            onFilesChange={isEditing ? setEditedAttachments : setSelectedFiles}
                            onError={(error) => showError(error)}
                            disabled={isPosting || isSelectedThreadArchived || isChannelArchived}
                            onAttachClick={handleAttachmentValidation}
                          />
                        </Box>
                        <Box data-testid="message-input-actions" sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, flexShrink: 0 }}>
                          <Button
                            data-testid="message-input-cancel-button"
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              if (isEditing) {
                                setIsEditing(false);
                                setMessageToEdit(null);
                                setEditedMessageContent("");
                                setEditedAttachments([]);
                              }
                              setPostingMethod("ask-shay");
                              handleCancelClick();
                              setIsMessageBoxVisible(false);
                              setShowCloseIcon(true);
                            }}
                            disabled={isPosting}
                            sx={{
                              color: muiTheme.palette.primary.main,
                              borderColor: muiTheme.palette.primary.main,
                              backgroundColor: isDarkMode
                                ? "transparent"
                                : muiTheme.palette.background.paper,
                              fontSize: "0.75rem",
                              padding: "4px 12px",
                              minWidth: "auto",
                              "&:hover": {
                                backgroundColor: muiTheme.palette.action.hover,
                                borderColor: muiTheme.palette.primary.main,
                              },
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            data-testid="message-input-send-button"
                            variant="contained"
                            size="small"
                            disabled={(isEditing ? !(editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim() : !newComment.replace(/<[^>]*>/g, "").trim()) || isPosting || isSelectedThreadArchived || isChannelArchived}
                            onClick={async () => {
                              if (isEditing && messageToEdit) await handleUpdateMessage();
                              else { await handlePostMessage(); setPostingMethod("ask-shay"); setIsMessageBoxVisible(false); setShowCloseIcon(true); }
                            }}
                            sx={{
                              backgroundColor: (isEditing ? (editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim() : newComment.replace(/<[^>]*>/g, "").trim()) ? muiTheme.palette.primary.main : muiTheme.palette.grey[300],
                              color: "white", fontSize: "0.75rem", padding: "4px 12px", minWidth: "auto",
                              "&:hover": { backgroundColor: (isEditing ? (editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim() : newComment.replace(/<[^>]*>/g, "").trim()) ? muiTheme.palette.primary.dark : muiTheme.palette.grey[400] },
                              "&.Mui-disabled": { backgroundColor: muiTheme.palette.grey[300], color: "white" },
                            }}
                          >
                            {isPosting ? <><CircularProgress size={12} sx={{ mr: 0.5, color: "white" }} /> Posting...</> : isEditing ? "Update" : "Post"}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            // No thread selected: show loading until API responds and latest thread is active, or empty state only when list is empty
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                p: 3,
                textAlign: "center",
              }}
            >
              {/* When email sync is in progress, show a single consistent state; EmailSyncStatusIndicator already renders one spinner in empty state – do not add a second one */}
              {channelType === "email" && emailSyncInProgress ? (
                <EmailSyncStatusIndicator
                  channelData={channelData}
                  channelId={channelId}
                  onSyncComplete={reloadAllAPIs}
                  onSyncInProgressChange={setEmailSyncInProgress}
                  hasThreads={getFilteredThreads().length > 0}
                  showEmptyState={true}
                />
              ) : threadsLoading ? (
                <>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading conversations…
                  </Typography>
                </>
              ) : getFilteredThreads().length === 0 ? (
                <>
                  {/* Sync-in-progress message when email is syncing; otherwise nothing */}
                  <EmailSyncStatusIndicator
                    channelData={channelData}
                    channelId={channelId}
                    onSyncComplete={reloadAllAPIs}
                    onSyncInProgressChange={setEmailSyncInProgress}
                    hasThreads={false}
                    showEmptyState={true}
                  />
                  {/* Empty-state copy; hide while sync in progress; common message when any filter(s) applied, else default empty copy */}
                  {!(channelType === "email" && emailSyncInProgress) && (() => {
                    const hasActiveFilters = getActiveFilterCount() > 0 || selectedEmailCategory === "archived";
                    return (
                      <>
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          {hasActiveFilters
                            ? "No threads match your filters"
                            : channelType === "email"
                              ? "No email conversations"
                              : channelType === "message"
                                ? "Welcome to your messaging workspace"
                                : "Welcome to your voice workspace"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {hasActiveFilters
                            ? "Try adjusting or clearing filters to see more results."
                            : channelType === "email"
                              ? "Conversations will appear here once you start receiving."
                              : channelType === "message"
                                ? "Start by sending a message or wait for conversations to arrive."
                                : "Start by making a call or wait for conversations to arrive."}
                        </Typography>
                      </>
                    );
                  })()}
                </>
              ) : (
                // Threads exist but selection not set yet; we select latest by default – show loading until active and data displayed
                <>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading conversation…
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Gap on the right side of main content panel */}
        <Box
          sx={{
            width: "6px",
            backgroundColor: isDarkMode
              ? muiTheme.palette.background.default
              : "#f5f5f5",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Data Sources Panel */}
      <DataSourcesPanel
        channelId={channelId ?? null}
        channelName={channelData?.name}
        isOpen={isSourcePanelOpen}
        onClose={handleSourcePanelToggle}
        onOpenChange={setIsSourcePanelOpen}
        threadId={selectedThread?.id || null}
        messages={messages}
        onDataSourceAdded={fetchEmbeddingStatus}
        initialOpenAddDataSource={initialOpenAddDataSource}
        onInitialAddDataSourceOpened={handleInitialAddDataSourceOpened}
      />

      {/* Channel Settings Component */}
      <ChannelSettings
        data-testid="channel-settings-drawer"
        open={isSettingsDrawerOpen}
        onClose={handleSettingsClose}
        channelId={channelId}
        channelName={channelData?.name}
        channelDescription={channelData?.channel_description || channelData?.description}
        channelTags={channelData?.channel_tags}
        channelSubtags={channelData?.channel_sub_tags}
        channelBusinessRules={channelData?.channel_business_rules}
        isChannelArchived={isChannelArchived}
        channelType={channelType}
        workspaceTag={channelData?.workspace_name}
        workspaceId={channelData?.workspace_id}
        initialTabId={initialSettingsTabId}
        initialOpenAddSkill={initialOpenAddSkill}
        onInitialAddSkillOpened={handleInitialAddSkillOpened}
        onChannelUpdated={fetchChannelData}
        // PR #665 review (M2) — was handleDashboardToggle directly, which opened the Dashboard at
        // its default scroll position (the comment here previously claimed it "deep-links" to the
        // breakdown, but nothing actually connected it to that section — now it does, via
        // dashboardScrollTarget below). Still reuses the same drawer-transition rules
        // (openDashboardFlags closes Settings when Dashboard opens) — only the scroll target is new.
        onOpenDashboard={handleOpenDashboardToWorkflowRuns}
      />


      {/* Add {LanguageConstants.thread} Modal */}
      <AddThreadModal
        data-testid="add-thread-modal"
        open={isAddThreadModalOpen}
        onClose={handleAddThreadClose}
        onAddThread={handleAddThread}
        isLoading={isCreatingThread}
        messagingProvider={channelData?.messaging_provider}
      />

      {/* Thread Menu */}
      <Menu
        data-testid="thread-menu"
        anchorEl={threadMenuAnchor}
        open={Boolean(threadMenuAnchor)}
        onClose={handleThreadMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          data-testid="thread-menu-edit"
          onClick={() =>
            selectedThreadForMenu && handleEditThreadOpen(selectedThreadForMenu)
          }
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <EditIcon fontSize="small" />
          Edit
        </MenuItem>
        {selectedThreadForMenu && (
          selectedThreadForMenu.is_archived ? (
            <MenuItem
              data-testid="thread-menu-unarchive"
              onClick={() => {
                if (selectedThreadForMenu) {
                  handleUnarchiveThread(selectedThreadForMenu);
                }
              }}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <UnarchiveIcon fontSize="small" />
              Unarchive
            </MenuItem>
          ) : (
            <MenuItem
              data-testid="thread-menu-archive"
              onClick={() => {
                if (selectedThreadForMenu) {
                  handleArchiveConfirmOpen(selectedThreadForMenu);
                }
              }}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ArchiveIcon fontSize="small" />
              Archive
            </MenuItem>
          )
        )}
        {/* Move Thread: same condition as header Message tag — show for all message channels (modal lists only regular channels) */}
        {channelType === "message" && (
            <MenuItem
              data-testid="thread-menu-move"
              onClick={() => {
                if (selectedThreadForMenu) {
                  setThreadToMove(selectedThreadForMenu);
                  handleThreadMenuClose();
                  setIsMoveThreadModalOpen(true);
                }
              }}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <MoveThreadIcon fontSize="small" />
              Move Thread
            </MenuItem>
          )}
        <MenuItem
          data-testid="thread-menu-delete"
          onClick={() =>
            selectedThreadForMenu &&
            handleDeleteConfirmOpen(selectedThreadForMenu)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "error.main",
          }}
        >
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Move Thread Modal - list of regular message channels */}
      <MoveThreadModal
        open={isMoveThreadModalOpen}
        onClose={() => {
          setIsMoveThreadModalOpen(false);
          setThreadToMove(null);
        }}
        thread={threadToMove ? { id: threadToMove.id, title: threadToMove.title, description: threadToMove.description } : null}
        currentChannelId={channelId}
        workspaceId={channelData?.workspace_id}
        onMoveSuccess={handleMoveThreadSuccess}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Edit {LanguageConstants.thread} Dialog */}
      <Dialog
        data-testid="edit-thread-dialog"
        open={isEditThreadModalOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          handleEditThreadClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Edit {LanguageConstants.thread}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              data-testid="edit-thread-name-input"
              fullWidth
              label="Thread Name"
              value={editThreadTitle}
              onChange={(e) => setEditThreadTitle(e.target.value)}
              margin="normal"
            />
            <TextField
              data-testid="edit-thread-description-input"
              fullWidth
              label="Description"
              value={editThreadDescription}
              onChange={(e) => setEditThreadDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button data-testid="edit-thread-cancel-button" onClick={handleEditThreadClose} disabled={isEditingThread}>
            Cancel
          </Button>
          <Button
            data-testid="edit-thread-save-button"
            onClick={() => {
              handleEditThread({
                name: editThreadTitle,
                description: editThreadDescription,
              });
            }}
            variant="contained"
            disabled={!editThreadTitle.trim() || isEditingThread}
          >
            {isEditingThread ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        data-testid="delete-thread-dialog"
        open={isDeleteConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          handleDeleteConfirmClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Delete {LanguageConstants.thread}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to delete the{" "}
              {LanguageConstants.thread.toLowerCase()} "{threadToDelete?.title}
              "?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone. All messages in this{" "}
              {LanguageConstants.thread.toLowerCase()} will be permanently
              deleted.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="delete-thread-cancel-button"
            onClick={handleDeleteConfirmClose}
            disabled={isDeletingThread}
          >
            Cancel
          </Button>
          <Button
            data-testid="delete-thread-confirm-button"
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeletingThread}
          >
            {isDeletingThread ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Deleting...
              </>
            ) : (
              `Delete ${LanguageConstants.thread}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        data-testid="archive-thread-dialog"
        open={isArchiveConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          handleArchiveConfirmClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Archive {LanguageConstants.thread}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to archive the{" "}
              {LanguageConstants.thread.toLowerCase()} "{threadToArchive?.title}
              "?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Archived threads will be hidden from the main view but can be
              restored later.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="archive-thread-cancel-button"
            onClick={handleArchiveConfirmClose}
            disabled={isArchivingThread}
          >
            Cancel
          </Button>
          <Button
            data-testid="archive-thread-confirm-button"
            onClick={handleArchiveConfirm}
            variant="contained"
            color="warning"
            disabled={isArchivingThread}
          >
            {isArchivingThread ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Archiving...
              </>
            ) : (
              `Archive ${LanguageConstants.thread}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk archive confirmation (checkbox selection) */}
      <Dialog
        data-testid="bulk-archive-threads-dialog"
        open={isBulkArchiveConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          if (!isBulkArchiving) setIsBulkArchiveConfirmOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          {threadsToBulkArchiveCount === 1
            ? `Archive ${LanguageConstants.thread}`
            : `Archive ${LanguageConstants.thread_s}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Archive {threadsToBulkArchiveCount} selected{" "}
              {LanguageConstants.thread.toLowerCase()}
              {threadsToBulkArchiveCount === 1 ? "" : "s"}? Archived items will
              be hidden from the main view but can be restored later.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsBulkArchiveConfirmOpen(false)}
            disabled={isBulkArchiving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void executeBulkArchive()}
            variant="contained"
            color="warning"
            disabled={isBulkArchiving || threadsToBulkArchiveCount === 0}
          >
            {isBulkArchiving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Archiving...
              </>
            ) : (
              "Archive"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk unarchive confirmation (checkbox selection; archived threads only) */}
      <Dialog
        data-testid="bulk-unarchive-threads-dialog"
        open={isBulkUnarchiveConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          if (!isBulkUnarchiving) setIsBulkUnarchiveConfirmOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          {threadsToBulkUnarchiveCount === 1
            ? `Unarchive ${LanguageConstants.thread}`
            : `Unarchive ${LanguageConstants.thread_s}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Restore {threadsToBulkUnarchiveCount} archived{" "}
              {LanguageConstants.thread.toLowerCase()}
              {threadsToBulkUnarchiveCount === 1 ? "" : "s"} to the active
              list?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsBulkUnarchiveConfirmOpen(false)}
            disabled={isBulkUnarchiving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void executeBulkUnarchive()}
            variant="contained"
            color="warning"
            disabled={
              isBulkUnarchiving || threadsToBulkUnarchiveCount === 0
            }
          >
            {isBulkUnarchiving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Unarchiving...
              </>
            ) : (
              "Unarchive"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation (checkbox selection) */}
      <Dialog
        data-testid="bulk-delete-threads-dialog"
        open={isBulkDeleteConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          if (!isBulkDeleting) setIsBulkDeleteConfirmOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          {bulkSelectedThreadIds.length === 1
            ? `Delete ${LanguageConstants.thread}`
            : `Delete ${LanguageConstants.thread_s}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Permanently delete {bulkSelectedThreadIds.length} selected{" "}
              {LanguageConstants.thread.toLowerCase()}
              {bulkSelectedThreadIds.length === 1 ? "" : "s"}? This cannot be
              undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsBulkDeleteConfirmOpen(false)}
            disabled={isBulkDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void executeBulkDelete()}
            variant="contained"
            color="error"
            disabled={isBulkDeleting || bulkSelectedThreadIds.length === 0}
          >
            {isBulkDeleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thread Switch Confirmation Dialog */}
      <Dialog
        data-testid="thread-switch-confirmation-dialog"
        open={showThreadSwitchConfirmation}
        onClose={handleCancelThreadSwitch}
        aria-labelledby="thread-switch-confirmation-title"
        aria-describedby="thread-switch-confirmation-description"
      >
        <DialogTitle id="thread-switch-confirmation-title">
          Switch Thread?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography>
              The compose box is currently open. Switching to another thread
              will close the compose box and clear all typed text. Are you sure
              you want to continue?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelThreadSwitch} color="primary">
            Keep Composing
          </Button>
          <Button
            onClick={handleConfirmThreadSwitch}
            color="error"
            variant="contained"
          >
            Switch Thread
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon-only landing view for a channel: shown until Dashboard / Data Sources / Settings is picked.
          Stays mounted as a backdrop when any of the three slides in from the home screen —
          see landingDrawerVisible above for why.
          Settings-not-opening bug — this Drawer is the only member of the channel base layer
          that's always `open={true}`. MUI's Modal stacking does not reliably re-order same-
          z-index Drawers by JSX/mount order once one has been continuously open since initial
          render, so at a tied zIndex 1300 this permanently-open Drawer covered Dashboard/Settings
          regardless of which one opened later (reproduced directly against this project's MUI
          version). Pinned just below the base layer via CHANNEL_LANDING_VIEW_Z_INDEX instead —
          Dashboard/Settings/Data Sources keep their own existing z-index values unchanged. */}
      {landingDrawerVisible && (
        <Drawer
          anchor="right"
          open={true}
          hideBackdrop
          className="channel-icon-landing-drawer"
          ModalProps={{
            disableEnforceFocus: true,
            disableAutoFocus: true,
            disableRestoreFocus: true,
          }}
          sx={{
            zIndex: CHANNEL_LANDING_VIEW_Z_INDEX,
            left: "60px",
            width: "calc(100% - 60px)",
            "& .MuiBackdrop-root": { display: "none !important" },
            "& .MuiDrawer-paper": {
              width: "calc(100% - 60px)",
              height: "100%",
              left: "0 !important",
              right: "auto !important",
              bgcolor: "background.default",
            },
          }}
        >
          <style>
            {`
              body.sidebar-closed .channel-icon-landing-drawer .MuiDrawer-paper {
                width: calc(100% - 60px) !important;
                left: 60px !important;
              }
              body.sidebar-open .channel-icon-landing-drawer .MuiDrawer-paper {
                width: calc(100% - 240px) !important;
                left: 240px !important;
              }
            `}
          </style>
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Toolbar
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <IconButton size="small" onClick={() => navigate("/supportSpace")}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {channelData?.name || "Channel"}
              </Typography>
            </Toolbar>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                px: 3,
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
                What would you like to do?
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "center",
                  gap: 3,
                  flexWrap: "wrap",
                }}
              >
                {/* Messaging provider shortcut card — message channels get an icon for their
                    provider (Regular/WhatsApp/Slack/Google Chat/Teams). Every provider except
                    Regular opens its external app in a new tab; Regular has no external app to
                    redirect to, so its card renders with no onClick/cursor change. */}
                {channelType === 'message' && (() => {
                  const messagingProvider = resolveMessagingProvider(channelData);
                  const { url: messagingUrl, label: messagingLabel, iconSrc: messagingIconSrc } =
                    MESSAGING_PROVIDER_CONFIG[messagingProvider];

                  return (
                    <Box
                      onClick={messagingUrl ? () => window.open(messagingUrl, '_blank', 'noopener,noreferrer') : undefined}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        width: 160,
                        py: 4,
                        px: 3,
                        borderRadius: 3,
                        border: `1px solid ${muiTheme.palette.divider}`,
                        bgcolor: "background.paper",
                        cursor: messagingUrl ? "pointer" : "default",
                        transition: "all 0.2s ease",
                        ...(messagingUrl && {
                          "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: muiTheme.palette.mode === "dark"
                              ? "0 4px 20px rgba(0,0,0,0.4)"
                              : "0 4px 20px rgba(0,0,0,0.10)",
                            transform: "translateY(-2px)",
                          },
                        }),
                      }}
                    >
                      {messagingIconSrc ? (
                        <Box
                          component="img"
                          src={messagingIconSrc}
                          alt={messagingLabel}
                          sx={{ width: 40, height: 40 }}
                        />
                      ) : (
                        <ChatIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                      )}
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" fontWeight={600}>{messagingLabel}</Typography>
                        {messagingUrl && (
                          <Typography variant="caption" color="text.secondary">Open {messagingLabel}</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
                {/* Webmail shortcut card — email channels only (Gmail/Outlook); non-email channels unaffected */}
                {(() => {
                  if (channelType !== 'email') return null;
                  const resolvedProvider = resolveWebmailProvider(channelData);
                  if (!resolvedProvider) return null;
                  const { url: webmailUrl, label: webmailLabel, iconSrc: webmailIconSrc } =
                    WEBMAIL_PROVIDER_CONFIG[resolvedProvider];

                  return (
                    <Box
                      onClick={() => window.open(webmailUrl, '_blank', 'noopener,noreferrer')}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        width: 160,
                        py: 4,
                        px: 3,
                        borderRadius: 3,
                        border: `1px solid ${muiTheme.palette.divider}`,
                        bgcolor: "background.paper",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          boxShadow: muiTheme.palette.mode === "dark"
                            ? "0 4px 20px rgba(0,0,0,0.4)"
                            : "0 4px 20px rgba(0,0,0,0.10)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={webmailIconSrc}
                        alt={webmailLabel}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" fontWeight={600}>{webmailLabel}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-all",
                          }}
                        >
                          {channelData?.name || ""}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
                {channelType !== "message" && (
                  <Box
                    onClick={handleDashboardToggle}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      width: 160,
                      py: 4,
                      px: 3,
                      borderRadius: 3,
                      border: `1px solid ${muiTheme.palette.divider}`,
                      bgcolor: "background.paper",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: muiTheme.palette.mode === "dark"
                          ? "0 4px 20px rgba(0,0,0,0.4)"
                          : "0 4px 20px rgba(0,0,0,0.10)",
                        transform: "translateY(-2px)",
                        "& .landing-card-icon": { color: "primary.main" },
                      },
                    }}
                  >
                    <AnalyticsIcon
                      className="landing-card-icon"
                      sx={{ fontSize: 40, color: "text.secondary", transition: "color 0.2s" }}
                    />
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="subtitle2" fontWeight={600}>Dashboard</Typography>
                      <Typography variant="caption" color="text.secondary">View analytics &amp; insights</Typography>
                    </Box>
                  </Box>
                )}
                <Box
                  onClick={handleSourcePanelToggle}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    width: 160,
                    py: 4,
                    px: 3,
                    borderRadius: 3,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: muiTheme.palette.mode === "dark"
                        ? "0 4px 20px rgba(0,0,0,0.4)"
                        : "0 4px 20px rgba(0,0,0,0.10)",
                      transform: "translateY(-2px)",
                      "& .landing-card-icon": { color: "primary.main" },
                    },
                  }}
                >
                  <Badge badgeContent={embeddingStatus?.total_datasources || 0} color="primary">
                    <SourceIcon
                      className="landing-card-icon"
                      sx={{ fontSize: 40, color: "text.secondary", transition: "color 0.2s" }}
                    />
                  </Badge>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" fontWeight={600}>Data Sources</Typography>
                    <Typography variant="caption" color="text.secondary">Manage connected sources</Typography>
                  </Box>
                </Box>
                <Box
                  onClick={handleSettingsClick}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    width: 160,
                    py: 4,
                    px: 3,
                    borderRadius: 3,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: muiTheme.palette.mode === "dark"
                        ? "0 4px 20px rgba(0,0,0,0.4)"
                        : "0 4px 20px rgba(0,0,0,0.10)",
                      transform: "translateY(-2px)",
                      "& .landing-card-icon": { color: "primary.main" },
                    },
                  }}
                >
                  <SettingsIcon
                    className="landing-card-icon"
                    sx={{ fontSize: 40, color: "text.secondary", transition: "color 0.2s" }}
                  />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" fontWeight={600}>Settings</Typography>
                    <Typography variant="caption" color="text.secondary">Configure channel options</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}

      <ChannelDashboard
        channelId={channelId || ''}
        channelName={channelData?.name}
        workspaceTag={channelData?.workspace_name}
        open={isDashboardOpen}
        onClose={handleDashboardClose}
        onOpenDataSources={handleSourcePanelToggle}
        dataSourceCount={embeddingStatus?.total_datasources || 0}
        onOpenSettings={handleSettingsClick}
        scrollToOnOpen={dashboardScrollTarget}
        onScrolledToSection={() => setDashboardScrollTarget(null)}
      />

      {/* Toaster/Snackbar for notifications */}
      <Snackbar
        open={toasterOpen}
        autoHideDuration={6000}
        onClose={handleToasterClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleToasterClose}
          severity={toasterSeverity}
          sx={{ width: "100%" }}
        >
          {toasterMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={isDeleteMessageConfirmOpen}
        onClose={handleCancelDeleteMessage}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Message?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this message?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteMessage}>Cancel</Button>
          <Button onClick={handleConfirmDeleteMessage} color="error" autoFocus>
            {isDeletingMessage ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Restriction Modal */}
      <ChannelRestrictionModal
        open={openMessageRestrictionModal}
        onClose={() => setOpenMessageRestrictionModal(false)}
        restrictionType="message"
      />

      {/* Attachment Restriction Modal */}
      <ChannelRestrictionModal
        open={openAttachmentRestrictionModal}
        onClose={() => setOpenAttachmentRestrictionModal(false)}
        restrictionType="attachment"
      />

      {/* Message limit modal (same style as channel creation limit) */}
      <Dialog
        open={messageLimitModalOpen}
        onClose={() => setMessageLimitModalOpen(false)}
        aria-labelledby="message-limit-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="message-limit-dialog-title">Message limit</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            {SUBSCRIPTION_LIMIT_MESSAGES.MESSAGE}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageLimitModalOpen(false)} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Filter Modal - Show for email and message channels */}
      {channelId && (
        <EmailFilterModal
          open={isFilterModalOpen}
          onClose={handleFilterClose}
          onApplyFilters={handleApplyFilters}
          channelId={channelId}
          threads={apiThreads}
          initialFilters={emailFilters}
          channelType={channelType}
        />
      )}

      {/* Ask Prism7 right panel (MUI Drawer): user + AI messages and message input */}
      {selectedThread?.id && (
        <AskPrism7Panel
          open={isAskPrism7PanelOpen}
          onClose={() => setIsAskPrism7PanelOpen(false)}
          messages={messages.filter((m) => (m.message_type === "user" || m.message_type === "system") && m.is_ai_processed === true)}
          renderMessage={(m) => renderOneMessageRef.current?.(m) ?? null}
          temporaryUserMessage={temporaryUserMessage}
          isAiThinking={isAiThinking}
          isGeneratingDraft={isGeneratingDraft}
          user={user}
          messagesEndRef={messagesEndRef}
          onLoadMore={() => loadMoreMessages({ fromPanel: true })}
          loadingMore={messagesLoadingMore}
          hasMore={messagesHasMore}
          messageInput={
            /* Reuse same message input as main: no close button in panel (panel header has close) */
            <Box data-testid="message-input-box-prism7-panel" sx={{ position: "relative", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
              <RichTextEditor
                key={editorKey}
                editable={!isSelectedThreadArchived && !isChannelArchived}
                renderControls={() => (
                  <MenuControlsContainer>
                    <MenuButtonBold />
                    <MenuButtonItalic />
                    <MenuButtonUnderline />
                    <MenuButtonStrikethrough />
                    <MenuDivider />
                    <MenuButtonBulletedList />
                    <MenuButtonOrderedList />
                    <MenuDivider />
                    <MenuButtonUndo />
                    <MenuButtonRedo />
                  </MenuControlsContainer>
                )}
                content={isEditing ? editedMessageContent : newComment || ""}
                onUpdate={({ editor }) => {
                  if (!isSelectedThreadArchived && !isChannelArchived) {
                    const html = editor.getHTML();
                    debouncedContentUpdate(html);
                  }
                }}
                onCreate={({ editor }) => {
                  if (postingMethod === "ask-shay" && newComment === ASK_PRISM7_DEFAULT_CONTENT) {
                    setTimeout(() => {
                      editor.commands.focus();
                      editor.commands.setTextSelection(editor.state.doc.content.size);
                    }, 100);
                  }
                }}
                onFocus={() => !isSelectedThreadArchived && !isChannelArchived && setIsEditorFocused(true)}
                onBlur={() => setIsEditorFocused(false)}
                extensions={[
                  StarterKit.configure({ heading: false, codeBlock: false, blockquote: false, code: false, underline: false }),
                  Underline,
                  Mention.configure(mentionSuggestionOptions),
                  Placeholder.configure({
                    placeholder: isChannelArchived
                      ? "This channel is archived. You cannot post new messages."
                      : isSelectedThreadArchived
                        ? "This thread is archived. You cannot post new messages."
                        : "Type your message here... Use @ to mention someone",
                  }),
                ]}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: isSelectedThreadArchived || isChannelArchived ? muiTheme.palette.grey[100] : muiTheme.palette.background.default,
                    fontSize: "0.875rem",
                    opacity: isSelectedThreadArchived || isChannelArchived ? 0.6 : 1,
                  },
                  "& .ProseMirror": {
                    minHeight: isEditorFocused || (newComment && newComment.replace(/<[^>]*>/g, "").trim()) ? "120px" : "80px",
                    maxHeight: isEditorFocused || (newComment && newComment.replace(/<[^>]*>/g, "").trim()) ? "200px" : "120px",
                    padding: "12px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  },
                }}
              />
              <Box sx={{ mt: 1, minWidth: 0 }}>
                {((isEditing ? editedAttachments : selectedFiles).length === 0) ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    {/* Ask Prism7 right panel: attachment icon commented out */}
                    {/* <MessageAttachmentInput
                      selectedFiles={isEditing ? editedAttachments : selectedFiles}
                      onFilesChange={isEditing ? setEditedAttachments : setSelectedFiles}
                      onError={(error) => showError(error)}
                      disabled={isPosting || isSelectedThreadArchived || isChannelArchived}
                    /> */}
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, marginLeft: "auto" }}>
                      <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (isEditing) {
                        setIsEditing(false);
                        setMessageToEdit(null);
                        setEditedMessageContent("");
                        setEditedAttachments([]);
                      }
                      handleCancelClick();
                      setIsAskPrism7PanelOpen(false);
                    }}
                    disabled={isPosting}
                    sx={{ color: "#1976d2", borderColor: "#1976d2", fontSize: "0.75rem", padding: "4px 12px" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={
                      (isEditing ? !(editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim() : !newComment.replace(/<[^>]*>/g, "").trim()) ||
                      isPosting ||
                      isSelectedThreadArchived ||
                      isChannelArchived
                    }
                    onClick={async () => {
                      if (isEditing && messageToEdit) await handleUpdateMessage();
                      else await handlePostMessage();
                      // Keep panel open so user can see response; close only via Cancel or header close
                    }}
                    sx={{
                      backgroundColor: (isEditing ? (editedMessageContent || newComment) : newComment).replace(/<[^>]*>/g, "").trim()
                        ? muiTheme.palette.primary.main
                        : muiTheme.palette.grey[300],
                      color: "white",
                      fontSize: "0.75rem",
                      padding: "4px 12px",
                    }}
                  >
                    {isPosting ? <> <CircularProgress size={12} sx={{ mr: 0.5, color: "white" }} /> Processing... </> : isEditing ? "Update" : "Post"}
                  </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Ask Prism7 right panel: attachment icon commented out */}
                    {/* <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, maxHeight: 72, overflowY: "auto", overflowX: "auto", mb: 1, pr: 0.5 }}>
                      <MessageAttachmentInput
                        selectedFiles={isEditing ? editedAttachments : selectedFiles}
                        onFilesChange={isEditing ? setEditedAttachments : setSelectedFiles}
                        onError={(error) => showError(error)}
                        disabled={isPosting || isSelectedThreadArchived || isChannelArchived}
                      />
                    </Box> */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, flexShrink: 0 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (isEditing) {
                            setIsEditing(false);
                            setMessageToEdit(null);
                            setEditedMessageContent("");
                            setEditedAttachments([]);
                          }
                          handleCancelClick();
                          setIsAskPrism7PanelOpen(false);
                        }}
                        disabled={isPosting}
                        sx={{ color: "#1976d2", borderColor: "#1976d2", fontSize: "0.75rem", padding: "4px 12px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={(isEditing ? !(editedMessageContent || newComment).replace(/<[^>]*>/g, "").trim() : !newComment.replace(/<[^>]*>/g, "").trim()) || isPosting || isSelectedThreadArchived || isChannelArchived}
                        onClick={async () => {
                          if (isEditing && messageToEdit) await handleUpdateMessage();
                          else await handlePostMessage();
                        }}
                        sx={{
                          backgroundColor: (isEditing ? (editedMessageContent || newComment) : newComment).replace(/<[^>]*>/g, "").trim() ? muiTheme.palette.primary.main : muiTheme.palette.grey[300],
                          color: "white", fontSize: "0.75rem", padding: "4px 12px",
                        }}
                      >
                        {isPosting ? <> <CircularProgress size={12} sx={{ mr: 0.5, color: "white" }} /> Processing... </> : isEditing ? "Update" : "Post"}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          }
        />
      )}
    </Box>
  );
};

export default ThreadDetailPage;
